import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ViewChildren, QueryList, ElementRef, ChangeDetectorRef, NgZone, HostListener, ViewContainerRef, ComponentRef, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ValueChangeEvent } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import * as pdfjsLib from 'pdfjs-dist';
import { forkJoin, of, Observable, Subject } from 'rxjs';
import { map, catchError, take, filter } from 'rxjs/operators';
import JSZip from 'jszip';
import { TruthManagement as TruthManagementService } from '../../services/truth-management';
import { ModalService, ModalType, ModalSize } from '../../services/modal.service';
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { Loan, Document, File, Attribute, TruthValue, AdminDocument, DocumentKey, EvaluationFile, FieldSourceData, AttributesResponse, TableData, TableRow } from '../../models/truth-management.model';
import { CanComponentDeactivate } from '../../guards/can-deactivate.guard';
import { PageHeaderComponent } from '../page-header/page-header.component';
import { TableRowInputComponent } from '../shared/table-row-input/table-row-input.component';
import { BboxSnipOverlayComponent, SnipRectPx } from '../shared/bbox-snip-overlay/bbox-snip-overlay.component';
import { PdfLazyViewerComponent } from '../shared/pdf-lazy-viewer/pdf-lazy-viewer.component';
import { BlobRangeTransport } from '../shared/pdf-lazy-viewer/blob-range-transport';
import { PdfPageRenderedEvent } from '../source/pdf-page-canvas.directive';
import { sourcePdfCache } from '../source/source-pdf-cache';
import { 
  FieldSchemaResponse, 
  FieldSchemaField, 
  FieldMetadata 
} from '../../models/truth-management.model';
// Configure PDF.js worker once at module load (same guarded setup as the
// source page — whichever module loads first wins, both pin the same version).
if (typeof window !== 'undefined' && !(pdfjsLib as any).GlobalWorkerOptions.workerSrc) {
  (pdfjsLib as any).GlobalWorkerOptions.workerSrc =
    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs';
}

// Ground truth status enum
enum GroundTruthStatus {
  COMPLETE = 'complete',
  PARTIAL = 'partial',
  NOT_STARTED = 'not-started'
}

// Status metadata interface
interface StatusMetadata {
  status: GroundTruthStatus;
  tooltip: string;
  filledRequired: number;
  totalRequired: number;
}

interface PdfScrollAnchor {
  left: number;
  top: number;
  ratioX: number;
  ratioY: number;
}


// Helper function to get status CSS class
function getStatusClass(status: GroundTruthStatus): string {
  switch (status) {
    case GroundTruthStatus.COMPLETE:
      return 'status-complete';
    case GroundTruthStatus.PARTIAL:
      return 'status-partial';
    case GroundTruthStatus.NOT_STARTED:
      return 'status-not-started';
    default:
      return 'status-unknown';
  }
}

// Helper function to safely convert union types to string
// Handles string, string[], TableData, null, undefined
function toStringValue(value: string | string[] | TableData | undefined | null): string {
  if (!value) return '';
  if (typeof value === 'object' && !Array.isArray(value)) {
    // TableData or other object - return empty string (not a string representation)
    return '';
  }
  if (Array.isArray(value)) return value[0]?.trim() || '';
  return String(value).trim();
}

@Component({
  selector: 'app-truth-management',
  imports: [CommonModule, FormsModule, PdfLazyViewerComponent, PageHeaderComponent, TableRowInputComponent, BboxSnipOverlayComponent],
  templateUrl: './truth-management.html',
  styleUrl: './truth-management.scss',
})
export class TruthManagement implements OnInit, OnDestroy, AfterViewInit, CanComponentDeactivate {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('pdfContainer') pdfContainer!: ElementRef<HTMLDivElement>;
  @ViewChild(PdfLazyViewerComponent) lazyViewer?: PdfLazyViewerComponent;
  @ViewChild('snipHost', { read: ViewContainerRef }) snipHost?: ViewContainerRef;
  @ViewChildren(TableRowInputComponent) tableRowInputs!: QueryList<TableRowInputComponent>;

  // ============================================
  // MANUAL BOUNDING-BOX SNIP STATE
  // ============================================
  activeSnipAttributeId: number | null = null;
  activeSnipPage: number | null = null;
  activeSnipArrayIndex: number | null = null;
  private originalFieldSourceDataMap: Map<number, FieldSourceData> = new Map();
  private manuallyOverriddenAttributeIds: Set<number> = new Set();
  private snipOverlayRef: ComponentRef<BboxSnipOverlayComponent> | null = null;
  private snipInstructionBarEl: HTMLElement | null = null;
  // Pending (not-yet-saved) manual bounding-box snips, awaiting Save Changes.
  // Nested: outer key = attributeId; inner key = 0-based array index (always 0 for non-array).
  // `previousSourceData` is captured ONCE per attribute on its first pending snip so Undo
  // can restore the pre-snip state regardless of how many per-index snips accumulated.
  private pendingSnipBboxByAttributeId: Map<number, Map<number, {
    pageNumber: number;
    pageWidth: number;
    bbox: [number, number, number, number];
  }>> = new Map();
  private pendingSnipPreviousSourceDataByAttributeId: Map<number, FieldSourceData | null> = new Map();
  // Attribute ids with a pending snip — contributes to the "Save Changes (N)"
  // count via hasUnsavedChanges()/getUnsavedCount() but stays out of the
  // attribute-value bulk-update payload.
  private pendingSnipModifiedAttributeIds: Set<number> = new Set();
  
  // Bound wheel handler for cleanup
  private boundWheelHandler = this.onPdfWheel.bind(this);
  
  loans: Loan[] = [];
  documents: Document[] = [];
  files: File[] = [];
  attributes: Attribute[] = [];
  attributeSearchTerm: string = '';
  truthValues: TruthValue[] = [];
  fileSearchTerm: string = ''; 
  
  selectedLoan: Loan | null = null;
  selectedDocument: Document | null = null;
  selectedDocumentId: number | null = null;
  selectedFile: File | null = null;
  selectedView: 'files' | 'attributes' = 'files';
  invalidAttributes: Set<string> = new Set(); // Store invalid attribute names 
  private currentClientId: string | null = null; // Track current client ID for API calls
  
  
  // Filtered attributes based on search - always sorted alphabetically
  get filteredAttributes(): Attribute[] {
    let result: Attribute[];
    
    if (!this.attributeSearchTerm.trim()) {
      // No search term - return all attributes
      result = [...this.attributes];
    } else {
      // Filter by search term
      const searchLower = this.attributeSearchTerm.toLowerCase();
      result = this.attributes.filter(attr => {
        // Defensive: ensure attributeLabel and attributeKey are strings before calling toLowerCase
        const label = typeof attr.attributeLabel === 'string' ? attr.attributeLabel : '';
        const key = typeof attr.attributeKey === 'string' ? attr.attributeKey : '';
        return label.toLowerCase().includes(searchLower) || key.toLowerCase().includes(searchLower);
      });
    }
    
    // Always sort alphabetically by attributeLabel for consistent ordering
    return result.sort((a, b) => {
      // Defensive: ensure values are strings before calling toLowerCase/localeCompare
      const labelA = typeof a.attributeLabel === 'string' ? a.attributeLabel : 
                     typeof a.attributeKey === 'string' ? a.attributeKey : '';
      const labelB = typeof b.attributeLabel === 'string' ? b.attributeLabel : 
                     typeof b.attributeKey === 'string' ? b.attributeKey : '';
      return labelA.toLowerCase().localeCompare(labelB.toLowerCase());
    });
  }
  
  // Filtered files (samples) based on search
get filteredFiles(): File[] {
  if (!this.fileSearchTerm.trim()) {
    return this.files;
  }
  const searchLower = this.fileSearchTerm.toLowerCase();
  return this.files.filter(file => 
    file.fileName?.toLowerCase().includes(searchLower)
  );
}
  // Cache for document counts
  documentFileCounts: Map<number, number> = new Map();
  documentAttributeCounts: Map<number, number> = new Map();
  // Cache for file-specific attribute counts
  fileAttributeCounts: Map<number | string, number> = new Map();
  // Cache for file status indicators
  fileStatusCache: Map<number | string, StatusMetadata> = new Map();
  
  showDialog = false;
  dialogMode: 'add' | 'edit' = 'add';
  dialogData: { id?: number; key: string; value: string } = { key: '', value: '' };
  
  showAddLoanDialog = false;
  newLoanNumber: string = '';
  newLoanName: string = '';
  
  showAddDocumentDialog = false;
  newDocumentLoanId: number | string = '';
  newDocumentNumber: string = '';
  newDocumentName: string = '';
  newDocumentDescription: string = '';
  
  showAddAttributeDialog = false;
  newAttributeLabel: string = '';
  newAttributeKey: string = '';
  newAttributeDescription: string = '';
  
  showEditAttributeDialog = false;
  editAttributeId: number | null = null;
  editAttributeLabel: string = '';
  editAttributeDescription: string = '';
  
  newFileName: string = '';
  newFilePath: string = '';
  newFileSize: number = 0;
  newFileMimeType: string = '';
  selectedFileObject: File | null = null;
  showTruthValuesPage: boolean = false;
  isLoadingTruthValues: boolean = false;

  // Tooltip properties
  tooltipVisible: boolean = false;
  tooltipText: string = '';
  tooltipTop: number = 0;
  tooltipLeft: number = 0;

  // PDF Viewer properties
  pdfSrc: string = '';
  isLoadingPdf: boolean = false;
  private pdfSasRetryCount = 0;
  private pdfLoadingFileId = '';
  private readonly PDF_MAX_RETRIES = 3;
  isUploadingFile: boolean = false;  // Loading state for file upload
  uploadProgress: string = '';
  isDownloadingFiles = false;
  downloadProgress = '';
  isDeletingAttribute: boolean = false;  // Loading state for attribute deletion
  isUpdatingAttribute: boolean = false;  // Loading state for attribute update
  currentPage: number = 1;
  pdfReady: boolean = false;
  totalPages: number = 0;
  zoom: number = 1.0;
  zoomPercentage: number = 100;
  pageInput: number | string = 1;
  pageRotations: Map<number, number> = new Map(); // Track rotation per page (page number -> degrees)
  private lastLoadedPdfSource: string = '';
  // Tracks the documentCode that was last used to initialize the forkJoin.
  // When only fileId changes (file switch), queryParams fires again but documentCode is unchanged;
  // we skip the forkJoin so getEvaluationFiles/getFieldSchemas/loadAllFileStatuses don't re-run.
  private lastLoadedDocumentCode: string | null = null;
  private isRotatingPdf: boolean = false;
  private pendingScrollRestore: PdfScrollAnchor | null = null;
  private pendingPageRestore: number | null = null;
  private rotateScrollLockUntil: number = 0;
  private rotateScrollRestoreFrame: number | null = null;
  private suppressPageBindingDuringRotate: boolean = false;
  private rotateEpoch: number = 0;  // Incremented on each rotate to isolate stale callbacks
  private activeRotateEpoch: number = 0;  // Tracks current active rotate cycle
  private pendingPageAnchor: { epoch: number; page: number; offsetRatio: number } | null = null;  // Page-anchor instead of raw scroll
  private rotateScrollLocked: boolean = false;  // Flag to lock scroll during rotate
  private rotateScrollHandler: ((e: Event) => void) | null = null;  // Scroll event handler to prevent jumps
  
  // PDF document reference (PDFDocumentProxy) — template-bound to the lazy viewer
  pdfDocument: any = null;
  // Cache for PDF page widths (page number -> width in PDF points)
  pdfPageWidths: Map<number, number> = new Map();
  // Cache for PDF page heights (page number -> height in PDF points)
  pdfPageHeights: Map<number, number> = new Map();
  private serverPageSizes: { width: number; height: number }[] | null = null;
  private serverPdfLength: number | null = null;

  // Layout model consumed by app-pdf-lazy-viewer — one entry per page, in px,
  // exact canvas pixel size at the current zoom (same contract as the source page).
  pageNumbers: number[] = [];
  pageWidthsPx: number[] = [];
  pageHeightsPx: number[] = [];
  pageScales: number[] = [];
  readonly pageGapRem = 1;
  pageGapPx = 16;
  pxPerRem = 16;
  // Monotonic counter guarding pdf.js getDocument callbacks against stale sources.
  private pdfDocLoadSeq = 0;
  // Background full-download of the current PDF into Cache Storage (same
  // mechanism as the source page); aborted on file switch and destroy.
  private warmCacheAbortController: AbortController | null = null;
  // 0-100: drives the "Loading document N%" line under the preview header
  // while the warm-cache download is in flight (same as the source page).
  pdfLoadProgress = 0;
  // True when the current document came in via the range transport. Gates the
  // bulk page-dimension pre-cache: over ranges it would fire getPage() for
  // every page at once (a startup range-request storm); it runs later instead,
  // once the warm download serves ranges from the local blob for free.
  private lastLoadUsedRangeTransport = false;

  // ResizeObserver for reactive bounding box re-rendering (User Story 7126)
  private pdfContainerResizeObserver?: ResizeObserver;
  private reRenderDebounceTimer?: number;
  
  // Cache for uploaded file blob URLs (keyed by file ID)
  uploadedFileBlobUrls: Map<number | string, string> = new Map();
  // Temporary storage for blob URL during upload
  pendingUploadBlobUrl: string | null = null;

  // Inline truth value editing properties
  editingAttributeId: number | null = null;
  editingArrayIndex: number | null = null; // Track which array element is being edited
  editingTruthValueText: string = '';
  editingFieldType: 'text' | 'date' | 'select' = 'text';
  editingFieldMetadata: any = null;
  editingAllowedValues: string[] = []; // Allowed values from API for dropdowns
  isSavingTruthValue: boolean = false;
  
  // Immediate visual feedback - show activating state on mousedown before render
  activatingAttributeId: number | null = null;
  activatingArrayIndex: number | null = null;

  // ============================================
  // DATA EXTRACTION STATE
  // ============================================
  
  // Extraction state flags - support multiple concurrent extractions
  isExtracting: boolean = false; // Legacy flag for backward compatibility 
  extractingFiles: Set<number | string> = new Set(); // Track all files currently extracting
  extractionRequestIds: Map<number | string, string> = new Map(); // fileId -> requestId
  extractingFileId: number | string | null = null; // Legacy - points to currently selected file if extracting
  lastExtractionTimestamp: number = 0;
  isOverrideMode: boolean = false; // Track when user confirmed to override existing values
  
  // Extraction history to track threshold logic
  extractionHistory: Map<number | string, {
    timestamp: number;
    fieldCount: number;
    modifiedSinceExtraction: number;
  }> = new Map();
  
  // Track newly uploaded files that need first-time extraction (bypass threshold)
  newlyUploadedFiles: Set<number | string> = new Set();
  
  // Track which files have been auto-filled (for re-trigger confirmation)
  autoFilledFiles: Set<number | string> = new Set();
  
  // Track fields that were overridden by a second auto-fill
  overriddenAutoPopulatedAttributeIds: Set<number> = new Set();
  
  // Auto-population threshold (configurable)
  private readonly AUTO_POPULATE_THRESHOLD = 0.1;  // ≤10% filled -> allow auto-population, >10% -> block
  private readonly EXTRACTION_POLL_INTERVAL = 2000;  // 2 seconds
  private readonly EXTRACTION_TIMEOUT = 1800000;  // 30 minutes

  // ============================================
  // BOUNDING BOX HIGHLIGHTING STATE (User Story 7126)
  // ============================================
  
  // Store source data (page, bbox) per attribute after Auto-Fill
  fieldSourceDataMap: Map<number, FieldSourceData> = new Map();
  
  // Currently highlighted attribute (only one can be highlighted at a time)
  highlightedAttributeId: number | null = null;
  private _bboxRenderGeneration = 0;
  
  // Track highlighted table cell (when clicking on a table cell in edit mode)
  highlightedTableCell: { attributeId: number; rowIndex: number; columnName: string } | null = null;
  
  // Computed bounding box overlays for rendering (in pixel coordinates)
  activeBoundingBoxes: Array<{
    left: number;
    top: number;
    width: number;
    height: number;
    index: number;
  }> = [];

  

  // Date formatting helpers - MM/DD/YYYY format (e.g., 07/22/2000)
  // Delegate to TruthManagementService so this logic is shared with table-row-input's
  // date columns instead of being duplicated.
  formatDateForDisplay(value: string): string {
    return this.truthManagementService.formatDateForDisplay(value);
  }

  // Normalize various date formats to M/D/YYYY
  normalizeDateToMMDDYYYY(value: string): string {
    return this.truthManagementService.normalizeDateToMMDDYYYY(value);
  }

  // Check if year is a leap year
  isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  }

  // Helper function to validate date in MM/DD/YYYY or MM-DD-YYYY format
  isValidDateFormat(value: string): boolean {
    return this.truthManagementService.isValidDateFormat(value);
  }

  // HELPER: Safely convert value to string, handling both string and string[] types
  private toStringValue(value: string | string[] | undefined | null): string {
    if (!value) return '';
    if (typeof value === 'string') return value.trim();
    if (Array.isArray(value)) return value.length > 0 ? String(value[0]).trim() : '';
    return '';
  }

  // HELPER: Safely check if string | string[] value is empty
  private isEmptyValue(value: string | string[] | TableData | undefined): boolean {
    if (!value) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0 || value.every(v => String(v).trim() === '');
    // TableData (object) - empty if no values or all values are empty
    if (typeof value === 'object') {
      return Object.values(value).every(v => !v || String(v).trim() === '');
    }
    return true;
  }

  // HELPER: Is this text an explicit "N/A"/"NA" marker (case-insensitive)?
  private isNAText(value: string): boolean {
    const normalized = value.trim().toLowerCase();
    return normalized === 'n/a' || normalized === 'na';
  }

  // Classify an autofill-extracted value before it's applied:
  // - 'empty': nothing was extracted - ignore (don't apply, count, or show)
  // - 'invalid': not a real value for this field - an explicit "N/A"/"NA", or non-empty text
  //   that fails the field's own validation (allowed_values or date format) - ignore, same as 'empty'
  // - 'valid': a real value for this field - apply normally
  // Autofill never auto-toggles the N/A state - that stays a manual, user-initiated action.
  private classifyAutofillValue(
    attribute: Attribute,
    extractedValue: string | string[] | TableData
  ): 'empty' | 'invalid' | 'valid' {
    // Tables: no per-cell allowed-values/date validation is modeled, so only the empty check applies.
    if (extractedValue && typeof extractedValue === 'object' && !Array.isArray(extractedValue)) {
      return this.isEmptyValue(extractedValue) ? 'empty' : 'valid';
    }

    const parts = Array.isArray(extractedValue)
      ? extractedValue.map(v => String(v).trim()).filter(v => v.length > 0)
      : (typeof extractedValue === 'string' && extractedValue.trim() ? [extractedValue.trim()] : []);

    if (parts.length === 0) {
      return 'empty';
    }

    if (parts.every(p => this.isNAText(p))) {
      return 'invalid';
    }

    const nonNAParts = parts.filter(p => !this.isNAText(p));

    if (attribute.allowed_values && attribute.allowed_values.length > 0) {
      const allowedLower = attribute.allowed_values.map(v => v.trim().toLowerCase());
      return nonNAParts.every(p => allowedLower.includes(p.toLowerCase())) ? 'valid' : 'invalid';
    }

    const featureLower = String(attribute.feature || '').toLowerCase();
    const labelLower = String(attribute.attributeLabel || '').toLowerCase();
    const keyLower = String(attribute.attributeKey || '').toLowerCase();
    const isDateField = featureLower.includes('date') || labelLower.includes('date') || keyLower.includes('date');

    if (isDateField) {
      return nonNAParts.every(p => this.isValidDateFormat(p)) ? 'valid' : 'invalid';
    }

    return 'valid';
  }

isSupportedDateValue(value: string): boolean {
  const trimmed = String(value || '').trim();
  if (!trimmed) return false;
  
  // Accept both MM/DD/YYYY and MM-DD-YYYY formats
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed) || /^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
    return this.isValidDateFormat(trimmed);
  }
  
  return false;
}

  // Delegates to TruthManagementService so this logic is shared with table-row-input's
  // date columns instead of being duplicated.
  onDateKeydown(event: KeyboardEvent, attributeId?: number): void {
    this.truthManagementService.restrictDateKeydown(event);
  }

  // Validate and correct date digits
  validateDateDigits(digits: string): string {
    return this.truthManagementService.validateDateDigits(digits);
  }

  formatDateForInput(event: Event, attributeId: number): void {
    const input = event.target as HTMLInputElement;
    const formatted = this.truthManagementService.formatDigitsToMMDDYYYY(input.value);

    // Update the value
    this.editingTruthValueText = formatted;

    // Only update input if different to avoid cursor issues
    if (input.value !== formatted) {
      input.value = formatted;
    }

    // Position cursor at the end
    const cursorPos = formatted.length;
    setTimeout(() => {
      input.setSelectionRange(cursorPos, cursorPos);
    }, 0);

    // Mark as modified only if it's a valid complete date
    if (this.isValidDateFormat(formatted)) {
      this.markDateModified(attributeId, formatted);
    }
  }

  markDateModified(attributeId: number, dateValue: string): void {
    const existingValue = this.currentFileTruthValues.get(attributeId);
    const originalFullValue = this.getCurrentTruthValue(attributeId);
    const normalizedDateValue = this.normalizeDateForComparison(String(dateValue || '').trim());
    const storedOriginalValue = existingValue?.value;
    
    let newArray: string[];
    
    // If editing a specific array index, update only that index
    if (this.editingArrayIndex !== null) {
      const originalArray = Array.isArray(originalFullValue)
        ? originalFullValue.map(v => String(v))
        : originalFullValue ? [String(originalFullValue)] : [];
      newArray = [...originalArray];
      
      // Ensure array is large enough
      while (newArray.length <= this.editingArrayIndex) {
        newArray.push('');
      }
      
      // Update only the specific index
      newArray[this.editingArrayIndex] = dateValue;

      const storedArray = Array.isArray(storedOriginalValue)
        ? storedOriginalValue.map(v => this.normalizeDateForComparison(String(v).trim()))
        : (typeof storedOriginalValue === 'string'
          ? [this.normalizeDateForComparison(storedOriginalValue.trim())]
          : []);
      const existingAtIndex = this.normalizeDateForComparison(String(storedArray[this.editingArrayIndex] || ''));

      // No-op guard: don't mutate state for same effective date value.
      if (normalizedDateValue === existingAtIndex) {
        return;
      }
    } else {
      // Single value, not an array
      newArray = [dateValue];

      const storedSingle = Array.isArray(storedOriginalValue)
        ? String(storedOriginalValue[0] || '').trim()
        : (typeof storedOriginalValue === 'string' ? storedOriginalValue.trim() : '');
      const normalizedStoredSingle = this.normalizeDateForComparison(storedSingle);

      // No-op guard: don't mutate state for same effective date value.
      if (normalizedDateValue === normalizedStoredSingle) {
        return;
      }
    }
    
    // Store modified value as native array
    this.modifiedValues.set(attributeId, newArray);
    this.modifiedAttributeIds.add(attributeId);
    this.autoPopulatedAttributeIds.delete(attributeId);
    this.cdr.detectChanges();
  }

  // Normalize date to comparable format (MM/DD/YYYY)
  normalizeDateForComparison(value: string): string {
    return this.truthManagementService.normalizeDateForComparison(value);
  }

  onDateInputBlur(attributeId: number): void {
    // Skip validation if user is clicking the date picker
    if (this.isPickingDate) {
      this.isPickingDate = false;
      return;
    }
    
    // Skip validation if refocusing after an error
    if (this.isRefocusingAfterError) {
      this.isRefocusingAfterError = false;
      return;
    }
    
    const value = this.editingTruthValueText.trim();
    
    // If empty, allow blur without validation
    if (!value) {
      this.onTruthValueBlur(attributeId);
      return;
    }
    
    // Try to normalize the date first
    const normalized = this.normalizeDateToMMDDYYYY(value);
    
    // Update the text with normalized value
    if (normalized !== value) {
      this.editingTruthValueText = normalized;
    }
    
    // Validate MM/DD/YYYY format with proper date ranges
    if (!this.isValidDateFormat(normalized)) {
      // Invalid date - show alert
      alert(this.truthManagementService.getDateValidationErrorMessage(normalized));
      // Refocus the input after alert is dismissed so user can continue editing
      this.isRefocusingAfterError = true;
      setTimeout(() => {
        const input = document.querySelector('.date-input-wrapper input') as HTMLInputElement;
        if (input) {
          input.focus();
          // Position cursor at the end
          input.setSelectionRange(input.value.length, input.value.length);
        }
      }, 0);
      // Don't save invalid date
      return;
    }

    this.onTruthValueBlur(attributeId);
  }

  // Convert MM/DD/YYYY to yyyy-mm-dd for native date picker
  getDatePickerValue(): string {
    return this.truthManagementService.getDatePickerValueFromText(this.editingTruthValueText);
  }

  // Handle date picker selection
  onDatePickerChange(event: Event, attributeId: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value; // yyyy-mm-dd format from date picker
    if (value) {
      // Convert to MM/DD/YYYY format
      const formatted = this.formatDateForDisplay(value);
      const normalizedCurrent = this.normalizeDateForComparison(this.editingTruthValueText.trim());
      const normalizedFormatted = this.normalizeDateForComparison(formatted.trim());

      // No-op guard: selecting same date should not clear autofill state.
      if (normalizedCurrent !== normalizedFormatted) {
        this.editingTruthValueText = formatted;
        this.markDateModified(attributeId, formatted);
      }
      
      // Focus back to the text input
      const wrapper = input.closest('.date-input-wrapper');
      if (wrapper) {
        const textInput = wrapper.querySelector('.date-input') as HTMLInputElement;
        if (textInput) {
          textInput.focus();
        }
      }
    }
  }

  // Open the hidden date picker
  // Handle mousedown on date picker icon - this fires BEFORE blur
  onDatePickerIconMousedown(event: Event): void {
    event.preventDefault();
    // Set flag BEFORE blur fires to prevent edit mode from closing
    this.isPickingDate = true;
  }

  openDatePicker(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    
    // Flag should already be set by mousedown
    this.isPickingDate = true;
    
    const icon = event.target as HTMLElement;
    const wrapper = icon.closest('.date-input-wrapper');
    if (wrapper) {
      const datePicker = wrapper.querySelector('.date-picker-hidden') as HTMLInputElement;
      if (datePicker) {
        // Use setTimeout to ensure the picker opens after the click event
        setTimeout(() => {
          datePicker.focus();
          try {
            datePicker.showPicker();
          } catch (e) {
            // Fallback: click the input if showPicker fails
            datePicker.click();
          }
        }, 0);
      }
    }
  }

  currentFileTruthValues: Map<number, TruthValue> = new Map();
  modifiedValues: Map<number, string | string[] | TableData> = new Map();
  modifiedAttributeIds: Set<number> = new Set();

  // Whether a table cell is currently being edited - mirrors editingAttributeId for the Save Changes button,
  // since table cell edits aren't committed to modifiedAttributeIds until blur
  isEditingTableCell: boolean = false;
  
  // Track auto-populated attribute IDs (separate from manual edits)
  autoPopulatedAttributeIds: Set<number> = new Set();
  
  // Store original auto-populated values before manual edits (for undo functionality)
    autoPopulatedOriginalValues: Map<number, string | string[] | TableData> = new Map();  
  
  // Track attributes marked as Not Applicable (N/A)
  notApplicableAttributes: Set<number> = new Set();
  
  // Map of which backend attributes contributed to each component attribute (for array fields with idx templates)
  originalApiAttributeNames: Map<number, Set<string>> = new Map();
  
  // Track N/A values that came from backend (to display "N/A" text instead of blank)
  backendNAAttributes: Set<number> = new Set();
  
  // Field metadata from document_keys API
  fieldMetadataCache: Map<string, any> = new Map();
  
  // File column collapse state
  isFileColumnCollapsed: boolean = false;

  // Draggable divider state between the PDF viewer and the truth values form
  splitPosition: number = 68; // Default viewer/form split (%), matches prior 2.2fr : minmax(420px, 1fr) ratio
  isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartSplit: number = 0;

  // Flag to prevent blur validation when opening date picker
  isPickingDate: boolean = false;
  
  // Flag to prevent blur validation when refocusing after error
  isRefocusingAfterError: boolean = false;

  // Swagger API integration
  useSwaggerAPI = true; // Toggle to use Swagger API for document types
  adminDocuments: AdminDocument[] = [];
  fieldSchemaMap: Map<string, FieldSchemaField[]> = new Map();          // Renamed: stores raw field_schema fields
  loadingFromAPI = false;
  isAuthenticated: boolean = false;
  
  // User role management
  isAdmin: boolean = false;
  isLoadingUserRole: boolean = true;
  
  // Evaluation files from Swagger API
  evaluationFiles: EvaluationFile[] = [];
  loadingEvalFiles = false;
  // Cache for truth values per file — avoids redundant API calls when revisiting a file.
  // Invalidated on bulk save (for that file) and cleared on page destroy.
  // Now stores full AttributesResponse to preserve attribute_summary
  private fileTruthValuesCache = new Map<string, AttributesResponse>();
  
  // Store attribute_summary from Attributes API for the selected file
  // Updated when file is selected (from Attributes API response)
  selectedFileAttributeSummary: { total_count: number; filled_count: number } | null = null;
  
  constructor(
    private truthManagementService: TruthManagementService,
    public router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    public authService: AuthService,
    private modalService: ModalService,
    private ngZone: NgZone,
    private notificationService: NotificationService,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.isAuthenticated = this.authService.hasValidToken();
    
    // Subscribe to client changes to update clientId for API calls
    this.authService.currentClient$
      .pipe(filter(client => client !== null))
      .subscribe((client: { client_id: string; client_name: string }) => {
        this.currentClientId = client.client_id;
      });
    
    // Check user role for admin features
    // Subscribe to centralized role from AuthService (single source of truth)
    this.authService.userRole$
      .pipe(filter(role => role !== null), take(1))
      .subscribe(role => {
        this.isAdmin = role === 'admin' || role === 'superuser';
        this.isLoadingUserRole = false;
        this.cdr.detectChanges();
      });
    
    // Check if there were ongoing auto-fills before page refresh/navigation
    // Now supports MULTIPLE concurrent extractions
    const savedExtractionsStr = localStorage.getItem('truthManagement_extractions');
    let hasRestoredExtraction = false;
    
    if (savedExtractionsStr) {
      try {
        const savedExtractions = JSON.parse(savedExtractionsStr);
        const now = Date.now();
        
        // Restore ALL active extraction states
        for (const [fileIdStr, extractionState] of Object.entries(savedExtractions as Record<string, any>)) {
          const timeSinceExtraction = now - extractionState.timestamp;
          
          // If requestId exists and not too old, restore extraction state
          if (extractionState.requestId && timeSinceExtraction < this.EXTRACTION_TIMEOUT) {
            const fileId = extractionState.fileId;
            const requestId = extractionState.requestId;
            // Restore extraction state for this file
            this.extractingFiles.add(fileId);
            this.extractingFiles.add(fileId.toString()); // Also add string version
            this.extractionRequestIds.set(fileId, requestId);
            this.isExtracting = true;
            hasRestoredExtraction = true;
            
            // Restore override mode if this extraction was in override mode
            if (extractionState.isOverrideMode) {
              this.isOverrideMode = true;
            }
            
            // If this is the first one, set as extractingFileId
            if (!this.extractingFileId) {
              this.extractingFileId = fileId;
              this.lastExtractionTimestamp = extractionState.timestamp || Date.now();
            }
            
            // Resume polling in service for this file
            setTimeout(() => {
              this.truthManagementService.resumePollingForRequest(
                requestId, 
                fileId, 
                extractionState.documentCode, 
                extractionState.fileName
              );
            }, 500 + Math.random() * 300); // Slight delay variation to avoid simultaneous polls
          }
        }
        // Force UI update if we restored any extractions
        if (hasRestoredExtraction) {
          setTimeout(() => this.cdr.detectChanges(), 100);
        }
      } catch (e) {
        localStorage.removeItem('truthManagement_extractions');
      }
    }
    
    // Only reset extraction state if we didn't restore any
    if (!hasRestoredExtraction) {
      this.isExtracting = false;
      this.isOverrideMode = false;
      this.extractingFiles.clear();
      this.extractionRequestIds.clear();
    }
    
    // Subscribe to extraction completion events from service
    this.truthManagementService.extractionCompleted$.subscribe(event => {
      // Type-safe comparison to handle string/number mismatches
      const isCurrentFile = this.selectedFile && (
        event.fileId == this.selectedFile.id || // Loose equality
        event.fileId === this.selectedFile.id ||
        event.fileId.toString() === this.selectedFile.id.toString()
      );
      
      // If this is the currently selected file AND extraction succeeded, apply results immediately
      if (event.success && isCurrentFile) {
        // Apply results immediately - no delay needed
        this.checkAndApplyPendingResults(event.fileId);
      } else if (event.success) {
      }
      
      // Clear extraction state for this file - delete all type variants
      this.extractingFiles.delete(event.fileId);
      this.extractingFiles.delete(event.fileId.toString());
      this.extractingFiles.delete(Number(event.fileId));
      this.extractionRequestIds.delete(event.fileId);
      this.extractionRequestIds.delete(event.fileId.toString());
      this.extractionRequestIds.delete(Number(event.fileId));
      
      // Update legacy flags if this was the current file
      if (this.extractingFileId == event.fileId) { // Loose equality
        this.extractingFileId = null;
      }
      
      // Update global flag
      this.isExtracting = this.extractingFiles.size > 0;
      
      // Force UI update
      this.cdr.detectChanges();
    });
    
    // Load all documents for dropdown — skipped when page is opened via documentCode/documentId
    // (GT page has no document dropdown; documents are only needed for the addDocument() admin flow)
    const initialCode = this.route.snapshot.queryParams['documentCode'];
    const initialDocId = this.route.snapshot.queryParams['documentId'];
    if (!initialCode && !initialDocId) {
      this.loadAllDocuments();
    }

   // Check for documentId or documentCode query parameter
  this.route.queryParams.subscribe(params => {
  const documentId = params['documentId'];
  const documentCode = params['documentCode'];
  const fileId = params['fileId']; // Capture fileId for restoration after refresh
  
  // If documentCode is provided (from Loan Validation), use Swagger API
  if (documentCode) {
    // Only re-initialize when documentCode actually changes.
    // Every file switch calls router.navigate({fileId}) which re-fires this subscription,
    // but documentCode is unchanged — skip the forkJoin to avoid redundant API calls.
    if (documentCode === this.lastLoadedDocumentCode) {
      return;
    }
    this.lastLoadedDocumentCode = documentCode;
    let doc = this.documents.find(d => d.documentNumber === documentCode);
    if (!doc) {
      // Create a temporary document entry
      doc = {
        id: Date.now(), // temporary ID
        documentNumber: documentCode,
        documentName: this.formatDocumentName(documentCode),
        description: '',
        loanId: 0,
        createdDate: new Date()
      } as Document;
      this.documents.push(doc);
    }
    this.selectedDocument = doc;
    this.selectedDocumentId = doc.id;
    
    //  Load ALL required data in parallel — NOW USING ONLY field_schema API
    this.loadingEvalFiles = true;
    forkJoin({
      files: this.truthManagementService.getEvaluationFiles(documentCode),
      fieldSchemas: this.truthManagementService.getFieldSchemas(documentCode)
    }).subscribe({
      next: ({ files, fieldSchemas }: { files: any[], fieldSchemas: FieldSchemaResponse }) => { 
        // Process FILES — API already filters by document_model; safety-check here
        const docTypeLower = documentCode.toLowerCase();
        const filteredFiles = files.filter((ef: any) =>
          (ef.document_model || '').toLowerCase() === docTypeLower
        );
        
        this.evaluationFiles = filteredFiles;
        this.files = filteredFiles.map((ef: any, index: number) => ({
          id: ef.uuid || index + 1,
          fileName: ef.file_name,
          documentId: this.selectedDocument?.id || 0,
          uploadedDate: new Date(ef.created_at || new Date())
        } as File));
        
        // Process FIELD_SCHEMA — extract fields and build matching map
        if (fieldSchemas && fieldSchemas.fields) {
          // Store raw field_schema fields
          this.fieldSchemaMap.set(documentCode, fieldSchemas.fields);

          // Convert field_schema fields to attributes with idx expansion
          // (also detects table fields and sets isTableField/tableColumns)
          this.attributes = this.expandFieldSchemaToAttributes(fieldSchemas.fields);
          // Debug: Log first 3 attributes to verify attributeKey mapping
          if (this.attributes.length > 0) {
            this.attributes.slice(0, 3).forEach(attr => {
            });
          }
        }
        this.loadingEvalFiles = false;
        
        // Calculate file statuses from attribute_summary in files API response
        if (this.evaluationFiles.length > 0) {
          this.loadFileStatusesFromSummary();
        } else {
          setTimeout(() => this.cdr.detectChanges(), 0);
        }
        
        // After loading files, check if there's a fileId in URL to restore selected file
        if (fileId && this.files.length > 0) {
          const fileToSelect = this.files.find(f => f.id.toString() === fileId || f.id === fileId);
          if (fileToSelect) {
            // Use setTimeout to ensure DOM is ready
            setTimeout(() => {
              this.selectFileOnly(fileToSelect);
            }, 100);
          }
        }
      },
      error: (err) => {
        this.files = [];
        this.attributes = [];
        this.loadingEvalFiles = false;
        setTimeout(() => this.cdr.detectChanges(), 0);
      }
    });
    
    setTimeout(() => this.cdr.detectChanges(), 0);
  }
  // If documentId is provided, load that document's files and attributes
  else if (documentId) {
    this.selectedDocumentId = +documentId;
    this.loadDocumentById(+documentId);
  } else {
    this.files = [];
    this.attributes = [];
  }
});
}

  loadAllDocuments(): void {
    this.loadDocumentsFromSwaggerAPI();
  }

  loadDocumentsFromSwaggerAPI(): void {
    this.loadingFromAPI = true;
    
    // Require client ID for API call
    if (!this.currentClientId) {
      return;
    }
    
    this.truthManagementService.getAdminDocuments(this.currentClientId).subscribe({
      next: (response: any) => {
        // Handle the API response format: { document_models: string[], count: number }
        // Note: API returns document_models, not document_types
        let documentTypes: string[] = [];
        
        if (response && response.document_models && Array.isArray(response.document_models)) {
          documentTypes = response.document_models;
        } else if (response && response.document_types && Array.isArray(response.document_types)) {
          // Fallback for legacy API format
          documentTypes = response.document_types;
        } else if (response && Array.isArray(response)) {
          documentTypes = response;
        }
        
        if (documentTypes.length > 0) {
          // Map to AdminDocument format
          this.adminDocuments = documentTypes.map((docType, index) => ({
            id: index + 1,
            document_code: docType,
            document_name: this.formatDocumentName(docType),
            description: '',
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }));
          
          // Also map to Document format for backward compatibility with dropdown
          this.documents = this.adminDocuments.map(doc => ({
            id: doc.id,
            documentNumber: doc.document_code,
            documentName: doc.document_name,
            description: doc.description || '',
            loanId: 0,
            createdDate: new Date(doc.created_at)
          } as Document));
          
          this.loadingFromAPI = false;
          setTimeout(() => this.cdr.detectChanges(), 0);
        } else {
          this.loadingFromAPI = false;
          this.documents = [];
        }
      },
      error: (error) => {
        this.loadingFromAPI = false;
        this.documents = [];
        setTimeout(() => this.cdr.detectChanges(), 0);
      }
    });
  }

  // Helper to format document code into readable name
  formatDocumentName(code: string): string {
    return code
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  onDocumentChange(): void {
  if (this.selectedDocumentId) {
    // Find the selected document from our loaded documents
    const selectedDoc = this.documents.find(d => d.id === this.selectedDocumentId);
    
    if (selectedDoc) {
      // Set the document directly
      this.selectedDocument = selectedDoc;
      // Update URL to persist document selection
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { documentCode: selectedDoc.documentNumber },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
      
      // Clear previous data
      this.files = [];
      this.attributes = [];
      this.fileStatusCache.clear();
      
      // Load ALL required data in parallel — NOW USING ONLY field_schema API
      this.loadingEvalFiles = true;
      forkJoin({
        files: this.truthManagementService.getEvaluationFiles(selectedDoc.documentNumber),
        fieldSchemas: this.truthManagementService.getFieldSchemas(selectedDoc.documentNumber)
      }).subscribe({
        next: ({ files, fieldSchemas }: { files: any[], fieldSchemas: FieldSchemaResponse }) => {
          // Process FILES — API already filters by document_model
          const docTypeLower = selectedDoc.documentNumber.toLowerCase();
          const filteredFiles = files.filter((ef: any) =>
            (ef.document_model || '').toLowerCase() === docTypeLower
          );
          
          this.evaluationFiles = filteredFiles;
          this.files = filteredFiles.map((ef: any, index: number) => ({
            id: ef.uuid || index + 1,
            fileName: ef.file_name,
            documentId: this.selectedDocument?.id || 0,
            uploadedDate: new Date(ef.created_at || new Date())
          } as File));
          
          // Process FIELD_SCHEMA — extract fields and build matching map
          if (fieldSchemas && fieldSchemas.fields) {
            // Store raw field_schema fields
            this.fieldSchemaMap.set(selectedDoc.documentNumber, fieldSchemas.fields);
            
            // Convert field_schema fields to attributes with idx expansion
            // Array fields with {idx} templates are expanded into multiple entries
            this.attributes = this.expandFieldSchemaToAttributes(fieldSchemas.fields);
            // Debug: Log sample attributes to verify expansion
            if (this.attributes.length > 0) {
              this.attributes.slice(0, 10).forEach((attr, idx) => {
              });
              
              // Check for CAIVRS fields specifically
              const caivrFields = this.attributes.filter(a => 
                a.attributeLabel?.toLowerCase().includes('caivrs') || 
                a.attributeKey?.toLowerCase().includes('caivrs')
              );
              if (caivrFields.length > 0) {
                caivrFields.forEach(attr => {
                });
              }
            }
          }
          
          // Set loading state to false
          this.loadingEvalFiles = false;
          
          // Calculate file statuses
          if (this.evaluationFiles.length > 0) {
            this.loadFileStatusesFromSummary();
          }
          
          // After loading files, check if there's a fileId in URL to restore selected file
          const fileId = this.route.snapshot.queryParams['fileId'];
          if (fileId && this.files.length > 0) {
            const fileToSelect = this.files.find(f => f.id.toString() === fileId || f.id === fileId);
            if (fileToSelect) {
              setTimeout(() => {
                this.selectFileOnly(fileToSelect);
              }, 100);
            }
          }
          
          setTimeout(() => this.cdr.detectChanges(), 0);
        },
        error: (err) => {
          this.files = [];
          this.attributes = [];
          this.loadingEvalFiles = false;
          setTimeout(() => this.cdr.detectChanges(), 0);
        }
      });
    }
  } else {
    this.selectedDocument = null;
    this.files = [];
    this.attributes = [];
    this.selectedFile = null;
    this.resetPdfViewerState(true);
    setTimeout(() => this.cdr.detectChanges(), 0);
  }
}
/**
 * Get allowed values with their descriptions for display
 * Returns array of {value, description, displayText, fullDescription}
 */
getFieldAllowedValuesWithDescriptions(attributeId: number): Array<{
  value: string;
  description: string;
  displayText: string;
  fullDescription: string;
}> {
  const attr = this.attributes.find(a => a.id === attributeId);
  if (!attr) return [];

  const featureLower = String(attr.feature || '').toLowerCase();
  const allowedValues = (attr.allowed_values && attr.allowed_values.length > 0)
    ? attr.allowed_values
    : (featureLower.includes('signature') ? ['Signed', 'Unsigned'] : []);
  const descriptions = (attr.allowed_values_description && attr.allowed_values_description.length > 0)
    ? attr.allowed_values_description
    : [];

  if (allowedValues.length === 0) return [];
  
  // Get current value being edited
  const editingValue = this.editingAttributeId === attributeId
    ? this.editingTruthValueText.trim()
    : '';

  // For array fields editing a specific index, use only THAT index's value
  let savedValue: string;
  if (this.editingAttributeId === attributeId && this.editingArrayIndex !== null) {
    const fullValue = this.getCurrentTruthValue(attributeId);
    // Convert to array if needed, but skip TableData (shouldn't appear in array fields)
    let values: string[] = [];
    if (typeof fullValue === 'object' && !Array.isArray(fullValue)) {
      // TableData - skip (shouldn't happen in array fields)
      values = [];
    } else if (Array.isArray(fullValue)) {
      values = fullValue.map(v => String(v).trim()).filter(v => v.length > 0 && v.toLowerCase() !== 'n/a');
    } else if (typeof fullValue === 'string') {
      values = fullValue.trim() && fullValue.toLowerCase() !== 'n/a' ? [fullValue.trim()] : [];
    }
    savedValue = values[this.editingArrayIndex] || '';
  } else {
    const fullValue = this.getCurrentTruthValue(attributeId);
    // For non-array display, use first element if array, or the string value
    if (typeof fullValue === 'object' && !Array.isArray(fullValue)) {
      // TableData - return empty (shouldn't happen in scalar fields)
      savedValue = '';
    } else if (Array.isArray(fullValue)) {
      savedValue = fullValue[0] ? String(fullValue[0]).trim() : '';
    } else if (typeof fullValue === 'string') {
      savedValue = fullValue.trim();
    } else {
      savedValue = '';
    }
  }

  const currentValue = editingValue || savedValue;

  // Check if current value exists in allowed values (case-insensitive, trim whitespace)
  const hasCurrentValue = !currentValue ||
    allowedValues.some(av => av.trim().toLowerCase() === currentValue.toLowerCase());
  
  // Map allowed values to option objects
  const options = allowedValues.map((value, index) => {
    const fullDescription = descriptions[index] || '';
    const maxLength = 30; 
    const truncatedDesc = fullDescription.length > maxLength 
      ? fullDescription.substring(0, maxLength) + '...'
      : fullDescription;
    
    const displayText = fullDescription 
      ? `${value} (${truncatedDesc})`
      : value;

    return {
      value,
      description: truncatedDesc,
      displayText,
      fullDescription
    };
  });
  
  // If current value doesn't match any allowed value, add it as first option
  if (!hasCurrentValue && currentValue) {
    options.unshift({
      value: currentValue,
      description: 'custom',
      displayText: `${currentValue} (custom value)`,
      fullDescription: 'Custom value - not in standard dropdown list'
    });
  }
  
  return options;
}
private _descriptionLoggedFor = new Set<number>();

  loadFieldSchemaFromSwaggerAPI(documentCode: string): void {
  this.truthManagementService.getFieldSchemas(documentCode).subscribe({
    next: (response: FieldSchemaResponse) => {
      if (response && response.fields && response.fields.length > 0) {
        // Store raw field_schema fields
        this.fieldSchemaMap.set(documentCode, response.fields);
        
        // Convert field_schema fields to attributes with idx expansion
        this.attributes = this.expandFieldSchemaToAttributes(response.fields);
        // Debug: Log first 3 attributes to verify attributeKey mapping
        if (this.attributes.length > 0) {
          this.attributes.slice(0, 3).forEach(attr => {
          });
        }
        
        // Calculate file statuses
        if (this.evaluationFiles.length > 0) {
          this.loadFileStatusesFromSummary();
        }
        setTimeout(() => this.cdr.detectChanges(), 0);
      }
    },
    error: (err) => {
      // Fall back to loading from local backend if available
      this.loadAttributesForDocument(this.selectedDocumentId!);
    }
  });
}

  loadDocumentById(documentId: number): void {
    // Get the document details
    this.truthManagementService.getDocument(documentId).subscribe({
      next: (document: any) => {
        // Handle both camelCase and PascalCase from API
        this.selectedDocument = {
          id: document.id || document.Id,
          documentNumber: document.documentNumber || document.DocumentNumber,
          documentName: document.documentName || document.DocumentName,
          description: document.description || document.Description,
          loanId: document.loanId || document.LoanId,
          createdDate: document.createdDate || document.CreatedDate
        } as Document;
        
        // Load files for this document
        this.loadFilesForDocument(documentId);
        
        // Load attributes for this document
        this.loadAttributesForDocument(documentId);
        
        setTimeout(() => this.cdr.detectChanges(), 0);
      },
      error: (error) => {
        this.selectedDocument = null;
        this.files = [];
        this.attributes = [];
        setTimeout(() => this.cdr.detectChanges(), 0);
      }
    });
  }



  loadTruthValuesPage(documentId: number, fileId: number): void {
    this.isLoadingTruthValues = true;
    setTimeout(() => this.cdr.detectChanges(), 0);
    
    // Load all data in parallel for faster loading
    const loans$ = this.truthManagementService.getLoans();
    const documents$ = this.truthManagementService.getAllDocuments();
    const files$ = this.truthManagementService.getAllFiles(documentId);
    const attributes$ = this.truthManagementService.getAllAttributes(documentId);
    const truthValues$ = this.truthManagementService.getTruthValuesByFileId(fileId);
    
    // Load loans
    loans$.subscribe({
      next: (loans) => {
        this.loans = loans;
        // If no loan is selected, try to find the loan for this document
        if (!this.selectedLoan && loans.length > 0) {
          this.selectedLoan = loans[0];
        }
        this.cdr.detectChanges();
      },
      error: (error) => {
      }
    });
    
    // Load documents
    documents$.subscribe({
      next: (documents) => {
        this.documents = documents;
        this.selectedDocument = documents.find(d => d.id === documentId) || null;
        
        // Set the selected loan based on the document
        if (this.selectedDocument && !this.selectedLoan) {
          const docLoan = this.loans.find(l => l.id === this.selectedDocument?.loanId);
          if (docLoan) {
            this.selectedLoan = docLoan;
          }
        }
        
        setTimeout(() => this.cdr.detectChanges(), 0);
      },
      error: (error) => {
      }
    });
    
    // Load files
    files$.subscribe({
      next: (files) => {
        this.files = files;
        this.selectedFile = files.find(f => f.id === fileId) || null;
        setTimeout(() => this.cdr.detectChanges(), 0);
      },
      error: (error) => {
      }
    });
    
    // Load attributes
    attributes$.subscribe({
      next: (attributes) => {
        this.attributes = attributes;
        setTimeout(() => this.cdr.detectChanges(), 0);
      },
      error: (error) => {
      }
    });
    
    // Load truth values
    truthValues$.subscribe({
      next: (response) => {
        this.truthValues = response.attributes;
        this.isLoadingTruthValues = false;
        setTimeout(() => this.cdr.detectChanges(), 0);
      },
      error: (error) => {
        this.truthValues = [];
        this.isLoadingTruthValues = false;
        setTimeout(() => this.cdr.detectChanges(), 0);
      }
    });
  }

  backToManagement(): void {
    this.showTruthValuesPage = false;
    
    // Navigate back with document context preserved
    if (this.selectedDocument) {
      this.router.navigate(['/truth-management'], { 
        queryParams: { documentId: this.selectedDocument.id } 
      });
    } else {
      this.router.navigate(['/truth-management']);
    }
  }

  onDocumentSelect(document: Document): void {
    this.selectedDocument = document;
    this.selectedFile = null;
    this.invalidAttributes = new Set(); 
    this.truthValues = [];
    this.resetPdfViewerState(true);
    this.truthManagementService.setSelectedDocument(document);
    this.loadFilesForDocument(document.id);
    this.loadAttributesForDocument(document.id);
    this.setView('files');
    this.cdr.detectChanges();
  }

  loadFileCountForDocument(documentId: number): void {
    this.truthManagementService.getAllFiles(documentId).subscribe({
      next: (files) => {
        this.documentFileCounts.set(documentId, files.length);
        setTimeout(() => this.cdr.detectChanges(), 0);
      },
      error: () => {
        this.documentFileCounts.set(documentId, 0);
      }
    });
  }

  loadAttributeCountForDocument(documentId: number): void {
    this.truthManagementService.getAllAttributes(documentId).subscribe({
      next: (attributes) => {
        this.documentAttributeCounts.set(documentId, attributes.length);
        setTimeout(() => this.cdr.detectChanges(), 0);
      },
      error: () => {
        this.documentAttributeCounts.set(documentId, 0);
      }
    });
  }

  loadAllFiles(): void {
    this.truthManagementService.getAllFiles().subscribe({
      next: (files) => {
        this.files = files;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.files = [];
      }
    });
  }

  loadAttributesFromAPI(): void {
    this.truthManagementService.getAllAttributes().subscribe({
      next: (attributes) => {
        this.attributes = attributes;
      },
      error: (error) => {
        this.attributes = [];
      }
    });
  }

  loadAttributesForDocument(documentId: number): void {
    // Load attributes using field_schema API
    if (this.selectedDocument) {
      const documentNumber = this.selectedDocument.documentNumber;
      this.truthManagementService.getFieldSchemas(documentNumber).subscribe({
        next: (fieldSchemaResponse: FieldSchemaResponse) => {
          if (fieldSchemaResponse && fieldSchemaResponse.fields && fieldSchemaResponse.fields.length > 0) {
            // Store raw field_schema fields
            this.fieldSchemaMap.set(documentNumber, fieldSchemaResponse.fields);
            
            // Convert field_schema fields to attributes with idx expansion
            this.attributes = this.expandFieldSchemaToAttributes(fieldSchemaResponse.fields);
            
            // Calculate file statuses from attribute_summary
            if (this.evaluationFiles.length > 0) {
              this.loadFileStatusesFromSummary();
            }
            
            this.cdr.detectChanges();
          } else {
            this.attributes = [];
            this.cdr.detectChanges();
          }
        },
        error: (error) => {
          this.attributes = [];
          this.cdr.detectChanges();
        }
      });
    } else {
      this.attributes = [];
      this.cdr.detectChanges();
    }
  }

  loadLoans(): void {
    this.truthManagementService.getLoans().subscribe(loans => {
      this.loans = loans;
    });
  }

  onLoanSelect(loan: Loan): void {
    this.selectedLoan = loan;
    this.selectedDocument = null;
    this.selectedFile = null;
    this.files = [];
    this.truthValues = [];
    this.resetPdfViewerState(true);
    this.truthManagementService.setSelectedLoan(loan);
    this.loadDocumentsForLoan(loan.id);
    this.loadFilesForLoan(loan.id);
  }

  loadDocumentsForLoan(loanId: number): void {
    this.truthManagementService.getDocumentsByLoanId(loanId).subscribe({
      next: (documents) => {
        this.documents = documents;
        // Load counts for each document
        documents.forEach(doc => {
          this.loadCountsForDocument(doc.id);
        });
      },
      error: (error) => {
      }
    });
  }

  loadCountsForDocument(documentId: number): void {
    // Load file count
    this.truthManagementService.getAllFiles(documentId).subscribe({
      next: (files: File[]) => {
        this.documentFileCounts.set(documentId, files.length);
        this.cdr.markForCheck();
      },
      error: () => {
        this.documentFileCounts.set(documentId, 0);
        this.cdr.markForCheck();
      }
    });
    
    // Load attribute count using filtered API call
    this.truthManagementService.getAllAttributes(documentId).subscribe({
      next: (attributes: Attribute[]) => {
        this.documentAttributeCounts.set(documentId, attributes.length);
        this.cdr.markForCheck();
      },
      error: () => {
        this.documentAttributeCounts.set(documentId, 0);
        this.cdr.markForCheck();
      }
    });
  }

  loadFilesForLoan(loanId: number): void {
    // Get all documents for this loan, then get all files for those documents
    this.truthManagementService.getDocumentsByLoanId(loanId).subscribe(documents => {
      const allFiles: File[] = [];
      documents.forEach(doc => {
        this.truthManagementService.getFilesByDocumentId(doc.id).subscribe(files => {
          allFiles.push(...files);
          this.files = allFiles;
        });
      });
    });
  }

  onFileSelect(file: File): void {
    // Navigate to truth values page
    if (this.selectedDocument) {
      this.router.navigate(['/truth-management', this.selectedDocument.id, file.id]);
    }
  }

  /**
   * Handle file selection with unsaved changes protection
   * Shows save confirmation modal if there are unsaved changes
   */
  selectFileOnly(file: File): void {
    // Prevent switching to the same file
    if (this.selectedFile?.id === file.id) {
      return;
    }

    // CHANGED: Removed auto-fill blocking - user can now switch files freely during auto-fill
    // Notification will appear below header when auto-fill completes

    // Check if there are unsaved changes before switching files
    if (this.modifiedAttributeIds.size > 0) {
      // Show "Do you want to save changes?" modal
      this.modalService.showSaveConfirmation(
        // User clicked "Yes" - save changes then switch file
        () => {
          this.executeSave(false)
            .then((saveSuccess) => {
              if (saveSuccess) {
                this.notificationService.showSaveSuccess(
                  this.selectedDocument?.documentNumber || '',
                  this.selectedFile?.fileName || ''
                );
               this.performFileSwitch(file);
              } else {
                this.showSaveFailedFileSwitch(file);
              }
            })
            .catch((error) => {
              this.showSaveFailedFileSwitch(file);
            });
        },
        // User clicked "No" - discard changes and switch file
        () => {
          // Clear pending auto-fill results if this was an auto-filled file
          if (this.selectedFile) {
            this.clearPendingExtractionResults(this.selectedFile.id);
            this.clearAutoFillCompletedForFile(this.selectedFile.id);
          }
          this.notificationService.showSaveDiscarded(
            this.selectedDocument?.documentNumber || '',
            this.selectedFile?.fileName || ''
          );
          this.performFileSwitch(file);
        }
      );
      return;
    }
    
    // No unsaved changes - switch directly
    this.performFileSwitch(file);
  }

  /**
   * Show options when save fails during file switch
   * Prevents user from being trapped - allows force switch with data loss warning
   */
  private showSaveFailedFileSwitch(targetFile: File): void {
    this.modalService.show({
      type: ModalType.CONFIRMATION,
      size: ModalSize.SMALL,
      title: 'Save Failed',
      message: 'Unable to save your changes. The server returned an error. Do you want to stay on this file to try again, or switch anyway and lose your changes?',
      confirmText: 'Switch Anyway',
      cancelText: 'Stay on Current File',
      onConfirm: () => {
        if (this.selectedFile) {
          this.clearAutoFillCompletedForFile(this.selectedFile.id);
        }
       this.performFileSwitch(targetFile);
      },
      onCancel: () => {
        // Do nothing - stay on current file
      }
    });
  }

  /**
   * Actually perform the file switch (internal method)
   * This is the extracted core logic from the original selectFileOnly
   */
  private performFileSwitch(file: File): void {
    // CHANGED: Don't abort extraction - let it complete in background
    // When extraction completes, it will store results in sessionStorage
    // and show notification even if user switched files
    if (this.isExtracting) {
      // Keep isExtracting flag as-is - it will be reset when extraction completes
      // Keep extractionRequestId - polling will continue
      // DON'T clear localStorage - we need it for recovery
    }
    
    // Select file and load detailed info from Swagger API
    this.selectedFile = file;
    
    // Check if the file we're switching TO is currently being extracted
    // This handles the case where user navigates away and back while extraction is running
    const isAlreadyExtractingThisFile = this.extractingFiles.has(file.id) || 
                                        this.extractingFiles.has(file.id.toString()) ||
                                        this.extractingFiles.has(Number(file.id));
    
    if (!isAlreadyExtractingThisFile) {
      const savedExtractionsStr = localStorage.getItem('truthManagement_extractions');
      if (savedExtractionsStr) {
        try {
          const savedExtractions = JSON.parse(savedExtractionsStr);
          
          // Check if THIS file has an active extraction
          for (const [fileIdStr, extractionState] of Object.entries(savedExtractions as Record<string, any>)) {
            const timeSinceExtraction = Date.now() - extractionState.timestamp;
            
            // Compare fileIds as both string and number to handle type mismatches
            const stateFileId = extractionState.fileId;
            const currentFileId = file.id;
            const isMatchingFile = stateFileId == currentFileId || // Loose equality handles string/number
                                    stateFileId === currentFileId || 
                                    stateFileId.toString() === currentFileId.toString();
            
            // If this file is currently being extracted, restore the UI state
            if (isMatchingFile && 
                extractionState.requestId && 
                timeSinceExtraction < this.EXTRACTION_TIMEOUT) {
              this.extractingFiles.add(file.id);
              this.extractingFiles.add(file.id.toString());
              this.extractionRequestIds.set(file.id, extractionState.requestId);
              this.isExtracting = true;
              this.extractingFileId = file.id;
              this.lastExtractionTimestamp = extractionState.timestamp || Date.now();
              // Service is already polling in background, no need to resume
              this.cdr.detectChanges(); // Force UI update to show spinner
              break; // Found the match, no need to continue
            }
          }
        } catch (e) {
        }
      }
    } else {
    }
    
    this.resetPdfViewerState(false);
    this.isLoadingPdf = true;
    
    // DON'T check pending results here - need to wait for attributes to load first
    // Will be called after attributes are loaded in loadAttributesForDocument
    
    // Update URL to persist selected file across page refreshes
    if (this.selectedDocument) {
      const currentParams = this.route.snapshot.queryParams;
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          ...currentParams,
          documentCode: this.selectedDocument.documentNumber,
          fileId: file.id
        },
        queryParamsHandling: 'merge',
        replaceUrl: true // Don't add to browser history
      });
    }
    
    // Cancel any active scroll restoration RAF loop and remove scroll lock handler to prevent scroll interference on file change
    if (this.rotateScrollRestoreFrame !== null) {
      cancelAnimationFrame(this.rotateScrollRestoreFrame);
      this.rotateScrollRestoreFrame = null;
    }
    
    if (this.rotateScrollHandler) {
      const viewer = this.getPdfViewerScrollContainer();
      if (viewer) {
        viewer.removeEventListener('scroll', this.rotateScrollHandler, true);
      }
      this.rotateScrollHandler = null;
    }
    
    // Auto-collapse file column when file is selected
    this.isFileColumnCollapsed = true;
    
    // Clear edit state when file changes
    this.editingAttributeId = null;
    this.editingTruthValueText = '';
    this.attributeSearchTerm = '';
    this.currentFileTruthValues.clear();
    this.modifiedValues.clear();
    this.modifiedAttributeIds.clear();
    this.autoPopulatedAttributeIds.clear();
    this.autoPopulatedOriginalValues.clear();
    this.overriddenAutoPopulatedAttributeIds.clear();
    this.notApplicableAttributes.clear();
    this.backendNAAttributes.clear();
    
    // Reset attribute_summary for new file (will be populated by loadTruthValuesForFile)
    this.selectedFileAttributeSummary = null;
    
    // Clear bounding box highlight state when file changes (User Story 7126)
    this.fieldSourceDataMap.clear();
    this.originalApiAttributeNames.clear();
    this.highlightedAttributeId = null;
    this.activeBoundingBoxes = [];
    this.resetSnipStateForFileSwitch();
    
    // Clear table data for all table field attributes to prevent data leaking between files
    // Since attributes are per-document-model and reused across files, old tableData can persist
    this.attributes.forEach(attr => {
      if (attr.isTableField) {
        attr.tableData = {};
      }
    });
    
    // Attributes are per document model, not per file — only load them on the first file click
    // (when this.attributes is empty). On subsequent switches, attributes are already in memory;
    // skip loadAttributesForDocument() to avoid redundant schema/keys calls.
    // loadTruthValuesForFile() will update selectedFileAttributeSummary with backend counts.
    if (this.selectedDocument) {
      if (this.attributes.length === 0) {
        // First file click for this model — attributes not loaded yet
        this.loadAttributesForDocument(this.selectedDocument.id);
        const checkAttributesLoaded = setInterval(() => {
          if (this.attributes && this.attributes.length > 0) {
            clearInterval(checkAttributesLoaded);
            this.loadTruthValuesForFile(file.id);
          }
        }, 50);
        // Timeout fallback after 5 seconds
        setTimeout(() => {
          clearInterval(checkAttributesLoaded);
          if (!this.attributes || this.attributes.length === 0) {
            this.loadTruthValuesForFile(file.id);
          }
        }, 5000);
      } else {
        // Attributes already loaded — fetch only this file's truth values
        this.loadTruthValuesForFile(file.id);
      }
    }

    // Check if we have a cached blob URL for this uploaded file (check both types)
    const cachedBlobUrl = this.uploadedFileBlobUrls.get(file.id) || this.uploadedFileBlobUrls.get(file.id.toString());
    if (file.id && cachedBlobUrl) {
    this.pdfReady = false;
    this.pdfSrc = cachedBlobUrl;
    // Keep isLoadingPdf = true until the first layout lands; loader stays
    // visible while the lazy viewer prepares the cached blob.
    this.zoom = 1.0;
    this.startPdfDocumentLoad();
    this.cdr.detectChanges();
    return;
    }

    // eval/files (list) and eval/files/{id} (detail) APIs return only metadata fields
    // (uuid, file_name, document_model, created_at) — no blob URL of any kind.
    // The detail call always fell through to tryStreamPdf, so call it directly.
    if (file.id) {
      this.tryStreamPdf(file.id);
    } else {
      this.pdfSrc = '';
      this.isLoadingPdf = false;
      this.cdr.detectChanges();
    }
  }

  
  // Toggle file column collapse/expand
  toggleFileColumn(): void {
    this.isFileColumnCollapsed = !this.isFileColumnCollapsed;
    
    // Trigger multiple resize events during the transition for smoother PDF scaling
    const resizeEvent = new Event('resize');
    const transitionDuration = 400; // matches CSS transition duration
    const intervals = 8; // number of resize events during transition
    const intervalTime = transitionDuration / intervals;
    
    for (let i = 1; i <= intervals; i++) {
      setTimeout(() => {
        window.dispatchEvent(resizeEvent);
      }, i * intervalTime);
    }
    
    // Final resize after transition completes to ensure accurate rendering
    setTimeout(() => {
      window.dispatchEvent(resizeEvent);
    }, transitionDuration + 50);
  }

  /**
   * Load the PDF document via pdf.js for the lazy viewer, streaming it the same
   * way the source/indexing page does: byte-range requests via BlobRangeTransport
   * (first pages paint before the full download finishes) plus a background
   * warm-cache full download into Cache Storage, keyed by the eval file id.
   * When serverPdfLength is available (from the files API), the range probe is
   * skipped — saving one round-trip before the PDF starts loading.
   * Success routes into the existing onPdfLoadComplete; failure into the
   * existing onPdfLoadError (SAS retry path).
   */
  private startPdfDocumentLoad(): void {
    const src = this.pdfSrc;
    if (!src) { return; }
    const seq = ++this.pdfDocLoadSeq;
    const previousDoc = this.pdfDocument;
    this.pdfDocument = null;
    this.pageNumbers = [];
    this.pageWidthsPx = [];
    this.pageHeightsPx = [];
    this.pageScales = [];
    this.warmCacheAbortController?.abort();
    this.pdfLoadProgress = 0;
    if (previousDoc) {
      try { previousDoc.destroy(); } catch { /* already destroyed */ }
    }
    const isStale = () => seq !== this.pdfDocLoadSeq || src !== this.pdfSrc;
    this.lastLoadUsedRangeTransport = false;

    const loadStreaming = async (): Promise<any> => {
      // blob: URLs (fresh uploads) are already local — plain load, nothing to stream.
      if (src.startsWith('blob:')) {
        return pdfjsLib.getDocument(src).promise;
      }
      let length = this.serverPdfLength;
      if (length) {
        // Server-provided length — skip the range probe entirely.
        if (isStale()) { throw { __stale: true }; }
      } else {
        const probe = await this.probePdfRange(src);
        if (isStale()) { throw { __stale: true }; }
        length = probe.length ?? (probe.rangeOk ? await this.fetchPdfLength(src) : null);
        if (isStale()) { throw { __stale: true }; }
        if (!length || !probe.rangeOk) {
          return pdfjsLib.getDocument(src).promise;
        }
      }
      const cacheKey = this.pdfLoadingFileId ? `gt:${this.pdfLoadingFileId}` : '';
      const cached = cacheKey ? await sourcePdfCache.get(cacheKey, length) : null;
      if (isStale()) { throw { __stale: true }; }
      const transport = new BlobRangeTransport(length, src, cached?.blob ?? null);
      this.lastLoadUsedRangeTransport = true;
      const doc = await (pdfjsLib as any).getDocument({
        range: transport,
        rangeChunkSize: 262144,
        disableStream: true,
        disableAutoFetch: true,
      }).promise;
      if (cached) {
        this.setPdfProgress(length, length);
        if (!this.serverPageSizes) {
          this.refineAllPageDimensionsInBackground();
        }
      } else if (cacheKey) {
        void this.warmPdfCache(src, length, cacheKey, transport);
      }
      return doc;
    };

    loadStreaming().then(
      (pdf: any) => {
        // Bail if a newer source superseded this load (fast file switching).
        if (isStale()) {
          try { pdf.destroy(); } catch { /* noop */ }
          return;
        }
        this.ngZone.run(() => this.onPdfLoadComplete(pdf));
      },
      (error: any) => {
        if (isStale() || error?.__stale) { return; }
        this.ngZone.run(() => this.onPdfLoadError(error));
      }
    );
  }

  /**
   * Probe the PDF's total byte length so the range transport can be
   * constructed. Uses a PLAIN GET (no Range header → a CORS "simple request",
   * no preflight) and reads Content-Length, which is a CORS-safelisted
   * response header readable without any Access-Control-Expose-Headers
   * configuration. The body is cancelled right after the headers arrive.
   * Returns null when the length cannot be determined (caller falls back to
   * a plain whole-URL load).
   */
  private async fetchPdfLength(url: string): Promise<number | null> {
    try {
      // HEAD: no body transferred (a plain GET starts streaming the whole
      // blob and steals bandwidth from page 1's ranges), no CORS preflight,
      // and Content-Length is a safelisted response header.
      const resp = await fetch(url, { method: 'HEAD' });
      const cl = resp.headers.get('Content-Length');
      if (!resp.ok) { return null; }
      const n = cl ? parseInt(cl, 10) : NaN;
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch { /* fall through */ }
    return null;
  }

  /**
   * One zero-byte test range request before committing to the streaming
   * transport. A 206 proves Range works and, when Content-Range is CORS-
   * exposed, yields the blob's exact total size (authoritative — a wrong
   * length sends pdf.js into a whole-file recovery scan). A 200 means the
   * server ignores Range (BlobRangeTransport slices such responses) and the
   * length comes from Content-Length. Any failure → plain-load fallback.
   */
  private async probePdfRange(url: string): Promise<{ length: number | null; rangeOk: boolean }> {
    try {
      const resp = await fetch(url, { headers: { Range: 'bytes=0-0' } });
      void resp.body?.cancel();
      if (resp.status === 206) {
        const m = /\/(\d+)\s*$/.exec(resp.headers.get('Content-Range') ?? '');
        const total = m ? parseInt(m[1], 10) : NaN;
        return { length: Number.isFinite(total) && total > 0 ? total : null, rangeOk: true };
      }
      if (resp.status === 200) {
        const cl = resp.headers.get('Content-Length');
        const n = cl ? parseInt(cl, 10) : NaN;
        return { length: Number.isFinite(n) && n > 0 ? n : null, rangeOk: true };
      }
    } catch { /* fall through */ }
    return { length: null, rangeOk: false };
  }

  /**
   * Background full download of the PDF into Cache Storage while the viewer
   * already paints pages from byte ranges. Once stored, every further range
   * is served from disk (transport.useLocalBlob). Port of the source page's
   * warmPdfCache; the original body drives the progress line.
   */
  private async warmPdfCache(
    url: string,
    total: number,
    cacheKey: string,
    transport: BlobRangeTransport,
  ): Promise<void> {
    this.warmCacheAbortController?.abort();
    const controller = new AbortController();
    this.warmCacheAbortController = controller;
    let stored = false;
    try {
      const response = await fetch(url, {
        // priority: 'low' is not yet in lib.dom types
        ...({ priority: 'low' } as any),
        signal: controller.signal,
      });
      if (!response.ok || !response.body) { return; }

      // Persist while we stream: the clone's body goes straight to Cache
      // Storage (disk) while the original drives the progress line — nothing
      // is buffered in JS. A partial body is never stored (cache.put only
      // resolves on a complete stream) and abort cancels both.
      const expiresAt = sourcePdfCache.expiryFromSignedUrl(url);
      const cloneBody = sourcePdfCache.isAvailable() ? response.clone().body : null;
      const storing: Promise<boolean> = cloneBody
        ? sourcePdfCache.put(cacheKey, cloneBody, total, expiresAt)
        : Promise.resolve(false);

      const reader = response.body.getReader();
      let fetched = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) { break; }
        fetched += value.length;
        this.setPdfProgress(fetched, total);
      }
      stored = await storing;
    } catch { /* aborted or network failure — ranges keep working */ }
    this.setPdfProgress(total, total);

    if (stored && !controller.signal.aborted) {
      const cachedNow = await sourcePdfCache.get(cacheKey, total);
      if (cachedNow) {
        transport.useLocalBlob(cachedNow.blob);
        if (!this.serverPageSizes) {
          this.refineAllPageDimensionsInBackground();
        }
      }
    }
  }

  /**
   * Measure every page's base dimensions and refine the layout — deferred for
   * range-transport loads until the document is local (otherwise getPage() for
   * every page fires a startup storm of range requests that delays the first
   * visible canvases; the source page avoids this via server-provided sizes).
   */
  private refineAllPageDimensionsInBackground(): void {
    const seq = this.pdfDocLoadSeq;
    this.preCacheAllPageDimensions().then(() => {
      if (seq !== this.pdfDocLoadSeq) { return; }
      this.computePdfPageLayout();
      this.cdr.detectChanges();
    }).catch(() => { /* per-page size feedback still corrects visible pages */ });
  }

  private setPdfProgress(fetched: number, total: number): void {
    if (!total) { this.pdfLoadProgress = 0; return; }
    this.pdfLoadProgress = Math.min(100, Math.round((fetched / total) * 100));
    this.cdr.markForCheck();
  }

  private tryStreamPdf(fileId: string | number): void {
    const fileIdStr = fileId.toString();
    this.pdfLoadingFileId = fileIdStr;
    this.pdfSasRetryCount = 0;
    this.pdfReady = false;

    const evalFile = this.evaluationFiles.find((ef: any) =>
      (ef.uuid || ef.id?.toString()) === fileIdStr
    );
    const sasUrl = evalFile?.blob_sas_url || null;

    this.serverPdfLength = evalFile?.length ?? null;
    this.serverPageSizes = evalFile?.pageSizes?.length
      ? evalFile.pageSizes.map(([w, h]) => ({ width: w, height: h }))
      : null;

    if (sasUrl) {
      this.pdfSrc = sasUrl;
      this.zoom = 1.0;
      this.startPdfDocumentLoad();
      this.cdr.detectChanges();
    } else {
      // SAS URL not available — show error instead of re-fetching Files API
      // (Files API was already called on initial page load)
      this.pdfSrc = '';
      this.isLoadingPdf = false;
      this.pdfReady = false;
      this.cdr.detectChanges();
      this.notificationService.showError('PDF not available for this file');
    }
  }

  private getEvalFileSasUrl(fileIdStr: string): string | null {
    const evalFile = this.evaluationFiles.find((ef: any) =>
      (ef.uuid || ef.id?.toString()) === fileIdStr
    );
    return evalFile?.blob_sas_url || null;
  }

  private refreshPdfFromFilesApi(fileIdStr: string): void {
    const documentModel = this.selectedDocument?.documentNumber;
    this.truthManagementService.getEvaluationFiles(documentModel).subscribe({
      next: (files) => {
        // Update evaluationFiles cache with fresh data (including updated SAS URLs)
        this.evaluationFiles = files;
        const evalFile = files.find((f: any) =>
          (f.uuid || f.id?.toString()) === fileIdStr
        );
        const sasUrl = evalFile?.blob_sas_url;
        if (sasUrl) {
          // Keep loader visible until the first layout lands.
          this.pdfSrc = sasUrl;
          this.zoom = 1.0;
          this.startPdfDocumentLoad();
          this.cdr.detectChanges();
        } else {
          this.pdfSrc = '';
          this.isLoadingPdf = false;
          this.pdfReady = false;
          this.cdr.detectChanges();
          this.notificationService.showError('Failed to load PDF: file not available');
        }
      },
      error: () => {
        this.pdfSrc = '';
        this.isLoadingPdf = false;
        this.pdfReady = false;
        this.cdr.detectChanges();
        this.notificationService.showError('Failed to load PDF');
      }
    });
  }

  onPdfLoadError(error: any): void {
    if (this.pdfSasRetryCount < this.PDF_MAX_RETRIES) {
      this.pdfSasRetryCount++;
      // This is the ONLY place where Files API should be called after initial page load
      // (to refresh expired SAS URLs)
      this.refreshPdfFromFilesApi(this.pdfLoadingFileId);
    } else {
      this.pdfSrc = '';
      this.isLoadingPdf = false;
      this.pdfReady = false;
      this.cdr.detectChanges();
      this.notificationService.showError('Failed to load PDF after retries');
    }
  }

  /**
   * Extract base key from external_key field (for storing in attributeKey)
   * Handles: dict objects, template strings, and simple strings
   * Examples:
   *   {"0": "Borrower_CAIVRS", "default": "Borrower{idx}_CAIVRS"} → "Borrower{idx}_CAIVRS"
   *   "Borrower{idx}_CAIVRS" → "Borrower{idx}_CAIVRS"
   *   "Receipt_Signature{idx}" → "Receipt_Signature{idx}"
   *   "1a_City" → "1a_City"
   * 
   * IMPORTANT: Prefer "default" (template) over "0" (base) because template expansion
   * covers ALL cases including the base. Template "Borrower{idx}_CAIVRS" expands to:
   * ["Borrower1_CAIVRS", "Borrower2_CAIVRS", ..., "Borrower_CAIVRS"]
   */
  private extractBaseKey(externalKey: any): string {
    if (!externalKey) return '';
    
    // If it's an object/dict, prefer "default" (template pattern) over "0" (base)
    // The template expansion will handle ALL indexed variants including the base case
    if (typeof externalKey === 'object' && !Array.isArray(externalKey)) {
      return externalKey['default'] || externalKey['0'] || '';
    }
    
    // If it's a string, return as-is (may contain {idx} template)
    return String(externalKey);
  }

  /**
   * Expand {idx} template pattern into possible concrete keys
   * Examples:
   *   "Borrower{idx}_CAIVRS" → ["Borrower_CAIVRS", "Borrower1_CAIVRS", "Borrower2_CAIVRS", ...]
   *   "Receipt_Signature{idx}" → ["Receipt_Signature", "Receipt_Signature1", "Receipt_Signature2", ...]
   *   "1a_City" → ["1a_City"] (no template, return as-is)
   */
  private expandIdxTemplate(template: string, maxIdx: number = 10): string[] {
    if (!template || !template.includes('{idx}')) {
      return [template];
    }
    
    const expanded: string[] = [];
    // Generate patterns for idx 0 to maxIdx (covers most cases)
    for (let i = 0; i <= maxIdx; i++) {
      // Format with idx (e.g., Borrower{idx}_CAIVRS → Borrower1_CAIVRS)
      expanded.push(template.replace(/{idx}/g, String(i + 1)));
    }
    // Also add pattern with idx removed (for idx=0 case without number)
    // e.g., Borrower{idx}_CAIVRS → Borrower_CAIVRS
    expanded.push(template.replace(/{idx}/g, ''));
    
    return expanded;
  }

  /**
   * Convert field_schema entries to attributes
   * For array fields with idx templates: Creates ONE attribute that will collect all idx variants
   * 
   * Example:
   *   Field: "Borrower CAIVRS", external_key: {"0": "Borrower_CAIVRS", "default": "Borrower{idx}_CAIVRS"}
   *   Creates: ONE attribute with:
   *     - attributeLabel: "Borrower CAIVRS"
   *     - attributeKey: "Borrower{idx}_CAIVRS" (template pattern for matching)
   *     - is_array: true
   *   
   *   Matching logic will recognize: Borrower_CAIVRS, Borrower2_CAIVRS, Borrower3_CAIVRS, ...
   *   All values collected into the SAME attribute, displayed as multiple boxes
   *   originalApiAttributeNames tracks which box came from which backend attribute
   */
  private expandFieldSchemaToAttributes(fields: FieldSchemaField[]): Attribute[] {
    const attributes: Attribute[] = [];
    let attributeIdCounter = 1;
    
    fields.forEach((field, fieldIndex) => {
      const isArrayField = field.field_datatype === 'array' || field.field_schema?.type === 'array';
      const isTableField = field.field_schema?.ld_feature === 'table' && field.field_schema?.ld_table_config;
      const externalKey = field.external_key;
      
      // Check if external_key is a dict object with indexed keys
      const isDict = externalKey && typeof externalKey === 'object' && !Array.isArray(externalKey);
      
      // For array fields with idx templates, create ONE attribute with template pattern
      // The matching logic will expand this template to match all variants
      const hasIdxTemplate = isDict || (typeof externalKey === 'string' && externalKey.includes('{idx}'));
      
      if (isTableField) {
        // Table field - extract columns from ld_table_config
        const baseKey = this.extractBaseKey(externalKey) || field.field_name;
        
        // Get column names from ld_table_config.columns
        const columnNames = field.field_schema?.ld_table_config?.columns?.map(col => col.name) || [];

        // Also include computed_schema.table_splits.fields - merge both sources, unique, order preserved
        const splitFields = field.computed_schema?.table_splits?.fields || [];
        const columnsToUse = Array.from(new Set([...columnNames, ...splitFields]));

        attributes.push({
          id: field.uuid || attributeIdCounter++,
          attributeLabel: field.field_name,
          attributeKey: baseKey,
          description: field.field_schema?.description || '',
          documentId: this.selectedDocument?.id || 0,
          prompt: field.field_schema?.items?.description || field.field_schema?.description || '',
          is_array: false,
          allowed_values: field.allowed_values || [],
          feature: field.feature || field.field_schema?.ld_feature,
          field_datatype: field.field_datatype,
          items: field.field_schema?.items || null,
          // Table field specific properties
          isTableField: true,
          tableColumns: columnsToUse.map((name: string) => ({ name })),
          tableData: {}  // Will be populated from API data
        } as Attribute);
      } else if (isArrayField && hasIdxTemplate) {
        // Extract template pattern - prefer "default" for dict, use as-is for string
        let templateKey: string;
        if (isDict) {
          templateKey = externalKey['default'] || externalKey['0'] || '';
        } else {
          templateKey = externalKey as string;
        }
        // Create ONE attribute entry - matching will expand template to find all variants
        attributes.push({
          id: field.uuid || attributeIdCounter++,
          attributeLabel: field.field_name,
          attributeKey: templateKey,  // Store template for expansion during matching
          description: field.field_schema?.description || '',
          documentId: this.selectedDocument?.id || 0,
          prompt: field.field_schema?.items?.description || field.field_schema?.description || '',
          is_array: true,  // Display as array (multiple value boxes)
          allowed_values: field.allowed_values || [],
          feature: field.feature || field.field_schema?.ld_feature,
          field_datatype: field.field_datatype,
          items: field.field_schema?.items || null
        } as Attribute);
      } else {
        // Non-array or no template - create single entry
        const baseKey = this.extractBaseKey(externalKey) || field.field_name;
        
        attributes.push({
          id: field.uuid || attributeIdCounter++,
          attributeLabel: field.field_name,
          attributeKey: baseKey,
          description: field.field_schema?.description || '',
          documentId: this.selectedDocument?.id || 0,
          prompt: field.field_schema?.items?.description || field.field_schema?.description || '',
          is_array: isArrayField && !hasIdxTemplate,  // Only non-idx arrays
          allowed_values: field.allowed_values || [],
          feature: field.feature || field.field_schema?.ld_feature,
          field_datatype: field.field_datatype,
          items: field.field_schema?.items || null
        } as Attribute);
      }
    });
    
    return attributes;
  }

  // Normalize attribute name for matching (handle different naming conventions)
  private normalizeAttributeName(name: string): string {
    if (!name) return '';
    return String(name)
      .toLowerCase()
      .trim()
      .replace(/[_\s-]+/g, '')  // Remove underscores, spaces, hyphens
      .replace(/[^a-z0-9]/g, ''); // Remove special characters
  }

  /** Deduplicate truth values: prefer space-format over underscore-format for same field. */
  private deduplicateByAttributeName(truthValues: TruthValue[]): TruthValue[] {
    const grouped = new Map<string, TruthValue[]>();

    for (const tv of truthValues) {
      const name = (tv as any).attributeName || '';
      const normalizedKey = this.normalizeAttributeName(name);
      if (!normalizedKey) continue;
      const group = grouped.get(normalizedKey) || [];
      group.push(tv);
      grouped.set(normalizedKey, group);
    }

    const result: TruthValue[] = [];
    for (const [, group] of grouped) {
      if (group.length === 1) {
        result.push(group[0]);
      } else {
        // Prefer entry whose name contains a space (schema format)
        const spaceFormatEntry = group.find(tv => ((tv as any).attributeName || '').includes(' '));
        result.push(spaceFormatEntry || group[0]);
      }
    }
    return result;
  }

  /**
   * Get field type for an attribute
   * Simple inline method - determines field type from attribute metadata
   * 
   * @param attributeId - Attribute ID
   * @returns Field type (text, date, select)
   */
  getFieldType(attributeId: number): 'text' | 'date' | 'select' {
    const attr = this.attributes.find(a => a.id === attributeId);
    if (!attr) return 'text';
    const featureLower = String(attr.feature || '').toLowerCase();
    
    // Check feature type
    if (featureLower.includes('date')) return 'date';
    if (featureLower.includes('signature') || featureLower.includes('enum') || featureLower.includes('checkbox')) return 'select';
    
    // Check for allowed values
    if (attr.allowed_values && attr.allowed_values.length > 0) return 'select';
    
    // Check if label/key contains 'date' - safely convert to string first
    const labelLower = String(attr.attributeLabel || '').toLowerCase();
    const keyLower = String(attr.attributeKey || '').toLowerCase();
    if (labelLower.includes('date') || keyLower.includes('date')) return 'date';
    
    return 'text';
  }

  /**
   * Get allowed values for an attribute
   * 
   * @param attributeId - Attribute ID
   * @returns Array of allowed values
   */
  getFieldAllowedValues(attributeId: number): string[] {
    const attr = this.attributes.find(a => a.id === attributeId);
    if (!attr) return [];

    const featureLower = String(attr.feature || '').toLowerCase();
    if (attr.allowed_values && attr.allowed_values.length > 0) {
      return attr.allowed_values;
    }

    return featureLower.includes('signature') ? ['Signed', 'Unsigned'] : [];
  }

  /**
   * Check if the currently selected file is being extracted
   * Used to disable fields only for files currently extracting, not all files
   * Handles string/number type mismatches
   */
  isCurrentFileExtracting(): boolean {
    if (!this.selectedFile) return false;
    
    const fileId = this.selectedFile.id;
    // Check for exact match, string version, and number version to handle type inconsistencies
    return this.extractingFiles.has(fileId) || 
           this.extractingFiles.has(fileId.toString()) ||
           this.extractingFiles.has(Number(fileId));
  }

  /**
   * Check if a specific file is currently being extracted (for progress indicator)
   * Handles string/number type mismatches
   */
  isFileExtracting(fileId: number | string): boolean {
    return this.extractingFiles.has(fileId) || 
           this.extractingFiles.has(fileId.toString()) ||
           this.extractingFiles.has(Number(fileId));
  }

  isFileAutoFillCompleted(fileId: number | string): boolean {
    const inMemoryCompleted = this.autoFilledFiles.has(fileId) ||
      this.autoFilledFiles.has(fileId.toString()) ||
      this.autoFilledFiles.has(Number(fileId));

    const hasValidPendingResult = this.hasValidPendingExtractionResults(fileId);
    const isSelectedFile = !!this.selectedFile && String(this.selectedFile.id) === String(fileId);

    // For selected file, preserve existing in-memory behavior.
    if (isSelectedFile) {
      return inMemoryCompleted || hasValidPendingResult;
    }

    // For non-selected files, indicator should mirror pending autofill value presence only.
    return hasValidPendingResult;
  }

  private hasValidPendingExtractionResults(fileId: number | string): boolean {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }

    const pendingKey = `truthManagement_pendingResults_${fileId}`;
    const pendingDataStr = sessionStorage.getItem(pendingKey);
    if (!pendingDataStr) {
      return false;
    }

    try {
      const pendingData = JSON.parse(pendingDataStr);
      const timestamp = Number(pendingData?.timestamp);
      if (!Number.isFinite(timestamp) || timestamp <= 0) {
        return false;
      }

      const age = Date.now() - timestamp;
      if (age < 0 || age > 30 * 60 * 1000) {
        return false;
      }

      const extractedAttrs = pendingData?.extractedAttrs;
      if (!extractedAttrs || typeof extractedAttrs !== 'object') {
        return false;
      }

      return this.hasAnyPendingExtractedValue(extractedAttrs as Record<string, any>);
    } catch {
      return false;
    }
  }

  private hasAnyPendingExtractedValue(extractedAttrs: Record<string, any>): boolean {
    for (const attrData of Object.values(extractedAttrs)) {
      if (Array.isArray(attrData)) {
        for (const item of attrData) {
          const candidate = item?.Value ?? item?.value;
          if (toStringValue(candidate).trim() !== '') {
            return true;
          }
        }
        continue;
      }

      if (attrData && typeof attrData === 'object') {
        const candidate = attrData.Value ?? attrData.value;
        if (toStringValue(candidate).trim() !== '') {
          return true;
        }
        continue;
      }

      if (toStringValue(attrData as string | string[] | undefined | null).trim() !== '') {
        return true;
      }
    }

    return false;
  }

  private clearAutoFillCompletedForFile(fileId: number | string): void {
    this.autoFilledFiles.delete(fileId);
    this.autoFilledFiles.delete(fileId.toString());
    this.autoFilledFiles.delete(Number(fileId));
    this.clearPendingExtractionResults(fileId);
  }

  /**
   * Parse table dictionary data into structured TableRow array
   * Dictionary format: {"ColumnName": ["val1", "val2", "val3"], "ColumnName2": ["val_a", "val_b", "val_c"]}
   * Returns: [{ColumnName: "val1", ColumnName2: "val_a"}, {ColumnName: "val2", ColumnName2: "val_b"}, ...]
   * 
   * @param tableData - Dictionary with column names as keys and array values
   * @param columnNames - Array of column names in order
   * @returns Array of TableRow objects
   */
  private parseTableRowsFromDictionary(tableData: { [key: string]: string[] }, columnNames: string[]): TableRow[] {
    if (!tableData || !columnNames || columnNames.length === 0 || Object.keys(tableData).length === 0) {
      return [];
    }

    // Parse all columns to get row values
    const rowCount = this.getTableRowCount(tableData, columnNames);
    if (rowCount === 0) {
      return [];
    }

    const rows: TableRow[] = [];

    // Reconstruct rows by iterating through row index and column names
    for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
      const row: TableRow = {};
      
      columnNames.forEach(columnName => {
        const columnValues = tableData[columnName] || [];
        row[columnName] = columnValues[rowIndex] || null;
      });
      
      rows.push(row);
    }

    return rows;
  }

  /**
   * Determine the number of rows in a table by checking any column's array length
   * All columns should have the same number of rows
   * 
   * @param tableData - Dictionary of column data
   * @param columnNames - Array of column names
   * @returns Number of rows
   */
  private getTableRowCount(tableData: { [key: string]: string[] }, columnNames: string[]): number {
    for (const columnName of columnNames) {
      const value = tableData[columnName];
      if (Array.isArray(value) && value.length > 0) {
        return value.length;
      }
    }
    return 0;
  }

  /**
   * Serialize TableRow array back to dictionary format
   * Reverses parseTableRowsFromDictionary
   * [{ColumnName: "val1", ColumnName2: "val_a"}, ...] => 
   *   {"ColumnName": ["val1", "val2"], "ColumnName2": ["val_a", "val_b"]}
   * 
   * @param rows - Array of table rows
   * @param columnNames - Array of column names (in order)
   * @returns Dictionary of column name -> array of values
   */
  private serializeTableRowsToDictionary(rows: TableRow[], columnNames: string[]): { [key: string]: string[] } {
    if (!rows || rows.length === 0 || !columnNames || columnNames.length === 0) {
      return {};
    }

    const tableData: { [key: string]: string[] } = {};
    
    // Flatten rows into array values per column
    columnNames.forEach(columnName => {
      const columnValues = rows.map(row => row[columnName] || '');
      tableData[columnName] = columnValues;
    });

    return tableData;
  }

  /**
   * Detect if extracted value is table-formatted data from API response
   * Table format: array of objects where each object has column names as keys,
   * and each column is an object with 'Value' and bbox properties
   * Example: [{ "ColumnA": { "Value": "...", "BoundingBox": [...], ... }, "ColumnB": { ... } }, ...]
   * 
   * Non-table format: array of objects with direct 'Value' property or no column structure
   */
  private isTableFormatData(value: any): boolean {
    if (!Array.isArray(value) || value.length === 0) {
      return false;
    }

    const firstItem = value[0];
    if (typeof firstItem !== 'object' || firstItem === null) {
      return false;
    }

    // Check if first item has nested objects with 'Value' property (table format)
    // vs direct 'Value' property (list format)
    const keys = Object.keys(firstItem);
    
    // If there's a direct 'Value' key, it's a list format (array field)
    if (keys.includes('Value')) {
      return false;
    }

    // Check if keys have values that are objects with 'Value' property (table format)
    for (const key of keys) {
      const item = firstItem[key];
      if (typeof item === 'object' && item !== null && 'Value' in item) {
        return true;
      }
    }

    return false;
  }

  /**
   * Extract table data from API response format to TableData dictionary format
   * Converts: [{ "ColumnA": { "Value": "val1", ... }, "ColumnB": { "Value": "val_a", ... } }, ...]
   * Into: { "ColumnA": ["val1", "val2", ...], "ColumnB": ["val_a", "val_b", ...] }
   * 
   * @param data - Array of objects representing table rows from API
   * @returns Object with tableData (TableData format) and columns (column names in order)
   */
  private extractTableDataFromApi(data: any[]): { tableData: TableData; columns: string[] } {
    const columns: string[] = [];
    const tableData: TableData = {};

    if (!Array.isArray(data) || data.length === 0) {
      return { tableData, columns };
    }

    // Get column names from first row (all objects should have same columns)
    const firstRow = data[0];
    if (typeof firstRow === 'object' && firstRow !== null) {
      // Filter out non-object values and metadata fields like 'Original Value', 'Reasoning', etc.
      columns.push(
        ...Object.keys(firstRow).filter(key => {
          const item = firstRow[key];
          return typeof item === 'object' && item !== null && 'Value' in item;
        })
      );
    }

    // Initialize column arrays
    columns.forEach(col => {
      tableData[col] = [];
    });

    // Extract values from each row
    data.forEach((rowData: any, rowIndex: number) => {
      columns.forEach(columnName => {
        const columnData = rowData[columnName];
        if (typeof columnData === 'object' && columnData !== null && 'Value' in columnData) {
          const value = columnData.Value || '';
          (tableData[columnName] as string[]).push(value);
        } else {
          (tableData[columnName] as string[]).push('');
        }
      });
    });

    return { tableData, columns };
  }

  /**
   * Extract per-cell bounding box data from table rows
   * Returns array of bboxes with page/width metadata, suitable for fieldSourceDataMap storage
   * Bboxes are collected in row-major order: [row0-col0, row0-col1, ..., row1-col0, row1-col1, ...]
   * 
   * @param data - Array of table row objects from API
   * @param columns - Column names in order
   * @returns Object with boundingBoxes array and metadata (pageNumber, pageWidth)
   */
  private extractTableBoundingBoxes(
    data: any[],
    columns: string[]
  ): {
    boundingBoxes: number[][];
    pageNumber: number;
    pageWidth: number;
    cellMetadata: Array<{ rowIndex: number; columnName: string; bbox: number[] }>;
  } {
    const boundingBoxes: number[][] = [];
    const cellMetadata: Array<{ rowIndex: number; columnName: string; bbox: number[] }> = [];
    let pageNumber = 1;
    let pageWidth = 612; // Default to US Letter width

    if (!Array.isArray(data) || data.length === 0 || !columns || columns.length === 0) {
      return { boundingBoxes, pageNumber, pageWidth, cellMetadata };
    }

    // Extract bbox from each cell, maintaining row-major order
    data.forEach((rowData: any, rowIndex: number) => {
      columns.forEach(columnName => {
        const columnData = rowData[columnName];
        
        if (typeof columnData === 'object' && columnData !== null) {
          // Extract bbox - handle [x0, y0, x1, y1] format
          const bbox = columnData.BoundingBox || columnData.bounding_box;
          
          // Track page number and width from first valid cell
          if (rowIndex === 0 && columnName === columns[0]) {
            const rawPageNum = columnData['Page Number'];
            pageNumber = typeof rawPageNum === 'string' ? parseInt(rawPageNum, 10) : (rawPageNum || 1);
            pageWidth = columnData['Page Width'] || 612;
          }

          // Add bbox if valid [x0, y0, x1, y1]
          if (
            bbox &&
            Array.isArray(bbox) &&
            bbox.length >= 4 &&
            typeof bbox[0] === 'number' &&
            typeof bbox[1] === 'number' &&
            typeof bbox[2] === 'number' &&
            typeof bbox[3] === 'number'
          ) {
            boundingBoxes.push([bbox[0], bbox[1], bbox[2], bbox[3]]);
            cellMetadata.push({
              rowIndex,
              columnName,
              bbox: [bbox[0], bbox[1], bbox[2], bbox[3]]
            });
          }
        }
      });
    });

    return { boundingBoxes, pageNumber, pageWidth, cellMetadata };
  }

  // Find matching attribute for a truth value - PRODUCTION-GRADE matching with all strategies
  private findMatchingAttribute(tvAttributeName: string): Attribute | undefined {
    if (!tvAttributeName) return undefined;
    
    // Skip invalid attribute names (like literal "string" type)
    if (tvAttributeName === 'string' || tvAttributeName === 'number' || tvAttributeName === 'boolean') {
      return undefined;
    }
    
    // Normalize the truth value attribute name
    const normalizedTvName = this.normalizeAttributeName(tvAttributeName);
    
    // Try multiple matching strategies in order of preference
    const matched = this.attributes.find(attr => {
      // Strategy 1: Exact match (case-sensitive)
      if (attr.attributeKey === tvAttributeName || attr.attributeLabel === tvAttributeName) {
        return true;
      }
      
      // Strategy 2: Case-insensitive match
      const attrKeyLower = String(attr.attributeKey || '').toLowerCase();
      const attrLabelLower = String(attr.attributeLabel || '').toLowerCase();
      const tvNameLower = tvAttributeName.toLowerCase();
      
      if (attrKeyLower === tvNameLower || attrLabelLower === tvNameLower) {
        return true;
      }
      
      // Strategy 3: Normalized match (removes spaces, underscores, special chars)
      const normalizedKey = this.normalizeAttributeName(attr.attributeKey || '');
      const normalizedLabel = this.normalizeAttributeName(attr.attributeLabel || '');
      if (normalizedKey === normalizedTvName || normalizedLabel === normalizedTvName) {
        return true;
      }
      
      // Strategy 4: {idx} template expansion matching
      // Handles: "Borrower{idx}_CAIVRS", "Receipt_Signature{idx}", etc.
      // Expands template to concrete patterns and checks if truth value matches any
      const attrKey = String(attr.attributeKey || '');
      if (attrKey.includes('{idx}')) {
        const expandedPatterns = this.expandIdxTemplate(attrKey);
        // Check if truth value matches any expanded pattern (case-insensitive)
        if (expandedPatterns.some(pattern => pattern.toLowerCase() === tvNameLower)) {
          return true;
        }
      }
      
      return false;
    });
    
    // Debug logging for unmatched values (remove after debugging)
    if (!matched) {
    }
    
    return matched;
  }

  // Load all truth values for a file into cache
  loadTruthValuesForFile(fileId: number | string): void {
    // Use cached truth values if available (avoids redundant API calls on file re-visit)
    const cacheKey = String(fileId);
    const cached = this.fileTruthValuesCache.get(cacheKey);
    if (cached) {
      // Cache now stores full AttributesResponse
      this.selectedFileAttributeSummary = cached.attribute_summary;
      // Also ensure fileStatusCache is in sync with cached data (for when user switches files)
      const { total_count, filled_count } = cached.attribute_summary;
      let status: GroundTruthStatus;
      if (total_count === 0) {
        status = GroundTruthStatus.NOT_STARTED;
      } else if (filled_count >= total_count) {
        status = GroundTruthStatus.COMPLETE;
      } else if (filled_count > 0) {
        status = GroundTruthStatus.PARTIAL;
      } else {
        status = GroundTruthStatus.NOT_STARTED;
      }
      
      const tooltip = status === GroundTruthStatus.COMPLETE
        ? `Complete - ${filled_count} out of ${total_count} filled`
        : status === GroundTruthStatus.PARTIAL
          ? `In Progress - ${filled_count} out of ${total_count} filled`
          : `Not Started - 0 out of ${total_count} filled`;
      
      this.fileStatusCache.set(fileId, {
        status,
        tooltip,
        filledRequired: filled_count,
        totalRequired: total_count
      });
      
      this.processTruthValues(cached.attributes, fileId);
      // Re-apply pending extraction state after hydration so transient source maps
      // (including bounding boxes) persist across refresh/navigation.
      this.checkAndApplyPendingResults(fileId);
      return;
    }
    this.truthManagementService.getTruthValuesByFileId(fileId).subscribe({
      next: (response) => {
        // Store full AttributesResponse in cache to preserve attribute_summary
        this.fileTruthValuesCache.set(cacheKey, response);
        
        // Store attribute_summary for the selected file
        this.selectedFileAttributeSummary = response.attribute_summary;
          if (response?.attribute_summary?.invalid_fields && Array.isArray(response.attribute_summary.invalid_fields)) {
      this.invalidAttributes = new Set(response.attribute_summary.invalid_fields);
    } else {
      this.invalidAttributes = new Set();  // Clear if no invalid fields
    }
        
        // CRITICAL: Update fileStatusCache so when user switches away and back, correct status is shown
        // Convert attribute_summary to StatusMetadata format
        const { total_count, filled_count } = response.attribute_summary;
        let status: GroundTruthStatus;
        if (total_count === 0) {
          status = GroundTruthStatus.NOT_STARTED;
        } else if (filled_count >= total_count) {
          status = GroundTruthStatus.COMPLETE;
        } else if (filled_count > 0) {
          status = GroundTruthStatus.PARTIAL;
        } else {
          status = GroundTruthStatus.NOT_STARTED;
        }
        
        const tooltip = status === GroundTruthStatus.COMPLETE
          ? `Complete - ${filled_count} out of ${total_count} filled`
          : status === GroundTruthStatus.PARTIAL
            ? `In Progress - ${filled_count} out of ${total_count} filled`
            : `Not Started - 0 out of ${total_count} filled`;
        
        this.fileStatusCache.set(fileId, {
          status,
          tooltip,
          filledRequired: filled_count,
          totalRequired: total_count
        });
        // Process the attributes array
        this.processTruthValues(response.attributes, fileId);
        // Re-apply pending extraction state after hydration so transient source maps
        // (including bounding boxes) persist across refresh/navigation.
        this.checkAndApplyPendingResults(fileId);
      },
      error: (error) => {
        this.currentFileTruthValues.clear();
        
        // Reset attribute_summary on error
        this.selectedFileAttributeSummary = { total_count: 0, filled_count: 0 };
        
        this.fileAttributeCounts.set(fileId, 0);
        
        this.currentFileTruthValues.clear();
      }
    });
  }

  /**
   * Process truth values array into component state.
   * Shared by both fresh API fetch and cache hit paths.
   */
  private processTruthValues(truthValues: TruthValue[], fileId: number | string, attribute_summary?: { total_count: number; filled_count: number }): void {
    // Store attribute_summary if provided (from API response)
    if (attribute_summary) {
      this.selectedFileAttributeSummary = attribute_summary;
    }
    this.currentFileTruthValues.clear();
    this.notApplicableAttributes.clear();
    this.backendNAAttributes.clear();
    this.fieldSourceDataMap.clear();
    this.originalApiAttributeNames.clear();

    let matchedCount = 0;
    let unmatchedCount = 0;
    const unmatchedNames: string[] = [];

    // Deduplicate: when DB has both space-format ("Actual Rent Unit 1") and
    // underscore-format ("Actual_Rent_Unit_1") for the same field, prefer space-format.
    const deduplicatedValues = this.deduplicateByAttributeName(truthValues);

    deduplicatedValues.forEach((tv, index) => {
      const tvAttributeName = (tv as any).attributeName;

      if (tvAttributeName) {
        const matchingAttr = this.findMatchingAttribute(tvAttributeName);

        if (matchingAttr && matchingAttr.id) {
          // v2 API returns value as native array if data_type === "array"
          // Store value directly - native array support eliminates delimiter concatenation
          this.currentFileTruthValues.set(matchingAttr.id, tv);
          
          // Parse table data if this is a table field
          if (matchingAttr.isTableField && matchingAttr.tableColumns && matchingAttr.tableColumns.length > 0) {
            const columnNames = matchingAttr.tableColumns.map(col => col.name);
            
            // Table fields store value as JSON dictionary: {"ColumnName": ["val1", "val2", "val3"], ...}
            let tableDataDict: { [key: string]: string[] } = {};
            
            try {
              // Try to parse as JSON if it's a string
              if (typeof tv.value === 'string') {
                const parsed = JSON.parse(tv.value);
                if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                  tableDataDict = parsed;
                }
              } else if (typeof tv.value === 'object' && tv.value !== null && !Array.isArray(tv.value)) {
                // Already an object
                tableDataDict = tv.value as { [key: string]: string[] };
              }
            } catch (e) {
              tableDataDict = {};
            }
            
            if (Object.keys(tableDataDict).length > 0) {
              // Parse dictionary into TableRow array
              const rows = this.parseTableRowsFromDictionary(tableDataDict, columnNames);
              matchingAttr.tableData = tableDataDict;
            } else {
              matchingAttr.tableData = {};
            }

            // Reconstruct per-cell bounding boxes (User Story 7126). bounding_box/page_number are
            // stored as flat arrays aligned to the same canonical cell order used on save:
            // for each row (0..rowCount-1), for each column in tableColumns order.
            const tablePageNumberRaw = (tv as any).page_number;
            const tableBoundingBoxRaw = (tv as any).bounding_box;

            if (Array.isArray(tableBoundingBoxRaw) && tableBoundingBoxRaw.length > 0) {
              const rowCount = this.getTableRowCount(tableDataDict, columnNames);
              const cellMetadata: Array<{ rowIndex: number; columnName: string; bbox: number[] }> = [];
              const tableBoundingBoxes: number[][] = [];
              let firstValidPageNum: number | null = null;

              let cellIndex = 0;
              for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
                for (const columnName of columnNames) {
                  const bbox = tableBoundingBoxRaw[cellIndex];
                  if (Array.isArray(bbox) && bbox.length >= 4) {
                    const pageVal = Array.isArray(tablePageNumberRaw) ? tablePageNumberRaw[cellIndex] : tablePageNumberRaw;
                    const parsedPage = pageVal != null ? parseInt(String(pageVal), 10) : null;
                    if (parsedPage && parsedPage > 0) {
                      if (!firstValidPageNum) firstValidPageNum = parsedPage;
                      const normalizedBbox: number[] = [bbox[0], bbox[1], bbox[2], bbox[3]];
                      tableBoundingBoxes.push(normalizedBbox);
                      cellMetadata.push({ rowIndex, columnName, bbox: normalizedBbox });
                    }
                  }
                  cellIndex++;
                }
              }

              if (tableBoundingBoxes.length > 0 && firstValidPageNum) {
                this.fieldSourceDataMap.set(matchingAttr.id, {
                  pageNumber: firstValidPageNum,
                  boundingBoxes: tableBoundingBoxes,
                  pageWidth: this.getPageWidthFromPdf(firstValidPageNum),
                  originalArrayLength: rowCount,
                  cellMetadata
                });
              }
            }
          }

          // Populate fieldSourceDataMap from DB for bounding box highlighting (User Story 7126)
          // Handle aligned arrays from API for array-type fields (table fields are handled above)
          const pageNumber = (tv as any).page_number;
          const boundingBox = (tv as any).bounding_box;
          
          // Get data type from FIELD SCHEMA (more reliable than API response)
          const documentCode = this.selectedDocument?.documentNumber;
          const fieldSchemas = documentCode ? this.fieldSchemaMap.get(documentCode) : undefined;
          let isArrayField = false;
          if (fieldSchemas && Array.isArray(fieldSchemas)) {
            const fieldSchema = fieldSchemas.find((f: any) => f.field_name === tvAttributeName);
            isArrayField = !!(fieldSchema && (fieldSchema.field_datatype === 'array' || fieldSchema.field_schema?.type === 'array'));
          }
          if (!matchingAttr.isTableField && boundingBox && Array.isArray(boundingBox) && boundingBox.length > 0) {
            const validBboxes: (number[] | null)[] = [];
            let firstValidPageNum: number | null = null;
            const currentArrayLength = Array.isArray(tv.value) ? tv.value.length : 1;

            const firstNonNull = boundingBox.find((b: any) => b != null);
            if (firstNonNull && Array.isArray(firstNonNull)) {
              (boundingBox as any[]).forEach((bbox: any, idx: number) => {
                if (!Array.isArray(bbox) || bbox.length < 4) {
                  validBboxes.push(null);
                  return;
                }

                const pageVal = Array.isArray(pageNumber) ? pageNumber[idx] : pageNumber;
                const parsedPage = pageVal != null ? parseInt(String(pageVal), 10) : null;

                if (parsedPage && parsedPage > 0) {
                  if (!firstValidPageNum) {
                    firstValidPageNum = parsedPage;
                  }
                  validBboxes.push([bbox[0], bbox[1], bbox[2], bbox[3]]);
                } else {
                  validBboxes.push(null);
                }
              });
            } else if (typeof boundingBox[0] === 'number' && boundingBox.length >= 4) {
              const pageVal = Array.isArray(pageNumber) ? pageNumber[0] : pageNumber;
              const parsedPage = pageVal != null ? parseInt(String(pageVal), 10) : null;

              if (parsedPage && parsedPage > 0) {
                firstValidPageNum = parsedPage;
                validBboxes.push([boundingBox[0], boundingBox[1], boundingBox[2], boundingBox[3]]);
              }
            }

            if (validBboxes.some(b => b !== null) && firstValidPageNum) {
              this.fieldSourceDataMap.set(matchingAttr.id, {
                pageNumber: firstValidPageNum,
                boundingBoxes: validBboxes as number[][],
                pageWidth: this.getPageWidthFromPdf(firstValidPageNum),
                originalArrayLength: currentArrayLength
              });
            }
          }
          // If value is "N/A" or "NA", mark attribute as not applicable
          if (tv.value) {
            let valueParts: string[];
            if (Array.isArray(tv.value)) {
              valueParts = tv.value.map(v => String(v).trim().toLowerCase()).filter(v => v.length > 0);
            } else {
              valueParts = [String(tv.value).trim().toLowerCase()];
            }
            const allPartsAreNA = valueParts.length > 0 && valueParts.every(part => part === 'n/a' || part === 'na');
            if (allPartsAreNA) {
              // Only add to N/A if we didn't just clear it (user toggled it ON)
              // If backend returned N/A but we have a modified value set, user cleared it - don't override
              if (!this.modifiedAttributeIds.has(matchingAttr.id)) {
                this.notApplicableAttributes.add(matchingAttr.id);
                this.backendNAAttributes.add(matchingAttr.id);
              }
            }
          }
          matchedCount++;
        } else {
          unmatchedCount++;
          unmatchedNames.push(tvAttributeName);
        }
      }
    });

    if (unmatchedCount > 0) {
      const docCode = this.selectedDocument?.documentNumber || 'Unknown';
      // Debug: Show available attribute keys for troubleshooting
      this.attributes.slice(0, 20).forEach((attr, idx) => {
      });
    }

    // Deduplicate bounding boxes loaded from DB to match auto-fill behavior
    // Skip table fields: cellMetadata indexes boundingBoxes 1:1 by (row, column) for per-cell
    // highlighting, and deduplicating would desync that mapping.
    this.fieldSourceDataMap.forEach((sourceData, attributeId) => {
      if (!sourceData.cellMetadata && sourceData.boundingBoxes && sourceData.boundingBoxes.length > 1) {
        const { uniqueBboxes, indicesMap } = this.deduplicateBoundingBoxes(sourceData.boundingBoxes);
        if (uniqueBboxes.length < sourceData.boundingBoxes.length) {
          sourceData.boundingBoxes = uniqueBboxes;
          sourceData.boundingBoxIndices = indicesMap;
        }
      }
    });

    this.fileAttributeCounts.set(fileId, this.currentFileTruthValues.size);
    this.fileAttributeCounts.set(fileId.toString(), this.currentFileTruthValues.size);

    // Schedule change detection after current cycle to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => this.cdr.detectChanges(), 0);
  }

  /**
   * Handle select field change
   * Called when user selects from dropdown
   * Note: [(ngModel)] automatically syncs editingTruthValueText with dropdown value
   */
  onSelectFieldChange(event: Event, attributeId: number): void {
    // Defensive check: ensure editing state is set
    if (this.editingAttributeId !== attributeId) {
      this.startEditingAttribute(attributeId, null);
    }
    
    // Build full value — for array fields update only the edited index, preserve the rest
    const trimmedValue = this.editingTruthValueText.trim();
    const existingValue = this.currentFileTruthValues.get(attributeId);
    const originalValue = this.getCurrentTruthValue(attributeId);

    // Convert to native array format - filter out empty strings for clean comparison
    const originalArray = Array.isArray(originalValue)
      ? [...originalValue].map(v => String(v).trim()).filter(v => v.length > 0)
      : (typeof originalValue === 'string' && originalValue.trim() ? [originalValue.trim()] : []);

    let newArray: string[];
    if (this.editingArrayIndex !== null) {
      // Build array with only non-empty values, then insert new value at correct position
      newArray = [...originalArray];
      
      // Extend array if needed to accommodate the editing index
      while (newArray.length <= this.editingArrayIndex) {
        newArray.push('');
      }
      
      // Set the value at this index
      newArray[this.editingArrayIndex] = trimmedValue;
      
      // Filter out empty strings to keep array clean (splice will happen later if needed)
      if (trimmedValue === '') {
        // If clearing a value, remove it from the array
        newArray.splice(this.editingArrayIndex, 1);
      }
    } else {
      newArray = trimmedValue !== '' ? [trimmedValue] : [];
    }

    // No-op guard: opening dropdown and leaving without effective value change
    // must not clear auto-fill indicators.
    const baselineLower = originalArray
      .filter(v => v.length > 0)
      .map(v => v.toLowerCase());
    const newArrayLowerForNoOp = newArray
      .filter(v => v.length > 0)
      .map(v => v.toLowerCase());

    const isNoOpAgainstDisplayedValue =
      JSON.stringify([...newArrayLowerForNoOp].sort()) ===
      JSON.stringify([...baselineLower].sort());

    if (isNoOpAgainstDisplayedValue) {
      if (trimmedValue === '') {
        this.editingAttributeId = null;
        this.editingArrayIndex = null;
        this.editingTruthValueText = '';
      }
      this.cdr.detectChanges();
      return;
    }

    const storedOriginal = Array.isArray(existingValue?.value)
      ? existingValue.value.map(v => String(v).trim().toLowerCase()).filter(v => v.length > 0)
      : (typeof existingValue?.value === 'string' ? [existingValue.value.trim().toLowerCase()] : []);

    // Compare array values (order-independent for case-insensitive match)
    const newArrayLower = newArray.filter(v => v.length > 0).map(v => v.toLowerCase());
    const arrayChanged = JSON.stringify(newArrayLower.sort()) 
      !== JSON.stringify(storedOriginal.sort());

    if (arrayChanged || (this.editingArrayIndex === null && trimmedValue.toLowerCase() !== (storedOriginal[0] || '').toLowerCase())) {
      // Store only non-empty values in modified values
      const cleanArray = newArray.filter(v => v.length > 0);
      this.modifiedValues.set(attributeId, cleanArray);
      this.modifiedAttributeIds.add(attributeId);
      this.autoPopulatedAttributeIds.delete(attributeId);

      if (this.selectedFile?.id !== undefined && this.selectedFile?.id !== null) {
        this.clearAutoFillCompletedForFile(this.selectedFile.id);
      }

      // Auto-enable field (remove from N/A) when user selects a value
      if (trimmedValue !== '') {
        this.notApplicableAttributes.delete(attributeId);
      }

      // Preserve source bbox data for unchanged sibling indexes in arrays.
    } else {
      // Value didn't change - remove from modified tracking
      this.modifiedAttributeIds.delete(attributeId);
      this.modifiedValues.delete(attributeId);
    }

    // If the user selected "-- Select --" (empty), exit edit mode immediately
    if (trimmedValue === '') {
      this.editingAttributeId = null;
      this.editingArrayIndex = null;
      this.editingTruthValueText = '';
    }

    // Trigger change detection to update status indicator immediately
    this.cdr.detectChanges();
  }

  /**
   * Handle blur for select/dropdown fields.
   * onSelectFieldChange already committed the correct value to modifiedValues — do NOT
   * call onTruthValueBlur, which re-reads getCurrentTruthValue (already updated by
   * onSelectFieldChange) and reconstructs the array value a second time, deleting an
   * extra entry. Example: clearing slot 0 of "A,B,C" → onSelectFieldChange correctly
   * gives "B,C"; onTruthValueBlur would then wrongly give "C" by removing slot 0 again.
   */
  onSelectBlur(event: Event, attributeId: number): void {
    if (this.editingAttributeId !== attributeId) {
      return;
    }

    // N/A restore guard: startEditingAttribute removes a field from notApplicableAttributes
    // the moment the user clicks it. If they open a dropdown and click away without
    // choosing anything, we must restore the N/A state so data is not silently lost.
    const storedOriginal = this.currentFileTruthValues.get(attributeId)?.value;
    const storedArray = Array.isArray(storedOriginal)
      ? storedOriginal.map(v => String(v).trim().toLowerCase())
      : (typeof storedOriginal === 'string' ? [storedOriginal.trim().toLowerCase()] : []);
    const isStoredNA = storedArray.length > 0 && storedArray.every(p => p === 'n/a' || p === 'na');
    if (this.editingTruthValueText.trim() === '' && isStoredNA) {
      this.notApplicableAttributes.add(attributeId);
      this.modifiedValues.delete(attributeId);
      this.modifiedAttributeIds.delete(attributeId);
    }

    // Exit edit mode — value already committed by onSelectFieldChange
    this.editingAttributeId = null;
    this.editingArrayIndex = null;
    this.editingTruthValueText = '';
    this.cdr.detectChanges();
  }

  /**
   * PRODUCTION-GRADE: Handle text field input
   * Called when user types in text/textarea field
   */
  onTextFieldInput(event: Event, attributeId: number): void {
    const inputElement = event.target as HTMLInputElement | HTMLTextAreaElement;
    const value = inputElement.value;
    
    // Ensure editing state is set
    if (this.editingAttributeId !== attributeId) {
      this.startEditingAttribute(attributeId, null);
    }
    
    // Update editing text (with leading whitespace removed)
    this.onTruthValueInputChange(value);
  }

  /**
   * Handle mousedown for immediate visual feedback
   * Shows activating state BEFORE edit mode fully renders
   */
  onValueBoxMouseDown(attributeId: number, arrayIndex: number | null = null): void {
    this.activatingAttributeId = attributeId;
    this.activatingArrayIndex = arrayIndex;
    
    // Clear activating state after brief moment
    setTimeout(() => {
      this.activatingAttributeId = null;
      this.activatingArrayIndex = null;
      this.cdr.detectChanges();
    }, 300);
  }

  // Start editing a specific attribute's truth value
  startEditingAttribute(attributeId: number, arrayIndex: number | null = null): void {
    // Prevent editing during auto-fill extraction for the current file
    if (this.isCurrentFileExtracting()) {
      return;
    }
    // AUTO-FIX: If field is marked as N/A, automatically enable it when user starts editing
    // This prevents data loss where user types a value but "N/A" gets saved instead
    if (this.notApplicableAttributes.has(attributeId)) {
      this.notApplicableAttributes.delete(attributeId);
      // Clear "N/A" from modified values to allow loading actual saved value or empty string
      if (this.modifiedValues.get(attributeId) === 'N/A') {
        this.modifiedValues.delete(attributeId);
        this.modifiedAttributeIds.delete(attributeId);
      }
    }
    
    this.editingAttributeId = attributeId;
    this.editingArrayIndex = arrayIndex;
    
    // Load existing value - check modified first, then current
    const fullValue = this.getCurrentTruthValue(attributeId);
    
    // If editing a specific array index, get just that value
    if (arrayIndex !== null) {
      const values = Array.isArray(fullValue)
        ? fullValue.map(v => String(v).trim())
        : (typeof fullValue === 'string' && fullValue.trim() ? [fullValue.trim()] : []);
      this.editingTruthValueText = values[arrayIndex] || '';
    } else {
      this.editingTruthValueText = Array.isArray(fullValue) ? fullValue[0] || '' : String(fullValue || '');
    }
    if (this.editingTruthValueText === 'N/A') {
  this.editingTruthValueText = '';
}
    
    // Get field type and metadata directly from attribute
    const attribute = this.attributes.find(attr => attr.id === attributeId);
    
    if (attribute) {
      const fieldType = this.getFieldType(attributeId);
      this.editingFieldType = fieldType;
      this.editingAllowedValues = this.getFieldAllowedValues(attributeId);
      this.editingFieldMetadata = {
        feature: attribute.feature,
        allowed_values: attribute.allowed_values || []
      };
      
      // Normalize date format if it's a date field
      if (fieldType === 'date') {
        this.editingTruthValueText = this.normalizeDateToMMDDYYYY(this.editingTruthValueText);
      }

      // Normalize select value casing to match allowed_values so ngModel pre-selects correctly.
      // API may store "yes" while schema has "Yes" — without this the dropdown renders blank.
      if (fieldType === 'select' && this.editingTruthValueText) {
        const matched = (attribute.allowed_values || []).find(
          av => av.toLowerCase() === this.editingTruthValueText.toLowerCase()
        );
        if (matched) {
          this.editingTruthValueText = matched;
        }
      }
    } else {
      // Fallback to old logic if attribute not found
      this.applyLegacyFieldTypeLogic(attribute);
    }
    
    // CRITICAL FIX: Force change detection AND markForCheck for OnPush strategy
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    
    // Auto-focus with production-grade retry logic
    this.focusEditingInputWithRetry();
  }
  
  /**
   * Legacy field type logic - ONLY used as fallback
   * Should rarely be called if enrichment works correctly
   */
  private applyLegacyFieldTypeLogic(attribute: Attribute | undefined): void {
    if (attribute) {
      const isDateField = attribute.feature === 'date' || 
        attribute.attributeLabel?.toLowerCase().includes('date') ||
        attribute.attributeKey?.toLowerCase().includes('date');
      
      const dropdownFeatures = ['signature', 'enum', 'checkbox'];
      const shouldUseDropdown = attribute.feature && dropdownFeatures.includes(attribute.feature);
      
      if (isDateField) {
        this.editingFieldType = 'date';
        this.editingFieldMetadata = { feature: 'date' };
        this.editingAllowedValues = [];
        this.editingTruthValueText = this.normalizeDateToMMDDYYYY(this.editingTruthValueText);
      } else if (shouldUseDropdown && attribute.allowed_values && attribute.allowed_values.length > 0) {
        this.editingFieldType = 'select';
        this.editingAllowedValues = attribute.allowed_values;
        this.editingFieldMetadata = { feature: attribute.feature, allowed_values: attribute.allowed_values };
      } else {
        this.editingFieldType = 'text';
        this.editingFieldMetadata = null;
        this.editingAllowedValues = [];
      }
    } else {
      this.editingFieldType = 'text';
      this.editingFieldMetadata = null;
      this.editingAllowedValues = [];
    }
  }
  
  /**
   * PRODUCTION-GRADE: Focus input with retry logic and NgZone wrapping
   * Fixes double-click issue by handling Angular 21's rendering timing
   */
  private focusEditingInputWithRetry(retryCount: number = 0): void {
    // Run inside Angular zone to ensure proper change detection
    this.ngZone.run(() => {
      // Calculate delay with exponential backoff: 150ms, 250ms, 350ms
      const baseDelay = 150; // Increased from 50ms for Angular 21 @if rendering cycle
      const delay = baseDelay + (retryCount * 100);
      const maxRetries = 3;
      
      setTimeout(() => {
        // Build specific selector based on current editing context
        let selector = '.truth-value-edit-input';
        
        // More specific selector based on field type
        if (this.editingFieldType === 'date') {
          selector = '.truth-value-edit-input.date-input';
        } else if (this.editingFieldType === 'select') {
          selector = '.truth-value-edit-input.select-input';
        } else {
          // Text input - exclude date and hidden date picker
          selector = '.truth-value-edit-input:not([type="date"]):not(.date-picker-hidden)';
        }
        
        const input = document.querySelector(selector) as HTMLElement;
        
        if (input) {
         input.focus();
          
          // For text inputs, place cursor at end
          if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
            const len = input.value?.length || 0;
            input.setSelectionRange(len, len);
          }
          
          // For select fields, open dropdown automatically
          if (input instanceof HTMLSelectElement && this.editingFieldType === 'select') {
            // Trigger click to open dropdown on some browsers
            setTimeout(() => {
              if (input && document.activeElement === input) {
                // Modern browsers
                if ('showPicker' in input && typeof (input as any).showPicker === 'function') {
                  try {
                    (input as any).showPicker();
                  } catch (e) {
                    // Fallback: just having focus is enough for most browsers
                  }
                }
              }
            }, 50);
          }
          
          // Force change detection after focus
          this.cdr.detectChanges();
        } else if (retryCount < maxRetries) {
          // Element not ready yet - retry with backoff
         this.focusEditingInputWithRetry(retryCount + 1);
        } else {
          // Max retries reached - log warning but don't fail
        }
      }, delay);
    });
  }
  
  /**
   * @deprecated Use focusEditingInputWithRetry() instead
   * Kept for backward compatibility
   */
  focusEditingInput(): void {
    this.focusEditingInputWithRetry();
  }

  /**
   * Toggle N/A (Not Applicable) state for an attribute
   * 
   * INVERTED BEHAVIOR:
   * - Toggle ON (checked) = Attribute ENABLED (can have value)
   * - Toggle OFF (unchecked) = Attribute DISABLED (marked as N/A, sends "N/A" to backend)
   */
  toggleNotApplicable(attributeId: number): void {
    if (this.isCurrentFileExtracting()) {
      return;
    }

    if (this.notApplicableAttributes.has(attributeId)) {
      // Currently N/A - User toggling ON to ENABLE attribute
      this.notApplicableAttributes.delete(attributeId);
      this.backendNAAttributes.delete(attributeId); // Also clear backend N/A flag
      
      const originalValue = this.currentFileTruthValues.get(attributeId);
      // Check if ALL stored values are N/A
      const storedVal = originalValue?.value;
      const storedIsNA = !storedVal ||
        (typeof storedVal === 'string' && (storedVal.trim() === '' || storedVal.toLowerCase() === 'n/a')) ||
        (Array.isArray(storedVal) && storedVal.map(v => String(v).trim().toLowerCase()).every(p => p === 'n/a' || p === 'na'));

      if (originalValue && !storedIsNA) {
        // Restore the original value - NOT modified (had a real value before)
        this.modifiedValues.delete(attributeId);
        this.modifiedAttributeIds.delete(attributeId);
      } else {
        // No original value or original was N/A - clear to empty
        this.modifiedValues.set(attributeId, '');
        this.modifiedAttributeIds.add(attributeId);
        // Explicitly remove from notApplicableAttributes to ensure 
      // it's treated as a regular field with empty value, not N/A
      this.notApplicableAttributes.delete(attributeId);
      this.backendNAAttributes.delete(attributeId);
        
        // Clear the cached truth value to prevent stale N/A from showing
        if (originalValue) {
          originalValue.value = '';
          this.currentFileTruthValues.set(attributeId, originalValue);
        }
      }
    } else {
      // Currently ENABLED - User toggling OFF to mark as N/A
      this.notApplicableAttributes.add(attributeId);
      // DO NOT add to backendNAAttributes - this is a user toggle, not backend data
      this.modifiedValues.set(attributeId, 'N/A');
      this.modifiedAttributeIds.add(attributeId);
    }

    this.cdr.detectChanges();
  }
  // Check if an attribute is marked as Not Applicable
  isNotApplicable(attributeId: number): boolean {
    return this.notApplicableAttributes.has(attributeId);
  }

  // Check if an attribute's N/A value came from backend (should display "N/A" text)
  isBackendNA(attributeId: number): boolean {
    return this.backendNAAttributes.has(attributeId);
  }

  /**
   * Get placeholder text for input fields
   * - Toggle OFF (N/A): Shows message about field not available
   * - Toggle ON: Shows field type specific placeholder
   */
  getPlaceholderText(attributeId: number): string {
    if (this.isNotApplicable(attributeId)) {
      return 'Field is turned off as field value is not available in the PDF';
    }
    
    // Default placeholders based on field type
    const fieldType = this.getFieldType(attributeId);
    if (fieldType === 'date') return 'MM/DD/YYYY';
    if (fieldType === 'select') return '-- Select --';
    return 'Type text value here...';
  }

  /**
   * Get placeholder text for display-only views (non-edit mode)
   * - Toggle OFF (N/A) → Shows "Field is turned off..." message
   * - Otherwise → Shows field type specific placeholder (MM/DD/YYYY, -- Select --, etc.)
   */
  getDisplayPlaceholderText(attributeId: number): string {
    // If toggle is OFF, show NOT APPLICABLE message
    if (this.isNotApplicable(attributeId)) {
      return 'Field is turned off as field value is not available in the PDF';
    }
    
    // Otherwise return field-type-specific placeholder
    const fieldType = this.getFieldType(attributeId);
    if (fieldType === 'date') return 'MM/DD/YYYY';
    if (fieldType === 'select') return '-- Select --';
    return 'Enter the value, click + to add';
  }

  /**
   * Get display value for an attribute
   * - Backend N/A → Display "N/A" text
   * - User toggle OFF → Display blank
   * - Regular value → Display the value
   */
  getDisplayValue(attributeId: number): string {
  // If field is marked as N/A (regardless of source), show blank
  if (this.isNotApplicable(attributeId)) {
    return '';
  }
  
  // Otherwise, show the actual value (convert to string for display)
  return toStringValue(this.getCurrentTruthValue(attributeId));
}

  // Helper: Detect if a field is a date field by checking multiple properties
  private isDateField(attribute: Attribute): boolean {
    if (!attribute) return false;
    const feature = String(attribute.feature || '').toLowerCase();
    const label = String(attribute.attributeLabel || '').toLowerCase();
    const key = String(attribute.attributeKey || '').toLowerCase();
    
    return feature.includes('date') || 
           label.includes('date') ||
           key.includes('date');
  }

  /**
 * Check if attribute is invalid (from backend API)
 * Backend returns invalid_fileds list from calculate_file_status_backend_v2()
 * 
 * @param attributeId - Attribute ID
 * @returns 'valid' | 'invalid' | 'none'
 */
isValueValid(attributeId: number): 'valid' | 'invalid' | 'none' {
  const attribute = this.attributes.find(a => a.id === attributeId);
  if (!attribute) return 'none';
  
  // Check if in invalid attributes set from API
  if (this.invalidAttributes.has(attribute.attributeLabel)) {
    return 'invalid';  // Shows ?
  }
  
  // Check if has data
  if (this.hasNonEmptyValue(attributeId)) {
    return 'valid';    // Shows ✓
  }
  
  return 'none';       // Shows o
}
  // Get the validation indicator symbol
  getValueIndicator(attributeId: number): string {
    const status = this.isValueValid(attributeId);
    switch (status) {
      case 'valid': return '✓';
      case 'invalid': return '?';
      case 'none': return '○';
    }
  }

  // Check if an attribute has unsaved modifications
  isModified(attributeId: number): boolean {
    return this.modifiedAttributeIds.has(attributeId);
  }

  getCurrentTruthValue(attributeId: number): string | string[] | TableData {
    if (this.modifiedValues.has(attributeId)) {
      return this.modifiedValues.get(attributeId) ?? '';
    }

    return this.currentFileTruthValues.get(attributeId)?.value ?? '';
  }

  // Get table data for an attribute - modified value takes precedence over the original, matching getCurrentTruthValue's pattern
  getTableDataValue(attribute: Attribute): TableData {
    if (this.modifiedValues.has(attribute.id!)) {
      return this.modifiedValues.get(attribute.id!) as TableData;
    }

    return attribute.tableData || {};
  }

  // Check if attribute has a non-empty value
  hasNonEmptyValue(attributeId: number): boolean {
    const modifiedValue = this.modifiedValues.get(attributeId);
    if (modifiedValue !== undefined && modifiedValue !== null) {
      // For TableData, check if any column has values
      if (typeof modifiedValue === 'object' && !Array.isArray(modifiedValue)) {
        return Object.values(modifiedValue).some(v => v && String(v).trim() !== '');
      }
      const strValue = toStringValue(modifiedValue);
      if (strValue.trim() !== '') {
        return true;
      }
    }
    const truthValue = this.currentFileTruthValues.get(attributeId);
    if (truthValue?.value !== undefined && truthValue?.value !== null) {
      // For TableData in truth values, check if any column has values
      if (typeof truthValue.value === 'object' && !Array.isArray(truthValue.value)) {
        return Object.values(truthValue.value).some(v => v && String(v).trim() !== '');
      }
      const strValue = toStringValue(truthValue.value);
      if (strValue.trim() !== '') {
        return true;
      }
    }
    return false;
  }

  // Get value as array - only splits for attributes with is_array === true
  getValueAsArray(attributeId: number): string[] {
    const value = this.getCurrentTruthValue(attributeId);
    if (!value) return [];
    
    // Check if this attribute is explicitly marked as array
    const attribute = this.attributes.find(a => a.id === attributeId);
    const isArrayAttribute = attribute?.is_array === true;
    
    // For non-array attributes, ALWAYS return the value as a single element
    // This ensures commas in text don't create multiple value boxes
    if (!isArrayAttribute) {
      return [toStringValue(value)];
    }
    
    // For array attributes, check if value is a JSON array format like ["val1", "val2"]
    const stringValue = toStringValue(value);
    const trimmedValue = stringValue;
    if (trimmedValue.startsWith('[') && trimmedValue.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmedValue);
        if (Array.isArray(parsed)) {
          // FIX: Always filter empty strings for consistency with splice-based removal
          const parsedArray = parsed.map(v => String(v).trim()).filter(v => v.length > 0);
          return parsedArray;
        }
      } catch (e) {
        // Not valid JSON, process as string value
      }
    }
    
    // For array attributes, work with native array format
    let parts: string[];
    if (Array.isArray(value)) {
      parts = value.map(v => String(v).trim()).filter(v => v.length > 0);
    } else if (typeof value === 'string') {
      parts = [value.trim()].filter(v => v.length > 0);
    } else {
      parts = [];
    }
    
    // Filter out N/A values
    const nonNAParts = parts.filter(v => {
      const lower = String(v || '').toLowerCase();
      return !(lower === 'n/a' || lower === 'na'); // Remove N/A
    });
    
    // Empty strings are filtered out above for clean array handling
    return nonNAParts;
  }

  /**
   * Get display value as array for array-type attributes
   * - Backend N/A → Show [] (empty array, no boxes)
   * - User toggle OFF → Show [] (empty array, no boxes)
   * - Regular values → Show array elements (filter out empties for clean UI)
   */
 getDisplayValueAsArray(attributeId: number): string[] {
  // If field is marked as N/A (regardless of source), show empty array (no boxes)
  if (this.isNotApplicable(attributeId)) {
    return [];
  }
  
  const values = this.getValueAsArray(attributeId);
  
  // Filter out N/A values - they shouldn't display as boxes (just like dates/names)
  const nonNAValues = values.filter(v => {
    const trimmed = v?.toString().trim().toLowerCase();
    return !(trimmed === 'n/a' || trimmed === 'na');
  });
  
  // If currently editing this attribute, show filled values + ONLY the one being edited
  if (this.editingAttributeId === attributeId && this.editingArrayIndex !== null) {
    // Build result with filled values + the one being edited
    const result = nonNAValues.map((v, idx) => 
      (idx === this.editingArrayIndex || (v && v.trim().length > 0)) ? v : null
    ).filter((v, idx) => v !== null || idx === this.editingArrayIndex) as string[];
    
    // If editing a NEW entry beyond the current array (e.g., clicking + to add)
    // append empty string so template can render the editing box
    if (this.editingArrayIndex >= nonNAValues.length) {
      // Add empty strings to fill gaps, then the editing entry
      while (result.length < this.editingArrayIndex) {
        result.push('');
      }
      result.push('');
    }
    
    return result;
  }
  
  // Filter out empty strings completely to prevent showing empty select boxes
  // API may return arrays like ["", "Signed", ""] - we only want to show ["Signed"]
  const result = nonNAValues.filter(v => v && v.trim().length > 0);
  
  return result;
}

  // Check if attribute should display as array (only if is_array flag is explicitly true)
  isArrayValue(attributeId: number): boolean {
    // If we're currently editing this attribute as an array, treat it as array
    if (this.editingAttributeId === attributeId && this.editingArrayIndex !== null) {
      return true;
    }
    
    // Check if this attribute is marked as array - MUST be explicitly true
    const attribute = this.attributes.find(a => a.id === attributeId);
    
    // Only treat as array if is_array is explicitly set to true
    // Do NOT treat as array just because value contains commas or looks like JSON array
    if (attribute?.is_array === true) {
      return true;
    }
    
    // For non-array attributes, NEVER display as value boxes
    // This prevents commas in text from creating multiple boxes
    return false;
  }

  // Add a new empty value box to an attribute (for array values)
  addNewValueBox(attributeId: number): void {
    // Exit any current edit mode first
    if (this.editingAttributeId !== null) {
      this.editingAttributeId = null;
      this.editingArrayIndex = null;
      this.editingTruthValueText = '';
    }
    
    // Get current value (will include any pending changes)
    const currentValue = this.getCurrentTruthValue(attributeId);
    
    // Convert to array - filter out empty strings and N/A values for clean state
    let currentArray = Array.isArray(currentValue)
      ? [...currentValue]
      : (typeof currentValue === 'string' && currentValue.trim() ? [currentValue.trim()] : []);
    
    // Remove N/A values AND empty strings for consistent array handling
    const cleanedArray = currentArray.map(v => String(v).trim()).filter(v => {
      const lower = v.toLowerCase();
      return v.length > 0 && !(lower === 'n/a' || lower === 'na');
    });
    
    // New entry will be at the end of the cleaned array
    const newIndex = cleanedArray.length;
    
    // Don't save empty string to modifiedValues - just track that we're editing
    // The value will be saved when user actually selects something
    // This prevents storing arrays with trailing empty strings
    
    // Start editing the new value (without modifying the stored array yet)
    this.editingAttributeId = attributeId;
    this.editingArrayIndex = newIndex;
    this.editingTruthValueText = '';
    
    // Get field type and metadata directly from attribute
    const attribute = this.attributes.find(attr => attr.id === attributeId);
    
    if (attribute) {
      const fieldType = this.getFieldType(attributeId);
      this.editingFieldType = fieldType;
      this.editingAllowedValues = this.getFieldAllowedValues(attributeId);
      this.editingFieldMetadata = {
        feature: attribute.feature,
        allowed_values: attribute.allowed_values || []
      };
    } else {
      // Fallback to old logic if attribute not found
      this.applyLegacyFieldTypeLogic(attribute);
    }
    
    this.cdr.detectChanges();
    
    // Auto-focus the input field after render
    this.focusEditingInput();
    }

  // Undo changes for a specific attribute field
  // arrayIndex: the specific field instance to undo (0 = first field, 1 = second, etc.)
  // For array attributes: removes added fields or clears original field values
  // For non-array attributes: restores to original value
  undoChange(attributeId: number, arrayIndex: number | null = null, event?: MouseEvent): void {
    // Prevent any default behavior and stop propagation
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const attribute = this.attributes.find(a => a.id === attributeId);
    const isArrayAttribute = attribute?.is_array === true;
    
    // Handle non-array attributes: simple restore to original
    if (!isArrayAttribute) {
      // Check if this was an auto-populated field that was edited
      if (this.autoPopulatedOriginalValues.has(attributeId)) {
        // Restore the auto-populated value
        const autoPopulatedValue = this.autoPopulatedOriginalValues.get(attributeId)!;
        this.modifiedValues.set(attributeId, autoPopulatedValue);
        this.modifiedAttributeIds.add(attributeId);
        this.autoPopulatedAttributeIds.add(attributeId); // Re-add to auto-populated set
        } else {
        // Restore to database value (remove modifications)
        this.modifiedValues.delete(attributeId);
        this.modifiedAttributeIds.delete(attributeId);
      }
      
      if (this.editingAttributeId === attributeId) {
        this.editingAttributeId = null;
        this.editingArrayIndex = null;
        this.editingTruthValueText = '';
        this.editingFieldType = 'text';
        this.editingFieldMetadata = null;
        this.editingAllowedValues = [];
      }
      
      this.cdr.detectChanges();
      return;
    }
    
    // Handle array attributes with field-specific undo logic
    // Check for auto-populated value first, then fall back to database value
    const autoPopValue = this.autoPopulatedOriginalValues.get(attributeId);
    const dbValue = this.currentFileTruthValues.get(attributeId)?.value;
    const originalValue = autoPopValue !== undefined ? autoPopValue : dbValue;
    
    // Convert to arrays - PRESERVE empty strings to maintain indices
    const originalArray = Array.isArray(originalValue)
      ? originalValue.map(v => String(v).trim())
      : (typeof originalValue === 'string' && originalValue.trim() ? [originalValue.trim()] : []);
    const currentArray = this.getValueAsArray(attributeId);
    
    // If no specific index provided, restore entire attribute to original
    if (arrayIndex === null) {
      this.modifiedValues.delete(attributeId);
      this.modifiedAttributeIds.delete(attributeId);
      
      if (this.editingAttributeId === attributeId) {
        this.editingAttributeId = null;
        this.editingArrayIndex = null;
        this.editingTruthValueText = '';
        this.editingFieldType = 'text';
        this.editingFieldMetadata = null;
        this.editingAllowedValues = [];
      }
      
      this.cdr.detectChanges();
      return;
    }
    
    // Field-specific undo logic
    const newArray = [...currentArray];
    
    if (arrayIndex < originalArray.length) {
      // CASE 1: This is an ORIGINAL field (existed in DB)
      // Restore it to its original value, don't remove the field
      newArray[arrayIndex] = originalArray[arrayIndex];
      // Check if we've restored everything to original state.
      // Clone before sort — .sort() mutates in place, and newArray must stay in
      // insertion order so sibling indices aren't scrambled when persisted.
      const isRestored = JSON.stringify([...newArray].sort()) === JSON.stringify([...originalArray].sort());
      if (isRestored) {
        // Check if this is an auto-populated value
        if (autoPopValue !== undefined) {
          // Keep as modified and auto-populated
          this.modifiedValues.set(attributeId, newArray);
          this.modifiedAttributeIds.add(attributeId);
          this.autoPopulatedAttributeIds.add(attributeId);
        } else {
          // Back to database value
          this.modifiedValues.delete(attributeId);
          this.modifiedAttributeIds.delete(attributeId);
        }
      } else {
        this.modifiedValues.set(attributeId, newArray);
        this.modifiedAttributeIds.add(attributeId);
      }
    } else {
      // CASE 2: This is an ADDED field (wasn't in DB)
      // Remove this specific field
      newArray.splice(arrayIndex, 1);
      // Update or clear modification tracking
      if (newArray.length === 0) {
        // All fields removed, back to empty
        const originalIsEmpty = originalArray.length === 0;
        if (originalIsEmpty) {
          this.modifiedValues.delete(attributeId);
          this.modifiedAttributeIds.delete(attributeId);
        } else {
          // Still different from original (original had values)
          this.modifiedValues.set(attributeId, []);
          this.modifiedAttributeIds.add(attributeId);
        }
      } else {
        // Clone before sort — same reason as above: preserve newArray's order.
        const isRestored = JSON.stringify([...newArray].sort()) === JSON.stringify([...originalArray].sort());
        if (isRestored) {
          // Check if this is an auto-populated value
          if (autoPopValue !== undefined) {
            // Keep as modified and auto-populated
            this.modifiedValues.set(attributeId, newArray);
            this.modifiedAttributeIds.add(attributeId);
            this.autoPopulatedAttributeIds.add(attributeId);
          } else {
            // Back to database value
            this.modifiedValues.delete(attributeId);
            this.modifiedAttributeIds.delete(attributeId);
          }
        } else {
          this.modifiedValues.set(attributeId, newArray);
          this.modifiedAttributeIds.add(attributeId);
        }
      }
    }
    
    // Exit edit mode if currently editing the field being undone
    if (this.editingAttributeId === attributeId && this.editingArrayIndex === arrayIndex) {
      this.editingAttributeId = null;
      this.editingArrayIndex = null;
      this.editingTruthValueText = '';
      this.editingFieldType = 'text';
      this.editingFieldMetadata = null;
      this.editingAllowedValues = [];
    }
    
    // Force change detection to update the UI immediately
    this.cdr.detectChanges();
    }

  // Check if there are any unsaved changes
  hasUnsavedChanges(): boolean {
    return this.modifiedAttributeIds.size > 0 || this.pendingSnipModifiedAttributeIds.size > 0;
  }

  // Get count of unsaved changes
  getUnsavedCount(): number {
    // Return total count of both auto-populated and manually edited fields,
    // plus attributes with a pending manual bounding-box snip (deduplicated).
    if (this.pendingSnipModifiedAttributeIds.size === 0) {
      return this.modifiedAttributeIds.size;
    }
    const union = new Set<number>(this.modifiedAttributeIds);
    this.pendingSnipModifiedAttributeIds.forEach(id => union.add(id));
    return union.size;
  }

  // Get count of auto-populated fields only
  getAutoPopulatedCount(): number {
    return this.autoPopulatedAttributeIds.size;
  }

  // Check if a field is auto-populated (has not been manually edited after auto-population)
  isAutoPopulated(attributeId: number): boolean {
    return this.autoPopulatedAttributeIds.has(attributeId);
  }

  // Show corner indicator only for auto-filled values that still have a valid bbox.
  hasAutoFilledBoundingBoxIndicator(attributeId: number): boolean {
    if (!this.isAutoPopulated(attributeId)) {
      return false;
    }

    return this.getResolvedBoundingBoxForIndex(attributeId, 0) !== null;
  }

  // Array-safe variant that checks the specific value index.
  hasAutoFilledBoundingBoxIndicatorForIndex(attributeId: number, valueIndex: number): boolean {
    if (!this.isAutoPopulated(attributeId)) {
      return false;
    }

    return this.getResolvedBoundingBoxForIndex(attributeId, valueIndex) !== null;
  }

  // Check if a field was overridden by a second auto-fill
  isOverridden(attributeId: number): boolean {
    return this.overriddenAutoPopulatedAttributeIds.has(attributeId);
  }

  getTableCellBboxSet(attributeId: number): Set<string> {
    const sourceData = this.fieldSourceDataMap.get(attributeId);
    const result = new Set<string>();
    if (sourceData?.cellMetadata) {
      for (const cm of sourceData.cellMetadata) {
        result.add(`${cm.rowIndex}:${cm.columnName}`);
      }
    }
    return result;
  }

  getTablePendingSnipIndices(attributeId: number): Set<number> {
    const inner = this.pendingSnipBboxByAttributeId.get(attributeId);
    return inner ? new Set(inner.keys()) : new Set();
  }

  onTableSnipToggleClicked(attributeId: number, cell: { rowIndex: number; columnName: string; event: MouseEvent }): void {
    const attr = this.attributes.find(a => a.id === attributeId);
    const columns = attr?.tableColumns || [];
    const colIndex = columns.findIndex(c => c.name === cell.columnName);
    const flatIndex = cell.rowIndex * columns.length + (colIndex >= 0 ? colIndex : 0);
    this.onSnipToggleClick(attributeId, flatIndex, cell.event);
  }

  private getResolvedBoundingBoxForIndex(attributeId: number, valueIndex: number): number[] | null {
    const sourceData = this.fieldSourceDataMap.get(attributeId);
    if (!sourceData?.boundingBoxes?.length) {
      return null;
    }

    if (sourceData.boundingBoxIndices && sourceData.boundingBoxIndices.length > 0) {
      const originalIndex = valueIndex + 1;
      for (let uniqueIdx = 0; uniqueIdx < sourceData.boundingBoxIndices.length; uniqueIdx++) {
        if (sourceData.boundingBoxIndices[uniqueIdx].includes(originalIndex)) {
          const mappedBbox = sourceData.boundingBoxes[uniqueIdx];
          return this.isValidBoundingBox(mappedBbox) ? mappedBbox : null;
        }
      }
      return null;
    }

    const directBbox = sourceData.boundingBoxes[valueIndex];
    if (this.isValidBoundingBox(directBbox)) {
      return directBbox;
    }

    // Fallback: if there is a single valid bbox but no index mapping, reuse it for array entries.
    if (sourceData.boundingBoxes.length === 1 && this.isValidBoundingBox(sourceData.boundingBoxes[0])) {
      return sourceData.boundingBoxes[0];
    }

    return null;
  }

  private isValidBoundingBox(bbox: number[] | null | undefined): bbox is number[] {
    return Array.isArray(bbox) && bbox.length === 4 && bbox.every(coord => Number.isFinite(coord));
  }

  // Check if a field is manually edited (not from auto-population, or edited after auto-population)
  isManuallyEdited(attributeId: number): boolean {
    return this.modifiedAttributeIds.has(attributeId) && !this.autoPopulatedAttributeIds.has(attributeId);
  }

  // Prevent leading whitespace while typing in truth value text fields
  onTruthValueInputChange(value: string): void {
    const text = value ?? '';
    const sanitized = text.replace(/^\s+/, '');

    if (sanitized !== this.editingTruthValueText) {
      this.editingTruthValueText = sanitized;
    }
  }

  // Handle blur event on truth value textarea
  onTruthValueBlur(attributeId: number): void {
    // Guard: If currently saving, don't process blur - value was already committed
    if (this.isSavingTruthValue) {
      return;
    }
    
    // Guard: If not currently editing this attribute, the value was already
    // committed by saveAllChanges, so don't process again
    if (this.editingAttributeId !== attributeId) {
      return;
    }
    
    const trimmedValue = this.editingTruthValueText.trim();
    const existingValue = this.currentFileTruthValues.get(attributeId);
    const originalValue = this.getCurrentTruthValue(attributeId);
   
    // Convert to native array format
    const originalArray = Array.isArray(originalValue)
      ? [...originalValue].map(v => String(v).trim())
      : (originalValue !== undefined && originalValue !== null && String(originalValue).trim()
          ? [String(originalValue).trim()]
          : []);

    let newArray: string[];
    
    // If editing a specific array index, update only that index
    if (this.editingArrayIndex !== null) {
      newArray = [...originalArray];
      while (newArray.length <= this.editingArrayIndex) {
        newArray.push('');
      }
      
      // Remove entry from array if cleared (match dropdown behavior)
      if (trimmedValue === '') {
        newArray.splice(this.editingArrayIndex, 1);
      } else {
        newArray[this.editingArrayIndex] = trimmedValue;
      }
    } else {
      newArray = [trimmedValue];
    }

    // No-op guard: if user only clicked into a field and blurred without changing
    // the effective displayed value, keep existing auto-fill indicators/state.
    // Blank entries are filtered on both sides so "no value" never compares unequal to
    // another "no value" (e.g. blurring a never-filled field without typing anything).
    const displayedBaseline = originalArray.map(v => String(v).trim()).filter(v => v !== '');
    const candidateValues = newArray.map(v => String(v).trim()).filter(v => v !== '');
    const isDateField = this.getFieldType(attributeId) === 'date';

    const isNoOpAgainstDisplayedValue = isDateField
      ? JSON.stringify(candidateValues.map(v => this.normalizeDateForComparison(v).toLowerCase()).sort())
        === JSON.stringify(displayedBaseline.map(v => this.normalizeDateForComparison(v).toLowerCase()).sort())
      : JSON.stringify(candidateValues.map(v => v.toLowerCase()).sort())
        === JSON.stringify(displayedBaseline.map(v => v.toLowerCase()).sort());

    if (isNoOpAgainstDisplayedValue) {
      this.editingAttributeId = null;
      this.editingArrayIndex = null;
      this.editingTruthValueText = '';
      this.cdr.detectChanges();
      return;
    }
    
    // Get the original stored value to compare for actual changes
    const storedOriginalValue = existingValue?.value;
    const storedArray = Array.isArray(storedOriginalValue)
      ? storedOriginalValue.map(v => String(v).trim().toLowerCase())
      : (storedOriginalValue !== undefined && storedOriginalValue !== null
          ? [String(storedOriginalValue).trim().toLowerCase()]
          : []);

    // Guard: user opened an N/A field but typed nothing → restore N/A state.
    const isStoredNA = storedArray.length > 0 && storedArray.every(p => p === 'n/a' || p === 'na');
    if (trimmedValue === '' && isStoredNA) {
      this.notApplicableAttributes.add(attributeId);
      this.modifiedValues.delete(attributeId);
      this.modifiedAttributeIds.delete(attributeId);
      this.editingAttributeId = null;
      this.editingArrayIndex = null;
      this.editingTruthValueText = '';
      // Status dot will reflect backend data - no local update needed
      this.cdr.detectChanges();
      return;
    }

    // Compare arrays for changes (date fields use normalized date comparison), ignoring blank
    // entries on both sides so a blank commit against a never-filled field isn't a "change".
    let arrayChanged: boolean;
    if (this.getFieldType(attributeId) === 'date') {
      const normalizedNewArray = newArray.map(v => this.normalizeDateForComparison(String(v).trim()).toLowerCase()).filter(v => v !== '');
      const normalizedStoredArray = storedArray.map(v => this.normalizeDateForComparison(String(v).trim()).toLowerCase()).filter(v => v !== '');
      arrayChanged = JSON.stringify(normalizedNewArray.sort()) !== JSON.stringify(normalizedStoredArray.sort());
    } else {
      const newArrayLower = newArray.map(v => v.toLowerCase()).filter(v => v !== '');
      const storedArrayNonBlank = storedArray.filter(v => v !== '');
      arrayChanged = JSON.stringify(newArrayLower.sort()) !== JSON.stringify(storedArrayNonBlank.sort());
    }
    
    // Only track as modified if the value actually changed
    if (arrayChanged) {
      // Update modifiedValues with native array format
      this.modifiedValues.set(attributeId, newArray);
      this.modifiedAttributeIds.add(attributeId);
      
      // Remove blue dot when user manually edits an auto-populated field
      this.autoPopulatedAttributeIds.delete(attributeId);

      if (this.selectedFile?.id !== undefined && this.selectedFile?.id !== null) {
        this.clearAutoFillCompletedForFile(this.selectedFile.id);
      }
      
      // Auto-enable field (remove from N/A) when user enters a value
      if (newArray.length > 0 && !newArray.every(v => v === '')) {
        this.notApplicableAttributes.delete(attributeId);
      }
    } else {
      // Value didn't change - remove from modified tracking (cleanup false positives)
      this.modifiedAttributeIds.delete(attributeId);
      this.modifiedValues.delete(attributeId);
    }
    
    // Exit edit mode
    this.editingAttributeId = null;
    this.editingArrayIndex = null;
    this.editingTruthValueText = '';
    
    // Status dot will reflect backend data after save - no local update needed
    
    this.cdr.detectChanges();
  }

  // Handle immediate change event (for dropdowns/selects)
  onTruthValueChange(attributeId: number): void {
    // Guard: If not currently editing this attribute, skip processing
    if (this.editingAttributeId !== attributeId) {
      return;
    }
    
    const trimmedValue = this.editingTruthValueText.trim();
    const existingValue = this.currentFileTruthValues.get(attributeId);
    const originalValue = this.getCurrentTruthValue(attributeId);
    
    // Convert to native array format
    const originalArray = Array.isArray(originalValue)
      ? [...originalValue].map(v => String(v).trim())
      : (originalValue !== undefined && originalValue !== null && String(originalValue).trim()
          ? [String(originalValue).trim()]
          : []);

    let newArray: string[];
    
    // If editing a specific array index, update only that index
    if (this.editingArrayIndex !== null) {
      newArray = [...originalArray];
      while (newArray.length <= this.editingArrayIndex) {
        newArray.push('');
      }
      
      // User selected a value, update the array at this index
      newArray[this.editingArrayIndex] = trimmedValue;
    } else {
      newArray = [trimmedValue];
    }
    
    // Check if value has changed from original stored value
    const storedOriginal = existingValue?.value;
    const storedArray = Array.isArray(storedOriginal)
      ? storedOriginal.map(v => String(v).trim())
      : (storedOriginal !== undefined && storedOriginal !== null
          ? [String(storedOriginal).trim()]
          : []);
    
    const arrayChanged = JSON.stringify(newArray.sort()) !== JSON.stringify(storedArray.sort());
    if (arrayChanged) {
      this.modifiedValues.set(attributeId, newArray);
      this.modifiedAttributeIds.add(attributeId);
      this.autoPopulatedAttributeIds.delete(attributeId);

      if (this.selectedFile?.id !== undefined && this.selectedFile?.id !== null) {
        this.clearAutoFillCompletedForFile(this.selectedFile.id);
      }
      
      // AUTO-MARK AS N/A when user clears an existing value
      if (newArray.length === 0 && storedArray.length > 0) {
        this.notApplicableAttributes.add(attributeId);
      }
    } else {
      this.modifiedValues.delete(attributeId);
      this.modifiedAttributeIds.delete(attributeId);
      // Also remove from N/A if value matches original (user restored it)
      this.notApplicableAttributes.delete(attributeId);
    }
  
    // Immediately exit edit mode for dropdowns
    this.editingAttributeId = null;
    this.editingArrayIndex = null;
    this.editingTruthValueText = '';
    this.cdr.detectChanges();
  }

  // Save all modified values
  saveAllChanges(): void {
    // Commit any table cell mid-edit first - the Save Changes button suppresses blur
    // via mousedown preventDefault, so a cell being typed into never fires saveCell() otherwise
    this.tableRowInputs?.forEach(tableInput => tableInput.commitActiveEdit());

    // If currently editing, commit the value first by directly processing it here
    // (more reliable than calling onTruthValueBlur which may have timing issues)
    if (this.editingAttributeId !== null && this.editingTruthValueText !== undefined) {
      const attributeId = this.editingAttributeId;
      let trimmedValue = (this.editingTruthValueText || '').trim();
      
      // For select fields, read actual DOM value to handle case where user opened dropdown but didn't select
      if (this.editingFieldType === 'select') {
        const selectElement = document.querySelector('.truth-value-edit-input.select-input') as HTMLSelectElement;
        if (selectElement) {
          const actualValue = selectElement.value;
          // Use actual DOM value (empty if "-- Select --" is shown)
          trimmedValue = actualValue === '-- Select --' ? '' : actualValue;
        }
      }
      
      // Validate date field before saving
      if (this.editingFieldType === 'date' && trimmedValue) {
        const normalizedDate = this.normalizeDateToMMDDYYYY(trimmedValue);
        if (!this.isValidDateFormat(normalizedDate)) {
          this.notificationService.showSaveError(this.truthManagementService.getDateValidationErrorMessage(normalizedDate));
          // Refocus the input after alert so user can continue editing
          this.isRefocusingAfterError = true;
          setTimeout(() => {
            const input = document.querySelector('.date-input-wrapper input') as HTMLInputElement;
            if (input) {
              input.focus();
              input.setSelectionRange(input.value.length, input.value.length);
            }
          }, 0);
          return; // Don't save invalid date
        }
      }
      
      const existingValue = this.currentFileTruthValues.get(attributeId);
      const originalValue = this.getCurrentTruthValue(attributeId);
      
      // Convert to native array format
      const originalArray = Array.isArray(originalValue)
        ? [...originalValue].map(v => String(v).trim())
        : (typeof originalValue === 'string' && originalValue.trim() ? [originalValue.trim()] : []);

      let newArray: string[];
      
      // If editing a specific array index, update only that index
      if (this.editingArrayIndex !== null) {
        newArray = [...originalArray];
        while (newArray.length <= this.editingArrayIndex) {
          newArray.push('');
        }
        newArray[this.editingArrayIndex] = trimmedValue;
      } else {
        newArray = [trimmedValue];
      }
      
      // Update modifiedValues with the new value
      const storedOriginal = existingValue?.value;
      const storedArray = Array.isArray(storedOriginal)
        ? storedOriginal.map(v => String(v).trim().toLowerCase())
        : (typeof storedOriginal === 'string' ? [storedOriginal.trim().toLowerCase()] : []);

      // N/A guard: user opened an N/A field without typing anything
      const isStoredNA = storedArray.length > 0 && storedArray.every(p => p === 'n/a' || p === 'na');
      if (newArray.length === 0 && isStoredNA) {
        this.notApplicableAttributes.add(attributeId);
        this.modifiedValues.delete(attributeId);
        this.modifiedAttributeIds.delete(attributeId);
      } else {
        // Compare arrays, ignoring blank entries on both sides so committing a blank edit on a
        // field that never had a value (newArray = [''], storedArray = []) isn't seen as a change.
        const newArrayLower = newArray.map(v => v.toLowerCase()).filter(v => v !== '');
        const storedArrayNonBlank = storedArray.filter(v => v !== '');
        const arrayChanged = JSON.stringify(newArrayLower.sort()) !== JSON.stringify(storedArrayNonBlank.sort());

        if (arrayChanged) {
          this.modifiedValues.set(attributeId, newArray);
          this.modifiedAttributeIds.add(attributeId);
        } else {
          this.modifiedValues.delete(attributeId);
          this.modifiedAttributeIds.delete(attributeId);
        }
      }

      // Clear editing state AFTER processing
      this.editingAttributeId = null;
      this.editingArrayIndex = null;
      this.editingTruthValueText = '';
    }

    if (this.modifiedAttributeIds.size === 0 && this.pendingSnipModifiedAttributeIds.size === 0) {
      return;
    }

    if (!this.selectedFile || !this.selectedDocument) {
      this.notificationService.showError('Please select a file to save changes.');
      return;
    }

    // Proceed with save confirmation (single dialog)
    this.proceedWithSave();
  }
/**
 * Extracted save logic - shows save confirmation and executes save
 */
private proceedWithSave(): void {
  // Show save confirmation modal (Scenario 6)
  this.modalService.showSaveConfirmation(
    () => {
      if (this.selectedFile) {
        this.clearAutoFillCompletedForFile(this.selectedFile.id);
      }
      this.executeSave();
    },
    // User clicked "No" - discard changes
    () => {
      // Clear pending auto-fill results if this was an auto-filled file
      if (this.selectedFile) {
        this.clearPendingExtractionResults(this.selectedFile.id);
        this.clearAutoFillCompletedForFile(this.selectedFile.id);
      }
      // Clear unsaved changes state
      this.modifiedValues.clear();
      this.modifiedAttributeIds.clear();
      this.autoPopulatedAttributeIds.clear();
      this.autoPopulatedOriginalValues.clear();
      this.overriddenAutoPopulatedAttributeIds.clear();
      this.fieldSourceDataMap.clear();
      // Discard any pending (not-yet-saved) snip edits.
      this.pendingSnipBboxByAttributeId.clear();
      this.pendingSnipPreviousSourceDataByAttributeId.clear();
      this.pendingSnipModifiedAttributeIds.clear();
      this.clearBoundingBoxHighlight();
      this.cdr.detectChanges();
    }
  );
}
  /**
   * Execute save operation - REFACTORED to return Promise<boolean>
   * 
   * @param showSuccessModal - Whether to show "Document Saved" modal on success
   * @returns Promise<boolean> - true if save succeeded, false if failed
   */
  private executeSave(showSuccessModal: boolean = true): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      // Guard: Prevent double-execution (production-grade protection)
      if (this.isSavingTruthValue) {
        resolve(false);
        return;
      }
      
      if (!this.selectedFile || !this.selectedDocument) {
        resolve(false);
        return;
      }

    this.isSavingTruthValue = true;

    // Collect all attributes to update in bulk (includes bounding box data - User Story 7126)
    const bulkAttributes: { 
      attribute: string; 
      feature?: string;
      data_type?: string;
      value: string | string[];  // Can be scalar or array (tables are JSON-stringified)
      page_number?: number | number[] | null;  // Single number for non-array fields, array for array fields
      bounding_box?: number[] | (number[] | null)[] | null;  // Single array for non-array fields, array of arrays/nulls for array fields
    }[] = [];

    // Helper function to determine bounding box data for an attribute (User Story 7126)
    // Returns null if value was modified from auto-filled state, otherwise returns stored bbox data
    // For array attributes, tracks which individual elements were modified to send null only for those
    const getBoundingBoxData = (attrId: number): { 
      page_number: number | null; 
      boundingBoxes: (number[] | null)[];  // Array where each element is either a bbox or null
    } => {
      const sourceData = this.fieldSourceDataMap.get(attrId);
      
      // If no source data or no bounding boxes available, return nulls
      if (!sourceData || !sourceData.boundingBoxes?.length) {
        return { page_number: null, boundingBoxes: [] };
      }
      
      // Get the current modified value
      const currentValue = this.modifiedValues.get(attrId);
      
      // If no current value in modifiedValues, not modified - return all bboxes
      // For array attributes with deduplicated bboxes, expand them back to full length
      if (!currentValue) {
        const attribute = this.attributes.find(a => a.id === attrId);
        const isArrayAttribute = attribute?.is_array === true;

        if (isArrayAttribute) {
          const pendingInner = this.pendingSnipBboxByAttributeId.get(attrId);
          const originalTv = this.currentFileTruthValues.get(attrId);
          const originalBbox = originalTv?.bounding_box;
          const valueRaw = originalTv?.value;
          const arrayLen = Array.isArray(valueRaw) ? valueRaw.length : 1;

          const origBboxes: (number[] | null)[] = [];
          if (originalBbox && Array.isArray(originalBbox)) {
            const firstNonNull = (originalBbox as any[]).find((b: any) => b != null);
            if (firstNonNull && Array.isArray(firstNonNull)) {
              for (const bbox of originalBbox as any[]) {
                origBboxes.push(Array.isArray(bbox) && bbox.length >= 4 ? bbox : null);
              }
            } else if (typeof originalBbox[0] === 'number' && originalBbox.length >= 4) {
              origBboxes.push(originalBbox as number[]);
            }
          }

          const bboxes: (number[] | null)[] = [];
          for (let i = 0; i < arrayLen; i++) {
            const pending = pendingInner?.get(i);
            bboxes.push(pending ? [...pending.bbox] : (origBboxes[i] ?? null));
          }
          return {
            page_number: sourceData.pageNumber ?? null,
            boundingBoxes: bboxes
          };
        }

        return {
          page_number: sourceData.pageNumber ?? null,
          boundingBoxes: sourceData.boundingBoxes
        };
      }
      
      // Determine the BASELINE value that has bounding boxes:
      // 1. Primary: autoPopulatedOriginalValues - the value that came with bboxes from autofill
      // 2. Fallback: currentFileTruthValues - the saved value (after autopopulated values are cleared on save)
      const baselineValue = this.autoPopulatedOriginalValues.get(attrId) || 
                           this.currentFileTruthValues.get(attrId)?.value || 
                           '';
      
      // Get the attribute to check if it's an array
      const attribute = this.attributes.find(a => a.id === attrId);
      const isArrayAttribute = attribute?.is_array === true;
      
      // For ARRAY attributes: check each element individually
      if (isArrayAttribute) {
        // Convert to arrays if needed
        const baselineValues = Array.isArray(baselineValue)
          ? baselineValue.map(v => String(v).trim())
          : (baselineValue ? [String(baselineValue).trim()] : []);
        const currentValues = Array.isArray(currentValue)
          ? currentValue.map(v => String(v).trim())
          : (currentValue ? [String(currentValue).trim()] : []);
        
        const originalTv = this.currentFileTruthValues.get(attrId);
        const originalBbox = originalTv?.bounding_box;
        const origBboxes: (number[] | null)[] = [];
        if (originalBbox && Array.isArray(originalBbox)) {
          const firstNonNull = (originalBbox as any[]).find((b: any) => b != null);
          if (firstNonNull && Array.isArray(firstNonNull)) {
            for (const bbox of originalBbox as any[]) {
              origBboxes.push(Array.isArray(bbox) && bbox.length >= 4 ? bbox : null);
            }
          } else if (typeof originalBbox[0] === 'number' && originalBbox.length >= 4) {
            origBboxes.push(originalBbox as number[]);
          }
        }

        const bboxResults: (number[] | null)[] = [];
        const pendingInner = this.pendingSnipBboxByAttributeId.get(attrId);
        for (let i = 0; i < currentValues.length; i++) {
          const pendingAtI = pendingInner?.get(i);
          bboxResults.push(pendingAtI ? [...pendingAtI.bbox] : (origBboxes[i] ?? null));
        }

        return {
          page_number: sourceData.pageNumber ?? null,
          boundingBoxes: bboxResults
        };
      }else {
        // For NON-ARRAY attributes: check if the single value was modified
        const baselineStr = Array.isArray(baselineValue) ? (baselineValue[0] || '') : String(baselineValue || '');
        const currentStr = Array.isArray(currentValue) ? (currentValue[0] || '') : String(currentValue || '');
        const valueWasModified = currentStr !== baselineStr;

        if (valueWasModified) {
          // Edited value plus a fresh manual bbox snip? Carry the snip, not null.
          // Non-array uses the inner map's single index=0 entry.
          const pendingAt0 = this.pendingSnipBboxByAttributeId.get(attrId)?.get(0);
          if (pendingAt0) {
            return { page_number: pendingAt0.pageNumber, boundingBoxes: [pendingAt0.bbox] };
          }
          return { page_number: null, boundingBoxes: [null] };
        }

        // Unmodified: return the bounding box
        return {
          page_number: sourceData.pageNumber ?? null,
          boundingBoxes: sourceData.boundingBoxes
        };
      }
    };

    // Helper function to build per-cell bounding box arrays for a table field attribute (User Story 7126)
    // Cell order is canonical: for each row (0..rowCount-1), for each column in attribute.tableColumns order.
    // This order must match the one used to reconstruct cellMetadata on load (processTruthValues), since the
    // backend stores/returns bounding_box/page_number as plain aligned arrays with no row/column labels.
    const getTableCellBoundingBoxData = (attrId: number, attr: Attribute): {
      page_number: number | null;
      boundingBoxes: (number[] | null)[];
    } => {
      const sourceData = this.fieldSourceDataMap.get(attrId);
      const columnNames = (attr.tableColumns || []).map(col => col.name);
      const pendingInner = this.pendingSnipBboxByAttributeId.get(attrId);
      const originalSourceData = this.pendingSnipPreviousSourceDataByAttributeId.get(attrId);
      const hasCellMeta = sourceData?.cellMetadata?.length || originalSourceData?.cellMetadata?.length;

      if (!hasCellMeta && !pendingInner?.size) {
        return { page_number: null, boundingBoxes: [] };
      }
      if (columnNames.length === 0) {
        return { page_number: null, boundingBoxes: [] };
      }

      let currentValue: string | string[] | TableData | undefined = this.modifiedValues.get(attrId);
      if (currentValue === undefined) {
        const raw = this.autoPopulatedOriginalValues.get(attrId)
          ?? this.currentFileTruthValues.get(attrId)?.value;
        if (typeof raw === 'string') {
          try { currentValue = JSON.parse(raw); } catch { /* leave undefined */ }
        } else {
          currentValue = raw;
        }
      }
      const currentTable: TableData = (currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue))
        ? currentValue as TableData
        : {};
      const baselineValueRaw = this.autoPopulatedOriginalValues.get(attrId) ?? this.currentFileTruthValues.get(attrId)?.value;
      const baselineTable: TableData = (baselineValueRaw && typeof baselineValueRaw === 'object' && !Array.isArray(baselineValueRaw))
        ? baselineValueRaw as TableData
        : {};

      const rowCount = this.getTableRowCount(currentTable, columnNames);
      const boundingBoxes: (number[] | null)[] = [];

      for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
        columnNames.forEach((columnName, colIndex) => {
          const flatIndex = rowIndex * columnNames.length + colIndex;

          const pendingSnip = pendingInner?.get(flatIndex);
          if (pendingSnip) {
            boundingBoxes.push([...pendingSnip.bbox]);
            return;
          }

          const currentCellValue = String((currentTable[columnName] || [])[rowIndex] ?? '').trim();
          const baselineCellValue = String((baselineTable[columnName] || [])[rowIndex] ?? '').trim();

          if (currentCellValue !== baselineCellValue) {
            boundingBoxes.push(null);
            return;
          }

          const cellMetaSource = originalSourceData || sourceData;
          const cellMeta = cellMetaSource?.cellMetadata?.find(
            cm => cm.rowIndex === rowIndex && cm.columnName === columnName
          );
          if (cellMeta) {
            boundingBoxes.push(cellMeta.bbox);
            return;
          }

          const origTv = this.currentFileTruthValues.get(attrId);
          const origBboxArr = origTv?.bounding_box;
          if (Array.isArray(origBboxArr) && origBboxArr[flatIndex] != null && Array.isArray(origBboxArr[flatIndex])) {
            boundingBoxes.push(origBboxArr[flatIndex] as number[]);
          } else {
            boundingBoxes.push(null);
          }
        });
      }

      const pageNum = sourceData?.pageNumber ?? originalSourceData?.pageNumber ?? pendingInner?.values().next().value?.pageNumber ?? null;
      return { page_number: pageNum, boundingBoxes };
    };

    // Process each modified attribute (includes attributes with a pending manual bbox snip
    // but no value edit, so the snip gets persisted inside the same bulk payload)
    const idsToSave = new Set<number>(this.modifiedAttributeIds);
    this.pendingSnipModifiedAttributeIds.forEach(id => idsToSave.add(id));
    idsToSave.forEach(attributeId => {
      const hasValueEdit = this.modifiedValues.has(attributeId);
      const newValue = hasValueEdit
        ? this.modifiedValues.get(attributeId)!
        : (this.currentFileTruthValues.get(attributeId)?.value ?? '');
      const existingValue = this.currentFileTruthValues.get(attributeId);

      // Get the attribute name/key for API calls
      const attribute = this.attributes.find(a => a.id === attributeId);
      if (!attribute) return; // Skip if attribute not found in schema
      
      const isArrayAttribute = attribute.is_array === true;
      const attributeName = attribute.attributeLabel;

      // Handle N/A (Not Applicable) based on toggle state
      if (this.notApplicableAttributes.has(attributeId)) {
        // Send "N/A" to backend for both array and scalar attributes
        bulkAttributes.push({ 
          attribute: attributeName, 
          value: 'N/A', 
          feature: attribute.feature || undefined,
          data_type: attribute.field_datatype || undefined,
          page_number: null, 
          bounding_box: null 
        });
        
        // Update local cache
        if (existingValue) {
          existingValue.value = 'N/A';
          this.currentFileTruthValues.set(attributeId, existingValue);
        } else {
          this.currentFileTruthValues.set(attributeId, {
            id: 0,
            attributeId: attributeId,
            attributeName: attributeName,
            value: 'N/A',
            documentId: this.selectedDocument!.id,
            fileId: this.selectedFile!.id,
            createdDate: new Date(),
            updatedDate: new Date()
          });
        }
        return;  // N/A handled, skip normal processing
      }

      // TABLE attributes: send per-cell aligned bounding_box/page_number arrays (User Story 7126)
      if (attribute.isTableField) {
        let tableValue = newValue;
        if (typeof tableValue === 'string') {
          try { tableValue = JSON.parse(tableValue); } catch { /* leave as-is, guard will catch */ }
        }
        if (typeof tableValue !== 'object' || Array.isArray(tableValue)) {
          return; // Guard against unexpected non-table value for a table field
        }

        const finalValue = JSON.stringify(tableValue);
        const cellBboxData = getTableCellBoundingBoxData(attributeId, attribute);
        const hasCells = cellBboxData.boundingBoxes.length > 0;
        const pageNumbers = hasCells
          ? cellBboxData.boundingBoxes.map(bbox => (bbox ? (cellBboxData.page_number ?? 0) : 0))
          : null;

        bulkAttributes.push({
          attribute: attributeName,
          value: finalValue,
          feature: attribute.feature || undefined,
          data_type: attribute.field_datatype || undefined,
          page_number: pageNumbers,
          bounding_box: hasCells ? cellBboxData.boundingBoxes : null
        });

        // Update local cache
        if (existingValue) {
          existingValue.value = finalValue;
          this.currentFileTruthValues.set(attributeId, existingValue);
        } else {
          this.currentFileTruthValues.set(attributeId, {
            id: 0,
            attributeId: attributeId,
            attributeName: attributeName,
            value: finalValue,
            documentId: this.selectedDocument!.id,
            fileId: this.selectedFile!.id,
            createdDate: new Date(),
            updatedDate: new Date()
          });
        }
        return;
      }

      // Get bbox data for this attribute
      const bboxData = getBoundingBoxData(attributeId);

      // ARRAY attributes: Send as single entry with array value (v2 API)
      if (isArrayAttribute) {
        // Skip table data for array attributes (shouldn't happen but guard against it)
        if (typeof newValue === 'object' && !Array.isArray(newValue)) {
          return;
        }
        
        // Convert to array if needed, filter out empty strings
        const arrayValue = Array.isArray(newValue) 
          ? newValue.map(v => String(v).trim()).filter(v => v.length > 0)
          : (String(newValue).trim() ? [String(newValue).trim()] : []);
        // For array attributes, page_number and bounding_box must be aligned arrays (one per entry)
        // UNIVERSAL RULE: For each entry - if bbox exists use page_number, else append 0
        const pageNumbers = arrayValue.map((_, idx) => {
          return bboxData.boundingBoxes[idx] ? (bboxData.page_number ?? 0) : 0;
        });

        // CRITICAL FIX: Extend bounding_box array to match arrayValue.length
        // For each entry: use existing bbox or append null for new entries without bbox
        const alignedBoundingBoxes = arrayValue.map((_, idx) => {
          return bboxData.boundingBoxes[idx] ?? null;
        });

        // Send single entry with array values and aligned bbox/page arrays to v2 API
        bulkAttributes.push({
          attribute: attributeName,
          value: arrayValue.length === 0 ? "[]" : arrayValue,  // Backend expects string "[]" for empty arrays
          feature: attribute.feature || undefined,
          data_type: attribute.field_datatype || undefined,
          page_number: arrayValue.length === 0 ? null : pageNumbers,  // null when all entries removed, else aligned array
          bounding_box: arrayValue.length === 0 ? null : alignedBoundingBoxes  // null when all entries removed, else aligned array
        });

        // Update local cache
        if (existingValue) {
          existingValue.value = arrayValue;
          this.currentFileTruthValues.set(attributeId, existingValue);
        } else {
          this.currentFileTruthValues.set(attributeId, {
            id: 0,
            attributeId: attributeId,
            attributeName: attributeName,
            value: arrayValue,
            documentId: this.selectedDocument!.id,
            fileId: this.selectedFile!.id,
            createdDate: new Date(),
            updatedDate: new Date()
          });
        }
      } else {
        // NON-ARRAY, non-table attributes: send as single string value (NEVER array)
        const finalValue: string = Array.isArray(newValue)
          ? (newValue[0] || '')  // Extract single value if somehow array
          : String(newValue || '');  // Ensure always string

        bulkAttributes.push({
          attribute: attributeName,
          value: finalValue,
          feature: attribute.feature || undefined,
          data_type: attribute.field_datatype || undefined,
          page_number: bboxData.page_number,  // Scalar, not array
          bounding_box: bboxData.boundingBoxes[0] ?? null  // Single bbox or null
        });

        // Update local cache
        if (existingValue) {
          existingValue.value = finalValue;
          this.currentFileTruthValues.set(attributeId, existingValue);
        } else {
          this.currentFileTruthValues.set(attributeId, {
            id: 0,
            attributeId: attributeId,
            attributeName: attributeName,
            value: finalValue,
            documentId: this.selectedDocument!.id,
            fileId: this.selectedFile!.id,
            createdDate: new Date(),
            updatedDate: new Date()
          });
        }
      }
    });

    // Use bulk API to save all changes at once
    if (bulkAttributes.length > 0) {
      // Send array values as-is - backend v2 API handles both arrays and strings
      const apiAttributes = bulkAttributes;
      this.truthManagementService.bulkUpdateAttributes(this.selectedFile!.id, apiAttributes).subscribe({
        next: (response) => {
          // Snip bbox was carried through the bulk API — clear all pending-snip state so
          // the Undo button vanishes. Next load from the attributes API returns the saved bbox.
          this.pendingSnipBboxByAttributeId.clear();
          this.pendingSnipPreviousSourceDataByAttributeId.clear();
          this.pendingSnipModifiedAttributeIds.clear();
          this.manuallyOverriddenAttributeIds.clear();
          // Hide the preview overlay now that the drawn bbox is persisted — the user can
          // click the attribute again to see the freshly-saved bbox from the API reload.
          this.clearBoundingBoxHighlight();

          // Clear modified tracking
          this.modifiedValues.clear();
          this.modifiedAttributeIds.clear();
          this.autoPopulatedAttributeIds.clear();
          this.autoPopulatedOriginalValues.clear();
          this.isSavingTruthValue = false;
          
          // Clear pending extraction results from sessionStorage now that data is saved to backend
          if (this.selectedFile) {
            const pendingKey = `truthManagement_pendingResults_${this.selectedFile.id}`;
            sessionStorage.removeItem(pendingKey);
            this.clearAutoFillCompletedForFile(this.selectedFile.id);
          }
          
          // Invalidate truth values cache for this file and re-fetch fresh data
          // loadTruthValuesForFile will update selectedFileAttributeSummary with backend counts
          if (this.selectedFile) {
            this.fileTruthValuesCache.delete(String(this.selectedFile.id));
            this.loadTruthValuesForFile(this.selectedFile.id);
            
            // 🔄 Cascade file status change to loan-validation for ground truth status recalculation
            const fileStatus = this.getFileStatus(this.selectedFile.id);
            if (fileStatus) {
              this.truthManagementService.notifyFileStatusChanged({
                documentModel: this.selectedDocument?.documentNumber || '',
                fileId: this.selectedFile.id,
                statusMetadata: fileStatus
              });
            }
          }

          // ✅ NOTIFY Loan Validation page to refresh dashboard
          this.truthManagementService.notifyDataChanged(this.selectedDocument?.documentNumber);
          
          this.cdr.detectChanges();
          
          // Show success toaster (not modal - follows UI pattern for validation runs)
          if (showSuccessModal) {
            this.notificationService.showSaveSuccess(
              this.selectedDocument?.documentNumber || '',
              this.selectedFile?.fileName
            );
          }

          resolve(true);
        },
        error: (error) => {
          this.notificationService.showSaveError('Technical reason');
          this.isSavingTruthValue = false;
          this.cdr.detectChanges();
          resolve(false);
        }
      });
    } else {
      // No attribute changes AND no pending snips — nothing to save, just clean up.
      this.modifiedValues.clear();
      this.modifiedAttributeIds.clear();
      this.autoPopulatedAttributeIds.clear();
      this.autoPopulatedOriginalValues.clear();
      this.isSavingTruthValue = false;
      this.cdr.detectChanges();
      resolve(true);
    }
    });
  }

  // PDF Viewer Methods
  private resetPdfViewerState(clearSource: boolean): void {
    this.pageRotations.clear();
    this.currentPage = 1;
    this.pageInput = 1;
    this.totalPages = 0;
    this.pdfReady = false;
    this.isRotatingPdf = false;
    this.pendingScrollRestore = null;
    this.pendingPageRestore = null;
    this.rotateScrollLockUntil = 0;
    this.suppressPageBindingDuringRotate = false;
    this.pendingPageAnchor = null;
    this.activeRotateEpoch = 0;
    this.rotateScrollLocked = false;

    // Remove scroll lock event handler if it exists
    if (this.rotateScrollHandler) {
      const viewer = this.getPdfViewerScrollContainer();
      if (viewer) {
        viewer.removeEventListener('scroll', this.rotateScrollHandler, true);
      }
      this.rotateScrollHandler = null;
    }

    if (this.rotateScrollRestoreFrame !== null) {
      cancelAnimationFrame(this.rotateScrollRestoreFrame);
      this.rotateScrollRestoreFrame = null;
    }

    if (clearSource) {
      this.pdfSrc = '';
    }

    this.lastLoadedPdfSource = '';
  }

  private getPdfViewerScrollContainer(): HTMLElement | null {
    // The CDK virtual-scroll viewport element inside app-pdf-lazy-viewer IS the
    // scroll container (scrollTop/scrollHeight/clientWidth all live on it).
    return this.lazyViewer?.scrollerElement ?? null;
  }

  private capturePdfScrollPosition(): PdfScrollAnchor | null {
    const viewer = this.getPdfViewerScrollContainer();
    if (!viewer) {
      return null;
    }

    const maxScrollLeft = Math.max(0, viewer.scrollWidth - viewer.clientWidth);
    const maxScrollTop = Math.max(0, viewer.scrollHeight - viewer.clientHeight);

    return {
      left: viewer.scrollLeft,
      top: viewer.scrollTop,
      ratioX: maxScrollLeft > 0 ? viewer.scrollLeft / maxScrollLeft : 0,
      ratioY: maxScrollTop > 0 ? viewer.scrollTop / maxScrollTop : 0
    };
  }

  private restorePdfScrollPosition(position: PdfScrollAnchor): void {
    const viewer = this.getPdfViewerScrollContainer();
    if (!viewer) {
      return;
    }

    const maxScrollLeft = Math.max(0, viewer.scrollWidth - viewer.clientWidth);
    const maxScrollTop = Math.max(0, viewer.scrollHeight - viewer.clientHeight);

    const targetLeft = Number.isFinite(position.ratioX)
      ? position.ratioX * maxScrollLeft
      : position.left;
    const targetTop = Number.isFinite(position.ratioY)
      ? position.ratioY * maxScrollTop
      : position.top;

    viewer.scrollLeft = Math.max(0, Math.min(maxScrollLeft, targetLeft));
    viewer.scrollTop = Math.max(0, Math.min(maxScrollTop, targetTop));
  }

  private schedulePdfScrollRestore(position: PdfScrollAnchor): void {
    this.pendingScrollRestore = position;

    // Multiple restores are intentional because PDF rerender is asynchronous in phases.
    setTimeout(() => this.restorePdfScrollPosition(position), 0);
    setTimeout(() => this.restorePdfScrollPosition(position), 80);
    setTimeout(() => this.restorePdfScrollPosition(position), 180);
    setTimeout(() => this.restorePdfScrollPosition(position), 320);
  }

  private maintainPdfScrollPosition(position: PdfScrollAnchor, durationMs: number = 1200): void {
    if (this.rotateScrollRestoreFrame !== null) {
      cancelAnimationFrame(this.rotateScrollRestoreFrame);
      this.rotateScrollRestoreFrame = null;
    }

    const start = Date.now();
    const restoreStep = () => {
      this.restorePdfScrollPosition(position);

      if (Date.now() - start < durationMs) {
        this.rotateScrollRestoreFrame = requestAnimationFrame(restoreStep);
      } else {
        this.rotateScrollRestoreFrame = null;
      }
    };

    restoreStep();
  }

  private releaseRotateGuards(): void {
    this.rotateScrollLocked = false;

    if (this.rotateScrollHandler) {
      const viewer = this.getPdfViewerScrollContainer();
      if (viewer) {
        viewer.removeEventListener('scroll', this.rotateScrollHandler, true);
      }
      this.rotateScrollHandler = null;
    }

    if (this.rotateScrollRestoreFrame !== null) {
      cancelAnimationFrame(this.rotateScrollRestoreFrame);
      this.rotateScrollRestoreFrame = null;
    }

    this.isRotatingPdf = false;
    this.pendingPageRestore = null;
    this.pendingScrollRestore = null;
    this.pendingPageAnchor = null;
    this.suppressPageBindingDuringRotate = false;
    this.activeRotateEpoch = 0;
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.pageInput = this.currentPage;
      this.scrollToPage(this.currentPage);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.pageInput = this.currentPage;
      this.scrollToPage(this.currentPage);
    }
  }

  goToPage(): void {
    // Convert to number and validate
    const pageNum = parseInt(String(this.pageInput), 10);
    
    // Check if it's a valid number
    if (isNaN(pageNum)) {
      this.pageInput = this.currentPage;
      return;
    }
    
    // Clamp to valid range
    if (pageNum < 1) {
      this.currentPage = 1;
    } else if (pageNum > this.totalPages) {
      this.currentPage = this.totalPages;
    } else {
      this.currentPage = pageNum;
    }
    
    // Sync input with actual page
    this.pageInput = this.currentPage;
    this.scrollToPage(this.currentPage);
  }

  /** The lazy viewer's scroll strategy reports which page sits under the top edge. */
  onLazyScrolledIndex(index: number): void {
    if (!this.totalPages) { return; }
    const page = Math.min(Math.max(0, index), this.totalPages - 1) + 1;
    if (page === this.currentPage) { return; }
    this.currentPage = page;
    // Don't clobber a number the user is typing in the page input.
    const active = document.activeElement as HTMLElement | null;
    const isTypingPage = !!active && active.tagName === 'INPUT' && active.closest('.viewer-header-inline') !== null;
    if (!isTypingPage) {
      this.pageInput = page;
    }
    this.cdr.markForCheck();
  }

  /**
   * A lazy-viewer page canvas finished painting.
   *  1. Size feedback — corrects the layout model if the real canvas size differs
   *     (pages with intrinsic /Rotate render landscape); setPageHeights is
   *     anchor-preserving so the correction never moves the user's viewport.
   *  2. Re-injects the highlighted attribute's bbox overlay when CDK recreates
   *     the page wrapper (templateCacheSize: 0), without recentering.
   */
  onLazyPageRendered(event: PdfPageRenderedEvent): void {
    if (this.isLoadingPdf) {
      this.isLoadingPdf = false;
      this.cdr.detectChanges();
    }

    const i = event.pageNum - 1;
    if (i >= 0 && i < this.pageHeightsPx.length) {
      if (Math.abs(event.heightPx - this.pageHeightsPx[i]) > 1
        || Math.abs(event.widthPx - this.pageWidthsPx[i]) > 1) {
        const heights = this.pageHeightsPx.slice();
        const widths = this.pageWidthsPx.slice();
        heights[i] = event.heightPx;
        widths[i] = event.widthPx;
        this.pageHeightsPx = heights;
        this.pageWidthsPx = widths;
        this.lazyViewer?.scrollDirective?.strategy.setPageHeights(heights, this.pageGapPx);
        this.cdr.markForCheck();
      }
    }

    if (this.highlightedAttributeId === null) { return; }
    const sourceData = this.fieldSourceDataMap.get(this.highlightedAttributeId);
    if (!sourceData || sourceData.pageNumber !== event.pageNum) { return; }
    const pageElement = this.getRenderedPageElement(event.pageNum);
    if (pageElement) {
      this.renderBoundingBoxOverlays(sourceData, false).catch(() => { /* retried on next render */ });
    }
  }

  /**
   * Build the lazy viewer's layout model: fit-to-width × zoom, from the
   * pre-cached base page dimensions (PDF points at scale 1).
   */
  computePdfPageLayout(): void {
    const total = this.totalPages;
    if (!total || !this.pdfDocument) { return; }

    this.pxPerRem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    this.pageGapPx = this.pageGapRem * this.pxPerRem;

    const scroller = this.lazyViewer?.scrollerElement;
    const containerWidth = scroller?.clientWidth || this.pdfContainer?.nativeElement?.clientWidth || 800;
    // Leave the same side breathing room the gap provides vertically.
    const availableWidth = Math.max(100, containerWidth - 2 * this.pageGapPx);

    const numbers: number[] = [];
    const widths: number[] = [];
    const heights: number[] = [];
    const scales: number[] = [];
    for (let n = 1; n <= total; n++) {
      // Unmeasured pages borrow page 1's size (exact for uniform documents),
      // then US Letter as the last resort; background refinement corrects them.
      const baseW = this.pdfPageWidths.get(n) ?? this.pdfPageWidths.get(1) ?? 612;
      const baseH = this.pdfPageHeights.get(n) ?? this.pdfPageHeights.get(1) ?? 792;
      const scale = (availableWidth / baseW) * this.zoom;
      numbers.push(n);
      scales.push(scale);
      widths.push(baseW * scale);
      heights.push(baseH * scale);
    }
    this.pageNumbers = numbers;
    this.pageScales = scales;
    this.pageWidthsPx = widths;
    this.pageHeightsPx = heights;

    // CDK caches its measured viewport size; re-measure once the layout lands
    // (covers attaching mid-transition) so the rendered range is computed from
    // the true visible height, not a stale/content-driven one.
    requestAnimationFrame(() => this.lazyViewer?.viewport?.checkViewportSize());
  }

  onPageInputKeydown(event: KeyboardEvent): void {
    // Allow: backspace, delete, tab, escape, enter, and numbers
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    
    if (allowedKeys.includes(event.key)) {
      if (event.key === 'Enter') {
        this.goToPage();
      }
      return;
    }
    
    // Block non-numeric characters
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  zoomIn(): void {
    if (this.zoom < 3.0 && this.applyZoomWithCenterAnchor(this.zoom + 0.25)) {
      // FIX BUG 3: Re-render bounding boxes after zoom change
      this.reRenderBoundingBoxesAfterZoom();
    }
  }

  zoomOut(): void {
    if (this.zoom > 0.25 && this.applyZoomWithCenterAnchor(this.zoom - 0.25)) {
      // FIX BUG 3: Re-render bounding boxes after zoom change
      this.reRenderBoundingBoxesAfterZoom();
    }
  }
  
  /**
   * PRODUCTION FIX: Re-render bounding boxes after zoom changes
   * Waits for PDF.js to finish re-rendering at new zoom level
   * Pages are left-aligned so offsetLeft is stable and predictable
   */
  private reRenderBoundingBoxesAfterZoom(): void {
    // Use common scheduling mechanism for consistency
    this.scheduleReRenderBoundingBoxes('zoom');
  }

  /**
   * Rotate current page by 90 degrees clockwise
   * Uses cumulative rotation to always rotate in same direction
   */
  rotatePdf(): void {
    if (!this.pdfSrc || !this.pdfReady) {
      return;
    }

    // Get current page's cumulative rotation (default to 0 if not set)
    const currentRotation = this.pageRotations.get(this.currentPage) || 0;
    
    // Always increment by 90° - never reset to 0°
    // This ensures CSS always animates clockwise: 0° → 90° → 180° → 270° → 360° → 450° ...
    const newRotation = currentRotation + 90;
    
    // Store cumulative rotation (don't use modulo). The lazy viewer's canvas
    // directive consumes it via [userRotation] and applies the CSS transform
    // (including the 90°/270° aspect-fit scale correction) — no manual DOM pass.
    this.pageRotations.set(this.currentPage, newRotation);
    this.cdr.detectChanges();
    
    // Re-render bounding boxes after rotation completes
    setTimeout(() => {
      if (this.highlightedAttributeId !== null) {
        const sourceData = this.fieldSourceDataMap.get(this.highlightedAttributeId);
        if (sourceData && sourceData.pageNumber === this.currentPage) {
          this.renderBoundingBoxOverlays(sourceData).catch(err => {
          });
        }
      }
    }, 350); // After CSS transition (300ms) + buffer
  }

  /**
   * Apply CSS transform rotation to a specific page container
   * @param pageNumber - Page number (1-indexed)
   * @param rotation - Cumulative rotation in degrees (0, 90, 180, 270, 360, 450, ...)
   * @param withTransition - Whether to animate the rotation (default: true)
   */
  private applyPageRotation(pageNumber: number, rotation: number, withTransition: boolean = true): void {
    if (!this.pdfContainer?.nativeElement) {
      return;
    }

    // Find the page container in DOM
    // ng2-pdf-viewer renders pages with class 'page' and data-page-number attribute
    const viewer = this.pdfContainer.nativeElement;
    const pageContainer = viewer.querySelector(`[data-page-number="${pageNumber}"]`) as HTMLElement;
    
    if (!pageContainer) {
      return;
    }

    // Calculate visual rotation (0-360 range) for aspect ratio calculations
    const visualRotation = rotation % 360;
    
    // Add or remove transition
    if (withTransition) {
      pageContainer.style.transition = 'transform 0.3s ease-in-out';
    } else {
      pageContainer.style.transition = 'none';
    }
    
    // Get the canvas element inside the page to determine dimensions
    const canvas = pageContainer.querySelector('canvas') as HTMLCanvasElement;
    
    let transformValue = `rotate(${rotation}deg)`;
    
    // For 90° and 270° visual rotations, apply scaling to fit within bounds
    // because the page dimensions are effectively swapped (width becomes height)
    if (canvas && (visualRotation === 90 || visualRotation === 270)) {
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      
      // Calculate aspect ratio to determine how much to scale
      if (canvasWidth > 0 && canvasHeight > 0) {
        const aspectRatio = canvasWidth / canvasHeight;
        
        // Scale factor to make rotated page fit within original bounds
        const scaleFactor = aspectRatio !== 1 ? Math.min(1 / aspectRatio, aspectRatio) : 1;
        
        transformValue = `rotate(${rotation}deg) scale(${scaleFactor})`;
      }
    }
    
    // Apply transform
    pageContainer.style.transform = transformValue;
    pageContainer.style.transformOrigin = 'center center';
  }

  /**
   * Apply all stored page rotations to visible pages
   * Called after PDF loads or page changes to reapply rotations
   */
  private applyAllPageRotations(): void {
    if (!this.pdfReady || this.pageRotations.size === 0) {
      return;
    }

    // Apply rotation to each page that has a stored rotation
    // Use withTransition=false to apply instantly (no animation when reapplying)
    this.pageRotations.forEach((rotation, pageNumber) => {
      this.applyPageRotation(pageNumber, rotation, false);
    });
  }

  getPdfPageBinding(): number | undefined {
    if (this.suppressPageBindingDuringRotate) {
      return undefined;
    }

    return this.pdfReady ? this.currentPage : 1;
  }
  /**
 * Get rotation for current page (defaults to 0 if not set)
 */
getCurrentPageRotation(): number {
  return this.pageRotations.get(this.currentPage) || 0;
}
  
  setZoomByPercentage(): void {
    // Convert to number and validate
    const zoomNum = parseInt(String(this.zoomPercentage), 10);
    
    // Check if it's a valid number
    if (isNaN(zoomNum)) {
      this.zoomPercentage = Math.round(this.zoom * 100);
      return;
    }
    
    // Clamp percentage between 50 and 300
    if (zoomNum < 50) {
      this.zoomPercentage = 50;
    } else if (zoomNum > 300) {
      this.zoomPercentage = 300;
    } else {
      this.zoomPercentage = zoomNum;
    }
    
    // Convert percentage to zoom decimal (e.g., 100% = 1.0, 50% = 0.5)
    if (this.applyZoomWithCenterAnchor(this.zoomPercentage / 100)) {
      // FIX BUG 3: Re-render bounding boxes if zoom actually changed
      this.reRenderBoundingBoxesAfterZoom();
    }
  }

  private applyZoomWithCenterAnchor(nextZoom: number): boolean {
    const viewer = this.getPdfViewerScrollContainer();
    const centerX = viewer ? viewer.clientWidth / 2 : 0;
    const centerY = viewer ? viewer.clientHeight / 2 : 0;
    return this.applyZoomWithAnchor(nextZoom, centerX, centerY);
  }

  private applyZoomWithAnchor(nextZoom: number, anchorOffsetX: number, anchorOffsetY: number): boolean {
    const clampedZoom = Math.min(3.0, Math.max(0.25, nextZoom));
    const oldZoom = this.zoom;
    if (Math.abs(clampedZoom - oldZoom) < 0.0001) {
      return false;
    }

    const viewer = this.getPdfViewerScrollContainer();
    const boundedOffsetX = viewer ? Math.min(Math.max(anchorOffsetX, 0), viewer.clientWidth) : 0;
    const boundedOffsetY = viewer ? Math.min(Math.max(anchorOffsetY, 0), viewer.clientHeight) : 0;
    const ratioX = viewer && viewer.scrollWidth > 0
      ? (viewer.scrollLeft + boundedOffsetX) / viewer.scrollWidth
      : 0;
    const ratioY = viewer && viewer.scrollHeight > 0
      ? (viewer.scrollTop + boundedOffsetY) / viewer.scrollHeight
      : 0;

    this.zoom = clampedZoom;
    this.zoomPercentage = Math.round(this.zoom * 100);
    // Rebuild the layout model at the new zoom — new pageScales redraw the
    // on-screen canvases; the strategy relayout is anchor-preserving.
    this.computePdfPageLayout();
    this.cdr.detectChanges();

    this.restorePdfZoomAnchor(ratioX, ratioY, boundedOffsetX, boundedOffsetY);
    return true;
  }

  private restorePdfZoomAnchor(ratioX: number, ratioY: number, anchorOffsetX: number, anchorOffsetY: number): void {
    const restore = () => {
      const viewer = this.getPdfViewerScrollContainer();
      if (!viewer) {
        return;
      }

      const nextLeft = (ratioX * viewer.scrollWidth) - anchorOffsetX;
      const nextTop = (ratioY * viewer.scrollHeight) - anchorOffsetY;
      const maxScrollLeft = Math.max(0, viewer.scrollWidth - viewer.clientWidth);
      const maxScrollTop = Math.max(0, viewer.scrollHeight - viewer.clientHeight);

      viewer.scrollLeft = Math.max(0, Math.min(maxScrollLeft, nextLeft));
      viewer.scrollTop = Math.max(0, Math.min(maxScrollTop, nextTop));
    };

    requestAnimationFrame(restore);
    setTimeout(restore, 40);
    setTimeout(restore, 120);
  }

  onZoomInputKeydown(event: KeyboardEvent): void {
    // Allow: backspace, delete, tab, escape, enter, and numbers
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    
    if (allowedKeys.includes(event.key)) {
      if (event.key === 'Enter') {
        this.setZoomByPercentage();
      }
      return;
    }
    
    // Block non-numeric characters
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onPdfLoadComplete(pdf: any): void {
    const loadedSource = this.pdfSrc;
    const isNewPdfSource = !!loadedSource && loadedSource !== this.lastLoadedPdfSource;
    const isActiveRotateEpoch = this.pendingPageAnchor && this.pendingPageAnchor.epoch === this.activeRotateEpoch;

    this.totalPages = pdf.numPages;
    // Document metadata is ready. The beat-loader is cleared as soon as the
    // first layout lands (below) — the lazy viewer's placeholders + per-page
    // spinners then provide the loading feedback, exactly like the source page.
    this.pdfReady = true;

    // Store PDF document reference (feeds the lazy viewer + page width lookups)
    this.pdfDocument = pdf;

    // Clear page dimension caches when loading new PDF
    if (isNewPdfSource) {
      this.pdfPageWidths.clear();
      this.pdfPageHeights.clear();

      if (this.serverPageSizes && this.serverPageSizes.length === this.totalPages) {
        for (let i = 0; i < this.serverPageSizes.length; i++) {
          this.pdfPageWidths.set(i + 1, this.serverPageSizes[i].width);
          this.pdfPageHeights.set(i + 1, this.serverPageSizes[i].height);
        }
        this.computePdfPageLayout();
        this.cdr.detectChanges();
        setTimeout(() => {
          if (this.isLoadingPdf && this.pdfSrc === loadedSource) {
            this.isLoadingPdf = false;
            this.cdr.detectChanges();
          }
        }, 8000);
      } else {
        this.computePdfPageLayout();
        this.cdr.detectChanges();
        setTimeout(() => {
          if (this.isLoadingPdf && this.pdfSrc === loadedSource) {
            this.isLoadingPdf = false;
            this.cdr.detectChanges();
          }
        }, 8000);

        pdf.getPage(1).then((page: any) => {
          const viewport = page.getViewport({ scale: 1.0 });
          this.pdfPageWidths.set(1, viewport.width);
          this.pdfPageHeights.set(1, viewport.height);
        }).catch(() => { /* layout keeps the 612x792 fallback */ }).then(() => {
          if (this.pdfSrc !== loadedSource) { return; }
          this.computePdfPageLayout();
          this.cdr.detectChanges();

          if (!this.lastLoadUsedRangeTransport) {
            this.refineAllPageDimensionsInBackground();
          }
        });
      }
    } else {
      this.computePdfPageLayout();
      this.cdr.detectChanges();
      const sameSrc = this.pdfSrc;
      setTimeout(() => {
        if (this.isLoadingPdf && this.pdfSrc === sameSrc) {
          this.isLoadingPdf = false;
          this.cdr.detectChanges();
        }
      }, 8000);
    }

    // Reset to first page only for newly loaded PDFs, not for rotate rerenders.
    if (isNewPdfSource) {
      setTimeout(() => {
        // Ignore stale completion when user has already switched source.
        if (this.pdfSrc !== loadedSource) {
          return;
        }

        this.currentPage = 1;
        this.pageInput = 1;
        this.cdr.detectChanges();
        // Page rotations are applied declaratively by the lazy viewer's canvas
        // directive via [userRotation] — no manual DOM pass needed anymore.
      }, 100);
    }

    // Phase 2: Restore page and scroll position during active rotate
    if (isActiveRotateEpoch && this.pendingPageAnchor) {
      const anchorPage = Math.min(Math.max(this.pendingPageAnchor.page, 1), this.totalPages || 1);
      this.currentPage = anchorPage;
      this.pageInput = anchorPage;
      this.cdr.detectChanges();

      // Restore scroll position with scroll lock active to prevent library from jumping it
      if (this.pendingScrollRestore) {
        const scrollPos = this.pendingScrollRestore;

        // Immediately restore scroll and keep lock active
        this.restorePdfScrollPosition(scrollPos);
        this.maintainPdfScrollPosition(scrollPos, 1500);
        
        // Heavy battery of restore passes to beat any internal reflows
        setTimeout(() => this.restorePdfScrollPosition(scrollPos), 0);
        setTimeout(() => this.restorePdfScrollPosition(scrollPos), 10);
        setTimeout(() => this.restorePdfScrollPosition(scrollPos), 30);
        setTimeout(() => this.restorePdfScrollPosition(scrollPos), 60);
        setTimeout(() => this.restorePdfScrollPosition(scrollPos), 100);
        setTimeout(() => this.restorePdfScrollPosition(scrollPos), 150);
        setTimeout(() => this.restorePdfScrollPosition(scrollPos), 250);
        setTimeout(() => this.restorePdfScrollPosition(scrollPos), 400);
        setTimeout(() => this.restorePdfScrollPosition(scrollPos), 600);
        setTimeout(() => this.restorePdfScrollPosition(scrollPos), 900);
        setTimeout(() => this.restorePdfScrollPosition(scrollPos), 1300);
        
        // Release scroll lock and guards after stabilization
        setTimeout(() => {
          this.releaseRotateGuards();
        }, 1500);
      } else {
        // No scroll to restore, release locks immediately
        setTimeout(() => {
          this.releaseRotateGuards();
        }, 300);
      }
    }

    this.lastLoadedPdfSource = loadedSource;
  }

  /**
   * Pre-cache page widths and heights for all pages in the loaded PDF
   * Eliminates race conditions when highlighting bounding boxes immediately after load
   */
  private async preCacheAllPageDimensions(): Promise<void> {
    if (!this.pdfDocument) {
      return;
    }

    const doc = this.pdfDocument;
    const seq = this.pdfDocLoadSeq;
    const numPages = doc.numPages;
    const BATCH = 4;

    for (let start = 1; start <= numPages; start += BATCH) {
      if (seq !== this.pdfDocLoadSeq || !this.pdfDocument) { return; }
      const end = Math.min(start + BATCH, numPages + 1);
      const batch: Promise<void>[] = [];
      for (let pageNum = start; pageNum < end; pageNum++) {
        batch.push(
          doc.getPage(pageNum)
            .then((page: any) => {
              const viewport = page.getViewport({ scale: 1.0 });
              this.pdfPageWidths.set(pageNum, viewport.width);
              this.pdfPageHeights.set(pageNum, viewport.height);
            })
            .catch(() => {})
        );
      }
      await Promise.all(batch);
      if (start + BATCH <= numPages) {
        await new Promise<void>(r => setTimeout(r, 0));
      }
    }
  }

  loadAttributeCountForFile(fileId: number | string): void {
    this.truthManagementService.getTruthValuesByFileId(fileId).subscribe({
      next: (response) => {
        // Extract attributes from AttributesResponse
        const truthValues = response.attributes;
        // Count unique attribute IDs for this file
        const uniqueAttributeIds = new Set(truthValues.map(tv => tv.attributeId));
        const count = uniqueAttributeIds.size;
        this.fileAttributeCounts.set(fileId, count);
        this.cdr.detectChanges();
      },
      error: () => {
        this.fileAttributeCounts.set(fileId, 0);
        this.cdr.detectChanges();
      }
    });
  }

  navigateToTruthValues(file: File): void {
    // Navigate to truth values page when arrow is clicked
    if (this.selectedDocument) {
      const docId = this.selectedDocument.id;
      const fileId = file.id;
      this.router.navigate(['/truth-management', docId, fileId]);
    }
  }

  navigateToTruthValuesForSelected(): void {
    // Navigate using the selected file from the header arrow button
    if (this.selectedFile && this.selectedDocument) {
      this.router.navigate(['/truth-management', this.selectedDocument.id, this.selectedFile.id]);
    }
  }

  onDocumentSelectWithView(document: Document, view: 'files' | 'attributes'): void {
    this.selectedDocument = document;
    this.selectedFile = null;
    this.truthValues = [];
    this.resetPdfViewerState(true);
    this.truthManagementService.setSelectedDocument(document);
    
    // Set view first
    this.setView(view);
    
    // Then load the appropriate data
    if (view === 'files') {
      this.loadFilesForDocument(document.id);
    } else {
      this.loadAttributesForDocument(document.id);
    }
  }

  loadFilesForDocument(documentId: number): void {
    // Load evaluation files from Swagger API
    if (this.selectedDocument) {
      this.loadEvaluationFiles(this.selectedDocument.documentNumber);
    } else {
      this.files = [];
      this.cdr.detectChanges();
    }
  }

  /**
   * Load evaluation files from Swagger API
   */
  loadEvaluationFiles(documentType?: string): void {
    this.loadingEvalFiles = true;
    
    // Pass documentType so API filters server-side (enables Redis cache)
    this.truthManagementService.getEvaluationFiles(documentType).subscribe({
      next: (allFiles: any[]) => {
        // API already filters by document_model; safety-check here
        let filteredFiles = allFiles;
        if (documentType) {
          const docTypeLower = documentType.toLowerCase();
          filteredFiles = allFiles.filter((ef: any) =>
            (ef.document_model || '').toLowerCase() === docTypeLower
          );
        }
        
        this.evaluationFiles = filteredFiles;
        
        // Convert to File format for backward compatibility
        // API uses 'uuid' as the file identifier, not 'id'
        this.files = filteredFiles.map((ef, index) => ({
          id: ef.uuid || ef.id || index + 1,
          fileName: ef.file_name,
          filePath: ef.file_path || ef.blob_url,
          documentId: this.selectedDocument?.id || 0,
          uploadedDate: new Date(ef.uploaded_at || ef.created_at || new Date())
        } as File));
        
        // Calculate file statuses from attribute_summary in refreshed files API response
       if (this.evaluationFiles.length > 0) {
          this.loadFileStatusesFromSummary();
        }
        
        this.loadingEvalFiles = false;
        this.cdr.detectChanges();

        // After loading files, check if there's a fileId in URL to restore selected file
        const fileId = this.route.snapshot.queryParams['fileId'];
        if (fileId && this.files.length > 0) {
          const fileToSelect = this.files.find(f => f.id.toString() === fileId || f.id === fileId);
          if (fileToSelect) {
            // Use setTimeout to ensure DOM is ready
            setTimeout(() => {
              this.selectFileOnly(fileToSelect);
            }, 100);
          }
        }
      },
      error: () => {
        this.loadingEvalFiles = false;
        this.files = [];
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Fallback to load files from local backend
   */
  loadLocalFiles(documentId: number): void {
    this.truthManagementService.getAllFiles(documentId).subscribe({
      next: (files) => {
        this.files = files;
        files.forEach(file => {
          this.loadAttributeCountForFile(file.id);
        });
        
        // Calculate file statuses from attribute_summary
        this.loadFileStatusesFromSummary();
        this.cdr.detectChanges();
      },
      error: (error) => {
      }
    });
  }

  loadTruthValuesForDocument(documentId: number): void {
    this.truthManagementService.getTruthValuesByDocumentId(documentId).subscribe(values => {
      this.truthValues = values;
    });
  }

  setView(view: 'files' | 'attributes'): void {
    this.selectedView = view;
    this.truthManagementService.setSelectedView(view);
  }

  getAttributeLabel(attributeId: number): string {
    const attribute = this.attributes.find(attr => attr.id === attributeId);
    return attribute ? attribute.attributeLabel : 'Unknown';
  }

  getDocumentDisplayName(documentNumber: string): string {
    // Extract the name part after the dash
    // Example: "DTSNAMSA - NAME-SIGNATURE AFFID" -> "NAME-SIGNATURE AFFID"
    if (!documentNumber) return '';
    const parts = documentNumber.split(' - ');
    return parts.length > 1 ? parts.slice(1).join(' - ') : documentNumber;
  }

  getFileCountForDocument(documentId: number): number {
    const count = this.documentFileCounts.get(documentId);
    
    // If count not loaded yet, trigger loading
    if (count === undefined) {
      setTimeout(() => this.loadCountsForDocument(documentId), 0);
      return 0;
    }
    return count;
  }

  getAttributeCountForDocument(documentId: number): number {
    const count = this.documentAttributeCounts.get(documentId);
    
    // If count not loaded yet, return 0
    if (count === undefined) {
      return 0;
    }
    return count;
  }

  getAttributeCountForFile(fileId: number): number {
    const count = this.fileAttributeCounts.get(fileId);
    
    // If count not loaded yet, trigger loading
    if (count === undefined) {
      setTimeout(() => this.loadAttributeCountForFile(fileId), 0);
      return 0;
    }
    return count;
  }

  getFileAttributeCount(fileId: number): string {
    const fileValues = this.truthValues.filter(tv => tv.fileId === fileId);
    const uniqueAttributeIds = new Set(fileValues.map(tv => tv.attributeId));
    const keyCount = uniqueAttributeIds.size;
    // Only count non-empty values
    const nonEmptyValueCount = fileValues.filter(tv => tv.value && toStringValue(tv.value) !== '').length;
    return `${keyCount}/${nonEmptyValueCount}`;
  }

  getTruthValueCounts(): string {
    // Show 0/0 if no truth values loaded yet
    if (!this.truthValues || this.truthValues.length === 0) {
      return '(0/0)';
    }
    
    // Count unique attribute keys (total keys)
    const uniqueAttributeIds = new Set(this.truthValues.map(tv => tv.attributeId));
    const keyCount = uniqueAttributeIds.size;
    
    // Count non-empty values (only count values that are not empty or whitespace)
    const nonEmptyValueCount = this.truthValues.filter(tv => 
      tv.value && tv.value.toString().trim() !== ''
    ).length;
    
    return `(${nonEmptyValueCount}/${keyCount})`;
  }

  openAddLoanDialog(): void {
    this.newLoanNumber = '';
    this.newLoanName = '';
    this.showAddLoanDialog = true;
  }

  closeAddLoanDialog(): void {
    this.showAddLoanDialog = false;
    this.newLoanNumber = '';
    this.newLoanName = '';
  }

  addLoan(): void {
    if (!this.newLoanNumber.trim() || !this.newLoanName.trim()) {
      alert('Please enter both loan number and loan name');
      return;
    }

    this.truthManagementService.addLoan(this.newLoanNumber, this.newLoanName);
    this.loadLoans();
    this.closeAddLoanDialog();
  }

  openAddDocumentDialog(): void {
    this.newDocumentNumber = '';
    this.newDocumentName = '';
    this.newDocumentDescription = '';
    this.showAddDocumentDialog = true;
  }

  closeAddDocumentDialog(): void {
    this.showAddDocumentDialog = false;
    this.newDocumentNumber = '';
    this.newDocumentName = '';
    this.newDocumentDescription = '';
  }

  addDocument(): void {
    if (!this.newDocumentNumber || !this.newDocumentNumber.trim()) {
      alert('Please enter document code');
      return;
    }

    if (!this.newDocumentName || !this.newDocumentName.trim()) {
      alert('Please enter document name');
      return;
    }

    // Default to loan ID 1 if no loan selected
    const loanId = 1;

    this.truthManagementService.addDocument(
      loanId, 
      this.newDocumentNumber, 
      this.newDocumentName,
      this.newDocumentDescription || undefined
    ).subscribe({
      next: (document) => {
        this.loadAllDocuments();
        this.closeAddDocumentDialog();
        setTimeout(() => {
          alert('Document added successfully!');
        }, 100);
      },
      error: (error) => {
        // Handle duplicate document error
        let errorMessage = 'Failed to add document';
        if (error.status === 400 && error.error?.detail) {
          errorMessage = error.error.detail;
        } else if (error.error?.detail) {
          errorMessage = error.error.detail;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        alert(`Error: ${errorMessage}`);
      }
    });
  }

  deleteDocument(document: Document): void {
    // Remove document from array immediately for instant UI update
    this.documents = this.documents.filter(d => d.id !== document.id);
    // Clear selection if deleted document was selected
    if (this.selectedDocument?.id === document.id) {
      this.selectedDocument = null;
      this.truthManagementService.setSelectedDocument(null);
      this.files = [];
      this.attributes = [];
    }
    // Delete from server
    this.truthManagementService.deleteDocument(document.id).subscribe({
      next: () => {
        // Reload from server to ensure consistency
        if (this.selectedLoan) {
          this.loadDocumentsForLoan(this.selectedLoan.id);
        }
      },
      error: (error) => {
        const errorMessage = error.error?.detail || error.message || 'Failed to delete document';
        alert(`Error: ${errorMessage}`);
        // Reload on error to restore the list
        if (this.selectedLoan) {
          this.loadDocumentsForLoan(this.selectedLoan.id);
        }
      }
    });
  }

  triggerFileInput(): void {
    if (this.fileInput) {
      this.fileInput.nativeElement.click();
    }
  }

  onFileSelectedAndUpload(event: any): void {
    const fileList: FileList = event.target.files;
    if (!fileList || fileList.length === 0) {
      return;
    }

    if (!this.selectedDocument) {
      this.notificationService.showError('Please select a document first');
      return;
    }

    const allFiles: globalThis.File[] = Array.from(fileList);

    // Filter out non-PDF files
    const nonPdfFiles = allFiles.filter(f => f.type !== 'application/pdf');
    const pdfFiles = allFiles.filter(f => f.type === 'application/pdf');
    if (nonPdfFiles.length > 0) {
      this.notificationService.showError('Please select a PDF File');
    }
    if (pdfFiles.length === 0) {
      event.target.value = '';
      return;
    }

    // Filter out client-side duplicates
    const duplicates: string[] = [];
    const validFiles: globalThis.File[] = [];
    for (const f of pdfFiles) {
      if (this.files.some(existing => existing.fileName?.toLowerCase() === f.name.toLowerCase())) {
        duplicates.push(f.name);
      } else {
        validFiles.push(f);
      }
    }
    if (validFiles.length === 0) {
      for (const dup of duplicates) {
        this.notificationService.showFileAlreadyExists(dup);
      }
      event.target.value = '';
      return;
    }

    const documentCode = this.selectedDocument.documentNumber;

    // Create blob URLs for all valid files
    const blobUrls = new Map<string, string>();
    let readCount = 0;
    for (const f of validFiles) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const blob = new Blob([e.target.result], { type: 'application/pdf' });
        blobUrls.set(f.name, URL.createObjectURL(blob));
        readCount++;
      };
      reader.onerror = () => { readCount++; };
      reader.readAsArrayBuffer(f);
    }

    // Show confirmation modal with duplicate info
    const totalCount = pdfFiles.length;
    const message = `Do you want to upload ${totalCount} document${totalCount > 1 ? 's' : ''}?`;

    const details: Array<{label: string; value: string}> = [];
    if (validFiles.length === 1) {
      details.push({ label: 'File', value: validFiles[0].name });
    } else {
      details.push({ label: 'Upload', value: `${validFiles.length} PDF files selected` });
    }
    if (duplicates.length > 0) {
      details.push({ label: 'Duplicate', value: `${duplicates.length} PDF file${duplicates.length > 1 ? 's' : ''}` });
    }

    this.modalService.show({
      type: ModalType.CONFIRMATION,
      size: ModalSize.SMALL,
      title: 'Confirm Upload',
      message,
      details,
      confirmText: 'Yes',
      cancelText: 'No',
      onConfirm: () => {
        if (validFiles.length === 1) {
          this.pendingUploadBlobUrl = blobUrls.get(validFiles[0].name) || null;
          this.executeFileUpload(event, validFiles[0], documentCode);
        } else {
          this.executeMultiFileUpload(event, validFiles, blobUrls, documentCode);
        }
      },
      onCancel: () => {
        event.target.value = '';
        blobUrls.forEach(url => URL.revokeObjectURL(url));
        blobUrls.clear();
      }
    });
  }

  private executeMultiFileUpload(
    event: any,
    files: globalThis.File[],
    blobUrls: Map<string, string>,
    documentCode: string
  ): void {
    if (this.isUploadingFile || !this.selectedDocument) { return; }

    this.isUploadingFile = true;
    this.uploadProgress = `Uploading 0 of ${files.length}...`;
    this.cdr.detectChanges();

    let completed = 0;
    const uploads$ = files.map(file =>
      this.truthManagementService.uploadFile(file, this.selectedDocument!.id, documentCode).pipe(
        map(result => {
          completed++;
          this.uploadProgress = `Uploading ${completed} of ${files.length}...`;
          this.cdr.detectChanges();
          return { file, result, error: null as any };
        }),
        catchError(error => {
          completed++;
          this.uploadProgress = `Uploading ${completed} of ${files.length}...`;
          this.cdr.detectChanges();
          return of({ file, result: null as any, error });
        })
      )
    );

    forkJoin(uploads$).subscribe(results => {
      const successes = results.filter(r => r.result);
      const failures = results.filter(r => r.error);

      for (const { file, result } of successes) {
        const blobUrl = blobUrls.get(file.name);
        if (blobUrl) {
          this.uploadedFileBlobUrls.set(result.id, blobUrl);
          this.uploadedFileBlobUrls.set(result.id.toString(), blobUrl);
        }
        const initialStatus: StatusMetadata = {
          status: GroundTruthStatus.NOT_STARTED,
          tooltip: `Not Started (0/${this.attributes.length} fields)`,
          filledRequired: 0,
          totalRequired: this.attributes.length
        };
        this.fileStatusCache.set(result.id, initialStatus);
        this.newlyUploadedFiles.add(result.id);
      }

      for (const { file, error } of failures) {
        const blobUrl = blobUrls.get(file.name);
        if (error?.status === 409) {
          if (blobUrl) { URL.revokeObjectURL(blobUrl); }
        } else {
          const localFile: File = {
            id: 'local_' + Date.now() + '_' + file.name,
            fileName: file.name,
            filePath: blobUrl || '',
            documentId: this.selectedDocument?.id || 0,
            uploadedDate: new Date()
          };
          if (blobUrl) {
            this.uploadedFileBlobUrls.set(localFile.id, blobUrl);
            this.uploadedFileBlobUrls.set(localFile.id.toString(), blobUrl);
          }
          this.files = [localFile, ...this.files];
        }
      }

      this.loadEvaluationFiles(documentCode);
      this.truthManagementService.notifyDataChanged(documentCode);

      if (successes.length > 0) {
        if (successes.length === 1) {
          this.notificationService.showUploadSuccess(successes[0].file.name);
        } else {
          const message = successes.length === files.length
            ? `${successes.length} files uploaded successfully`
            : `${successes.length} of ${files.length} files uploaded successfully`;
          const fileNames = successes.map(s => s.file.name).join(', ');
          this.notificationService.showSuccess(message, [
            { label: 'Files', value: fileNames }
          ]);
        }
      }
      if (failures.length > 0) {
        const dupCount = failures.filter(f => f.error?.status === 409).length;
        const otherCount = failures.length - dupCount;
        if (dupCount > 0) {
          this.notificationService.showFileAlreadyExists(
            dupCount === 1
              ? failures.find(f => f.error?.status === 409)!.file.name
              : `${dupCount} files`
          );
        }
        if (otherCount > 0) {
          this.notificationService.showError(`Failed to upload: ${failures.filter(f => f.error?.status !== 409).map(f => f.file.name).join(', ')}`);
        }
      }

      this.isUploadingFile = false;
      this.uploadProgress = '';
      event.target.value = '';
      this.cdr.detectChanges();
    });
  }

  private executeFileUpload(event: any, file: any, documentCode: string): void {
    // Guard: Prevent double-execution (production-grade protection)
    if (this.isUploadingFile) {
      return;
    }
    
    if (!this.selectedDocument) {
      this.notificationService.showError('Document context lost');
      return;
    }
    
    // Show upload loader
    this.isUploadingFile = true;
    this.cdr.detectChanges();

    // Upload file directly to evaluation API using new uploadFile method
    this.truthManagementService.uploadFile(file, this.selectedDocument.id, documentCode).subscribe({
      next: (uploadedFile) => {
        // Hide upload loader
        this.isUploadingFile = false;
        
        // Refresh evaluation files to get fresh blob_sas_url from server
        // This ensures download button will work immediately
        this.loadEvaluationFiles(documentCode);
        
        // Cache the blob URL for this file so it can be viewed when clicked
        if (this.pendingUploadBlobUrl) {
          this.uploadedFileBlobUrls.set(uploadedFile.id, this.pendingUploadBlobUrl);
          this.uploadedFileBlobUrls.set(uploadedFile.id.toString(), this.pendingUploadBlobUrl);
          this.pendingUploadBlobUrl = null;
        }
        
        // CRITICAL FIX: Set initial status for newly uploaded file
        // New files have no truth values yet, so they should show red dot (NOT_STARTED)
        const initialStatus: StatusMetadata = {
          status: GroundTruthStatus.NOT_STARTED,
          tooltip: `Not Started (0/${this.attributes.length} fields)`,
          filledRequired: 0,
          totalRequired: this.attributes.length
        };
        this.fileStatusCache.set(uploadedFile.id, initialStatus);
        
        // Mark file as newly uploaded for first-time extraction (bypass threshold)
        this.newlyUploadedFiles.add(uploadedFile.id);
        
        this.truthManagementService.notifyDataChanged(documentCode);
        
        // Reset file input
        event.target.value = '';
        
        // Show upload completed toaster
        this.notificationService.showUploadSuccess(file.name);
      },
      error: (error) => {
        // Hide upload loader
        this.isUploadingFile = false;
        this.cdr.detectChanges();
        
        // Handle 409 Conflict - file already exists
        if (error.status === 409) {
          // Clean up the pending blob URL without adding duplicate
          if (this.pendingUploadBlobUrl) {
            URL.revokeObjectURL(this.pendingUploadBlobUrl);
            this.pendingUploadBlobUrl = null;
          }
          event.target.value = '';
          // Use toaster for error message
          this.notificationService.showFileAlreadyExists(file.name);
          return;
        }
        
        let errorMessage = 'Failed to upload file to Evaluation API';
        let errorDetails = '';
        if (error.status === 400 && error.error?.detail) {
          errorDetails = error.error.detail;
        } else if (error.error?.detail) {
          errorDetails = error.error.detail;
        } else if (error.message) {
          errorDetails = error.message;
        }
        
        // Create a local file entry to show in the UI even though upload failed
        const localFile: File = {
          id: 'local_' + Date.now(),
          fileName: file.name,
          filePath: this.pendingUploadBlobUrl || '', // Use the local blob URL
          documentId: this.selectedDocument?.id || 0,
          uploadedDate: new Date()
        };
        
        // Cache the blob URL for this local file
        if (this.pendingUploadBlobUrl) {
          this.uploadedFileBlobUrls.set(localFile.id, this.pendingUploadBlobUrl);
          this.uploadedFileBlobUrls.set(localFile.id.toString(), this.pendingUploadBlobUrl);
          this.pendingUploadBlobUrl = null;
        }
        
        // Add to files array so user can see it locally (place at beginning)
        this.files = [localFile, ...this.files];
        
        // DO NOT auto-select - just show in list
        this.cdr.detectChanges();
        
        event.target.value = '';
        // Show error toaster
        this.notificationService.showError('Failed to upload file');
      }
    });
  }

  /**
   * Check user role to determine if admin features should be shown
   * Uses session storage to cache the role for the current session
   */
  private checkUserRole(): void {
    // Intentionally left empty — role is now managed exclusively by AuthService
  }

  downloadFile(event: Event, file: File): void {
  event.stopPropagation();

  const evalFile = this.evaluationFiles.find((ef: any) => ef.uuid === file.id);
  const downloadUrl = evalFile?.blob_sas_url;

  this.modalService.show({
    type: ModalType.CONFIRMATION,
    size: ModalSize.SMALL,
    title: 'Download File',
    message: `Do you want to download the file "${file.fileName}"?`,
    confirmText: 'Yes, Download',
    cancelText: 'No, Cancel',
    onConfirm: () => {
      if (!downloadUrl) {
        this.notificationService.showError('Download URL is not available for this file.');
        return;
      }

      // Build timestamped filename: baseName_YYYYMMDD_HHmmss.ext
      const lastDot = file.fileName.lastIndexOf('.');
      const baseName = lastDot !== -1 ? file.fileName.substring(0, lastDot) : file.fileName;
      const extension = lastDot !== -1 ? file.fileName.substring(lastDot) : '';
      const now = new Date();
      const timestamp = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0')
      ].join('') + '_' + [
        String(now.getHours()).padStart(2, '0'),
        String(now.getMinutes()).padStart(2, '0'),
        String(now.getSeconds()).padStart(2, '0')
      ].join('');
      const downloadFileName = `${baseName}_${timestamp}${extension}`;

      fetch(downloadUrl)
        .then(response => response.blob())
        .then(blob => {
          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = downloadFileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(objectUrl);
        })
        .catch(() => {
          this.notificationService.showError('Failed to download the file. Please try again.');
        });
    }
  });
}

  downloadFiles(): void {
    this.downloadFilesAsZip(this.files.map(f => String(f.id)));
  }

  private downloadFilesAsZip(fileIds: string[]): void {
    if (fileIds.length === 0) return;

    const filesToDownload = fileIds.map(id => {
      const file = this.files.find(f => String(f.id) === id);
      const evalFile = this.evaluationFiles.find((ef: any) => ef.uuid === id);
      return { file, evalFile, url: evalFile?.blob_sas_url };
    }).filter(f => f.file && f.url);

    if (filesToDownload.length === 0) {
      this.notificationService.showError('Download URLs are not available for the selected files.');
      return;
    }

    const skippedCount = fileIds.length - filesToDownload.length;

    const message = `Download all ${filesToDownload.length} file${filesToDownload.length > 1 ? 's' : ''} as ZIP?`;

    this.modalService.show({
      type: ModalType.CONFIRMATION,
      size: ModalSize.SMALL,
      title: 'Download Files',
      message,
      confirmText: 'Yes, Download',
      cancelText: 'Cancel',
      onConfirm: async () => {
        this.isDownloadingFiles = true;
        this.downloadProgress = `Fetching 0 of ${filesToDownload.length}...`;
        this.cdr.detectChanges();

        const zip = new JSZip();
        let fetched = 0;
        const failed: string[] = [];

        const results = await Promise.allSettled(
          filesToDownload.map(async (item) => {
            try {
              const response = await fetch(item.url!);
              const blob = await response.blob();
              zip.file(item.file!.fileName, blob);
            } catch {
              failed.push(item.file!.fileName);
              throw new Error(item.file!.fileName);
            } finally {
              fetched++;
              this.downloadProgress = `Fetching ${fetched} of ${filesToDownload.length}...`;
              this.cdr.detectChanges();
            }
          })
        );

        const successCount = filesToDownload.length - failed.length;

        if (successCount === 0) {
          this.isDownloadingFiles = false;
          this.downloadProgress = '';
          this.cdr.detectChanges();
          this.notificationService.showError('Failed to download files. Please try again.');
          return;
        }

        this.downloadProgress = 'Creating ZIP...';
        this.cdr.detectChanges();

        try {
          const zipBlob = await zip.generateAsync({ type: 'blob' });
          const now = new Date();
          const timestamp = [
            now.getFullYear(),
            String(now.getMonth() + 1).padStart(2, '0'),
            String(now.getDate()).padStart(2, '0')
          ].join('') + '_' + [
            String(now.getHours()).padStart(2, '0'),
            String(now.getMinutes()).padStart(2, '0'),
            String(now.getSeconds()).padStart(2, '0')
          ].join('');
          const docName = this.selectedDocument?.documentNumber || 'files';
          const zipFileName = `${docName}_${timestamp}.zip`;

          const objectUrl = URL.createObjectURL(zipBlob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = zipFileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(objectUrl);

          this.notificationService.showSuccess(`${successCount} file${successCount > 1 ? 's' : ''} downloaded successfully`);

          if (failed.length > 0) {
            this.notificationService.showError(`Failed to download: ${failed.join(', ')}`);
          }
          if (skippedCount > 0) {
            this.notificationService.showError(`${skippedCount} file${skippedCount > 1 ? 's' : ''} skipped (no download URL)`);
          }
        } catch {
          this.notificationService.showError('Failed to create ZIP file. Please try again.');
        }

        this.isDownloadingFiles = false;
        this.downloadProgress = '';
        this.cdr.detectChanges();
      }
    });
  }

  deleteFile(event: Event, file: File): void {
    // Stop event propagation to prevent file selection
    event.stopPropagation();
    
    // Check if file has truth values
    const hasTruthValues = this.checkFileHasTruthValues(file.id);
    
    // Prepare appropriate confirmation message based on truth values presence
    const title = hasTruthValues ? 'GT Present' : 'GT Not Present';
    const message = hasTruthValues 
      ? 'Deleting the file will permanently remove the ground truth as well.\n\nAre you sure you want to proceed?'
      : 'Are you sure you want to delete this file?';
    
    // Show confirmation dialog with red delete button
    this.modalService.show({
      type: ModalType.CONFIRMATION,
      size: ModalSize.SMALL,
      title: title,
      message: message,
      confirmText: 'Delete File',
      cancelText: 'Cancel',
      isDanger: true,
      onConfirm: () => {
        this.performFileDelete(file);
      }
    });
  }
  
  /**
   * Check if a file has any truth values
   * @param fileId - ID of the file to check
   * @returns true if file has truth values, false otherwise
   */
  private checkFileHasTruthValues(fileId: number | string): boolean {
    // First check if this is the currently selected file with loaded truth values
    if (this.selectedFile && this.selectedFile.id === fileId) {
      // Check currentFileTruthValues for any non-empty values
      for (const [attributeId, truthValue] of this.currentFileTruthValues.entries()) {
        if (!this.isEmptyValue(truthValue.value)) {
        return true;
        }
      }
    }
    
    // Check file status cache for filled fields (works for all files)
    const fileStatus = this.fileStatusCache.get(fileId);
    if (fileStatus && fileStatus.filledRequired > 0) {
      return true;
    }
    return false;
  }

  /**
   * Perform the actual file deletion
   * @param file - File to delete
   */
  private performFileDelete(file: File): void {
    this.truthManagementService.deleteFile(file.id).subscribe({
      next: () => {
        // Refresh evaluation files to sync with server state
        // This removes deleted file and updates any changed blob_sas_urls
        if (this.selectedDocument) {
          this.loadEvaluationFiles(this.selectedDocument.documentNumber);
        }
        
        // If the deleted file was selected, clear selection
        if (this.selectedFile?.id === file.id) {
          this.selectedFile = null;
          this.resetPdfViewerState(true);
          this.currentFileTruthValues.clear();
          this.modifiedValues.clear();
          this.modifiedAttributeIds.clear();
          this.autoPopulatedAttributeIds.clear();
        }
        
        // Clear file status cache
        this.fileStatusCache.delete(file.id);
        
        // Show success message
        this.notificationService.showFileDeleted(file.fileName);
      },
      error: (error) => {
        this.notificationService.showError('Failed to delete', [
          { label: 'Reason', value: 'Technical reason' }
        ]);
      }
    });
  }

  openAddAttributeDialogMethod(): void {
    this.newAttributeKey = '';
    this.newAttributeLabel = '';
    this.newAttributeDescription = '';
    this.showAddAttributeDialog = true;
  }

  closeAddAttributeDialogMethod(): void {
    this.showAddAttributeDialog = false;
    this.newAttributeKey = '';
    this.newAttributeLabel = '';
    this.newAttributeDescription = '';
  }

  addAttribute(): void {
    if (!this.newAttributeLabel || !this.newAttributeLabel.trim()) {
      alert('Please enter attribute name');
      return;
    }

    if (!this.selectedDocument) {
      alert('Please select a file first');
      return;
    }

    // Auto-generate attribute key from label
    const attributeKey = this.newAttributeLabel
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

    this.truthManagementService.addAttribute(
      attributeKey,
      this.newAttributeLabel,
      this.newAttributeDescription,
      this.selectedDocument.id
    ).subscribe({
      next: (attribute) => {
        // Add attribute to array immediately for instant UI update
        this.attributes = [...this.attributes, attribute];
        // Update count
        const currentCount = this.documentAttributeCounts.get(this.selectedDocument!.id) || 0;
        this.documentAttributeCounts.set(this.selectedDocument!.id, currentCount + 1);
        // Reload from server to ensure consistency
        this.loadAttributesForDocument(this.selectedDocument!.id);
        this.closeAddAttributeDialogMethod();
        setTimeout(() => {
          alert('Attribute added successfully!');
        }, 100);
      },
      error: (error) => {
        let errorMessage = 'Failed to add attribute';
        if (error.status === 400 && error.error?.detail) {
          errorMessage = error.error.detail;
        } else if (error.error?.detail) {
          errorMessage = error.error.detail;
        } else if (error.message) {
          errorMessage = error.message;
        }
        alert(`❌ ${errorMessage}`);
      }
    });
  }

  editAttribute(attribute: Attribute): void {
    if (!attribute.id) {
      alert('Invalid attribute');
      return;
    }

    // Open edit dialog with current values
    this.editAttributeId = attribute.id;
    this.editAttributeLabel = attribute.attributeLabel;
    this.editAttributeDescription = attribute.description || '';
    this.showEditAttributeDialog = true;
  }
  
  saveEditAttribute(): void {
    // Guard: Prevent double-execution (production-grade protection)
    if (this.isUpdatingAttribute) {
      return;
    }
    
    if (!this.editAttributeId) {
      return;
    }
    
    if (!this.editAttributeLabel.trim()) {
      alert('Attribute name is required');
      return;
    }

    this.isUpdatingAttribute = true;

    // Update attribute via API
    this.truthManagementService.updateAttribute(this.editAttributeId, {
      attributeLabel: this.editAttributeLabel.trim(),
      description: this.editAttributeDescription.trim()
    }).subscribe({
      next: () => {
        this.isUpdatingAttribute = false;
        if (this.selectedDocument) {
          this.loadAttributesForDocument(this.selectedDocument.id);
        }
        this.closeEditAttributeDialog();
        alert('Attribute updated successfully!');
      },
      error: (error) => {
        this.isUpdatingAttribute = false;
        alert('Failed to update attribute');
      }
    });
  }
  
  closeEditAttributeDialog(): void {
    this.showEditAttributeDialog = false;
    this.editAttributeId = null;
    this.editAttributeLabel = '';
    this.editAttributeDescription = '';
  }

  deleteAttribute(attribute: Attribute): void {
    // Guard: Prevent double-execution (production-grade protection)
    if (this.isDeletingAttribute) {
     return;
    }
    
    if (!attribute.id) {
      alert('Invalid attribute');
      return;
    }

    if (confirm(`Are you sure you want to delete the attribute "${attribute.attributeLabel}"?\n\nThis will also delete all associated truth values.`)) {
      this.isDeletingAttribute = true;
      
      this.truthManagementService.deleteAttribute(attribute.id).subscribe({
        next: () => {
          this.isDeletingAttribute = false;
          // Remove from array immediately
          this.attributes = this.attributes.filter(a => a.id !== attribute.id);
          // Update count
          if (this.selectedDocument) {
            const currentCount = this.documentAttributeCounts.get(this.selectedDocument.id) || 0;
            this.documentAttributeCounts.set(this.selectedDocument.id, Math.max(0, currentCount - 1));
            // Reload to ensure consistency
            this.loadAttributesForDocument(this.selectedDocument.id);
          }
          this.notificationService.showSuccess('Attribute deleted successfully');
        },
        error: (error) => {
          this.isDeletingAttribute = false;
          this.notificationService.showError('Failed to delete attribute');
        }
      });
    }
  }


  openAddValueDialog(): void {
    this.dialogMode = 'add';
    this.dialogData = { key: '', value: '' };
    this.showDialog = true;
  }

  editValue(value: TruthValue): void {
    this.dialogMode = 'edit';
    const attribute = this.attributes.find(a => a.id === value.attributeId);
    this.dialogData = { 
      id: value.id, 
      key: attribute?.attributeKey || '', 
      value: toStringValue(value.value) 
    };
    this.showDialog = true;
  }

  renameValue(value: TruthValue): void {
    const currentLabel = this.getAttributeLabel(value.attributeId);
    const newValue = prompt(`Update value for "${currentLabel}"`, toStringValue(value.value));
    
    if (newValue !== null && newValue.trim() !== value.value) {
      this.truthManagementService.updateTruthValue(value.id, {
        value: newValue.trim(),
        fileId: value.fileId
      }).subscribe({
        next: () => {
          if (this.selectedFile) {
            this.loadTruthValuesForFile(this.selectedFile.id);
          }
        },
        error: (error) => {
          alert('Failed to update truth value');
        }
      });
    }
  }

  deleteValue(value: TruthValue): void {
    const attributeLabel = this.getAttributeLabel(value.attributeId);
    if (confirm(`Are you sure you want to delete "${attributeLabel}"?`)) {
      this.truthManagementService.deleteTruthValue(value.id, value.fileId).subscribe({
        next: () => {
          if (this.selectedFile) {
            this.loadTruthValuesForFile(this.selectedFile.id);
          }
        },
        error: (error) => {
          alert('Failed to delete truth value');
        }
      });
    }
  }

  saveValue(): void {
    if (!this.dialogData.key.trim()) {
      alert('Please select an attribute');
      return;
    }

    if (!this.dialogData.value.trim()) {
      alert('Please enter a value');
      return;
    }

    if (this.dialogMode === 'add') {
      // Find the attribute to get its ID
      const attribute = this.attributes.find(a => a.attributeKey === this.dialogData.key);
      if (!attribute || !attribute.id) {
        alert('Attribute not found');
        return;
      }

      this.truthManagementService.addTruthValue({
        attributeId: attribute.id,
        value: this.dialogData.value,
        documentId: this.selectedDocument!.id,
        fileId: this.selectedFile!.id
      }).subscribe({
        next: () => {
          this.loadTruthValuesForFile(this.selectedFile!.id);
          this.closeDialog();
        },
        error: (error) => {
          alert('Failed to add truth value: ' + (error.error?.detail || error.message));
        }
      });
    } else if (this.dialogMode === 'edit') {
      this.truthManagementService.updateTruthValue(this.dialogData.id!, {
        value: this.dialogData.value,
        fileId: this.selectedFile!.id
      }).subscribe({
        next: () => {
          this.loadTruthValuesForFile(this.selectedFile!.id);
          this.closeDialog();
        },
        error: (error) => {
          alert('Failed to update truth value');
        }
      });
    }
  }

  closeDialog(): void {
    this.showDialog = false;
    this.dialogData = { key: '', value: '' };
  }

  openAddAttributeDialog(): void {
    this.newAttributeLabel = '';
    this.newAttributeKey = '';
    this.showAddAttributeDialog = true;
  }

  closeAddAttributeDialog(): void {
    this.showAddAttributeDialog = false;
    this.newAttributeLabel = '';
    this.newAttributeKey = '';
  }

  // Excel Import functionality
  triggerExcelImport(): void {
    // Ask user for document code
    const documentCode = prompt('Enter the Document Code to import data for:');
    
    if (!documentCode || documentCode.trim() === '') {
      alert('Document Code is required to import data.');
      return;
    }
    
    // Confirm import
    const confirmImport = confirm(
      `Import data for Document Code: ${documentCode.trim()}?\n\n` +
      `This will read from the Excel file in the repo and import only rows matching this document code.`
    );
    
    if (confirmImport) {
      this.importExcelData(documentCode.trim());
    }
  }

  navigateToAccuracy(): void {
    this.router.navigate(['/dashboard']);
  }

  navigateToHome(): void {
    this.router.navigate(['/dashboard']);
  }

  navigateToSequencing(): void {
    this.router.navigate(['/']);
  }

  navigateToDataExtraction(): void {
    this.router.navigate(['/data-extraction']);
  }

  navigateToExtractionEval(): void {
    this.router.navigate(['/dashboard']);
  }

  navigateToVersioning(): void {
    this.router.navigate(['/versioning']);
  }

  navigateToAdminSettings(): void {
    this.router.navigate(['/admin-settings']);
  }

  navigateToStatsDashboard(): void {
    this.router.navigate(['/stats-dashboard']);
  }

  navigateToOperationsRequestAnalytics(): void {
    this.router.navigate(['/indexing']);
  }


  logout(): void {
    // REQUIREMENT: Check if auto-fill is in progress before logout
    if (this.isExtracting) {
      this.modalService.show({
        type: ModalType.CONFIRMATION,
        size: ModalSize.SMALL,
        title: 'Auto Fill in Progress',
        message: 'Auto fill is in progress. If you leave this page, auto fill will be terminated. Do you want to continue?',
        confirmText: 'Yes, Logout',
        cancelText: 'No, Stay Here',
        allowOutsideClick: false,
        allowEscapeKey: false,
        onConfirm: () => {
          // Clear all extraction state
          this.extractingFiles.clear();
          this.extractionRequestIds.clear();
          this.isExtracting = false;
          this.extractingFileId = null;
          this.isOverrideMode = false;
          localStorage.removeItem('truthManagement_extraction');
          
          // Proceed with logout
          sessionStorage.removeItem('userRole');
          sessionStorage.removeItem('userEmail');
          sessionStorage.removeItem('userSessionId');
          this.authService.clearTokens();
          this.isAuthenticated = false;
          this.router.navigate(['/login']);
        },
        onCancel: () => {
        }
      });
      return;
    }
    
    // Check if there are unsaved changes
    const hasUnsaved = this.hasUnsavedChanges();
    
    // Show appropriate logout confirmation message
    const message = hasUnsaved 
      ? 'You have not saved your changes. Still you want to logout?'
      : 'Do you want to Log Out?';
    
    this.modalService.showLogoutConfirmation(() => {
      // Auth service handles clearing session storage and navigation
      this.authService.logout();
      this.isAuthenticated = false;
    }, message);
  }



  importExcelData(documentCode: string): void {
    this.truthManagementService.importExcel(documentCode).subscribe({
      next: (response) => {
        if (response.success) {
          const stats = response.stats;
          const data = response.data || [];
          
          // Build data display string
          let dataDisplay = '';
          if (data.length > 0) {
            dataDisplay = '\n\n📊 Imported Data:\n';
            data.forEach((item: any, index: number) => {
              dataDisplay += `${index + 1}. File: ${item.fileName}\n   Key: ${item.key}\n   Fact: ${item.fact}\n\n`;
            });
          }
          
          alert(
            `✅ Import Successful for Document Code: ${documentCode}!\n\n` +
            `📈 Statistics:\n` +
            `Total rows: ${stats.total_rows}\n` +
            `Files created: ${stats.files_created}\n` +
            `Attributes created: ${stats.attributes_created}\n` +
            `Truth values created: ${stats.truth_values_created}` +
            dataDisplay +
            `${stats.errors.length > 0 ? '\n⚠️ Errors:\n' + stats.errors.join('\n') : ''}`
          );
          
          // Reload documents to show newly imported data
          this.loadAllDocuments();
          this.loadAttributesFromAPI();
        } else {
          alert(
            `⚠️ Import completed with errors:\n\n` +
            `${response.stats.errors.join('\n')}`
          );
        }
      },
      error: (error) => {
        alert(`❌ Import failed: ${error.error?.detail || error.message || 'Unknown error'}`);
      }
    });
  }

  // Ground truth status methods

  /**
   * Populate fileStatusCache using attribute_summary from the files API response.
   * This avoids N+1 getTruthValuesByFileId calls on page load.
   * The files API now returns { total_count, filled_count } per file.
   */
  loadFileStatusesFromSummary(): void {
    if (this.evaluationFiles.length === 0) {
      return;
    }

    for (const ef of this.evaluationFiles) {
      const fileId = ef.uuid;

      // Skip the currently selected file — its status comes from selectedFileAttributeSummary
      // which is updated by loadTruthValuesForFile() when the file is selected/saved.
      if (this.selectedFile && String(fileId) === String(this.selectedFile.id)) continue;

      const summary = (ef as any).attribute_summary as { total_count: number; filled_count: number } | undefined;
      const totalRequired = summary?.total_count ?? 0;
      const filledRequired = summary?.filled_count ?? 0;

      let status: GroundTruthStatus;
      if (totalRequired === 0) {
        status = GroundTruthStatus.NOT_STARTED;
      } else if (filledRequired >= totalRequired) {
        status = GroundTruthStatus.COMPLETE;
      } else if (filledRequired > 0) {
        status = GroundTruthStatus.PARTIAL;
      } else {
        status = GroundTruthStatus.NOT_STARTED;
      }

      const tooltip = status === GroundTruthStatus.COMPLETE
        ? `Complete - ${filledRequired} out of ${totalRequired} filled`
        : status === GroundTruthStatus.PARTIAL
          ? `In Progress - ${filledRequired} out of ${totalRequired} filled`
          : `Not Started - 0 out of ${totalRequired} filled`;

      const metadata: StatusMetadata = {
        status,
        tooltip,
        filledRequired,
        totalRequired
      };
      this.fileStatusCache.set(fileId, metadata);
    }

    this.cdr.detectChanges();
  }

  /** @deprecated Use loadFileStatusesFromSummary() instead — kept for fallback reference */
  loadAllFileStatuses(): void {
    // Load truth values for all files to calculate actual status
    if (this.files.length === 0 || this.attributes.length === 0) {
      return;
    }
    
    // Define result type for type safety
    interface FileStatusResult {
      fileId: number | string;
      response: AttributesResponse;
      error: boolean;
    }
    
    // Use forkJoin for better performance - load all files in parallel
    const fileStatusRequests: Observable<FileStatusResult>[] = this.files.map(file => 
      this.truthManagementService.getTruthValuesByFileId(file.id).pipe(
        map((response: AttributesResponse): FileStatusResult => ({ 
          fileId: file.id, 
          response,
          error: false 
        })),
        catchError((): Observable<FileStatusResult> => of({ 
          fileId: file.id, 
          response: { file_id: '', file_name: '', document_model: '', attributes: [], count: 0, attribute_summary: { total_count: 0, filled_count: 0 } },
          error: true 
        }))
      )
    );
    
    forkJoin(fileStatusRequests).subscribe((results: FileStatusResult[]) => {
      results.forEach((result: FileStatusResult) => {
        // Skip the currently selected file — its status comes from selectedFileAttributeSummary
        // (updated by loadTruthValuesForFile when backend data is fetched).
        if (this.selectedFile && String(result.fileId) === String(this.selectedFile.id)) return;

        if (result.error || !result.response.attribute_summary) {
          // If error loading truth values or no summary, set to "not started"
          const metadata: StatusMetadata = {
            status: GroundTruthStatus.NOT_STARTED,
            tooltip: `Not Started (0/${this.attributes.length} fields)`,
            filledRequired: 0,
            totalRequired: this.attributes.length
          };
          this.fileStatusCache.set(result.fileId, metadata);
        } else {
          // Use backend-provided attribute_summary directly (no local calculation)
          const { total_count, filled_count } = result.response.attribute_summary;
          
          let status: GroundTruthStatus;
          if (total_count === 0) {
            status = GroundTruthStatus.NOT_STARTED;
          } else if (filled_count >= total_count) {
            status = GroundTruthStatus.COMPLETE;
          } else if (filled_count > 0) {
            status = GroundTruthStatus.PARTIAL;
          } else {
            status = GroundTruthStatus.NOT_STARTED;
          }
          
          const tooltip = status === GroundTruthStatus.COMPLETE
            ? `Complete - ${filled_count} out of ${total_count} filled`
            : status === GroundTruthStatus.PARTIAL
              ? `In Progress - ${filled_count} out of ${total_count} filled`
              : `Not Started - 0 out of ${total_count} filled`;
          
          this.fileStatusCache.set(result.fileId, {
            status,
            tooltip,
            filledRequired: filled_count,
            totalRequired: total_count
          });
        }
      });
      // Trigger change detection once after all statuses are loaded
      this.cdr.detectChanges();
    });
  }

  /**
   * Get file status metadata (for tooltip display)
   * Returns backend-provided counts and computed status
   */
  getFileStatus(fileId: number | string): StatusMetadata | undefined {
    // For selected file, use Attributes API data if available
    if (this.selectedFile && String(fileId) === String(this.selectedFile.id)) {
      if (this.selectedFileAttributeSummary) {
        const { filled_count, total_count } = this.selectedFileAttributeSummary;
        
        let status: GroundTruthStatus;
        if (total_count === 0) {
          status = GroundTruthStatus.NOT_STARTED;
        } else if (filled_count >= total_count) {
          status = GroundTruthStatus.COMPLETE;
        } else if (filled_count > 0) {
          status = GroundTruthStatus.PARTIAL;
        } else {
          status = GroundTruthStatus.NOT_STARTED;
        }
        
        const tooltip = status === GroundTruthStatus.COMPLETE
          ? `Complete - ${filled_count} out of ${total_count} filled`
          : status === GroundTruthStatus.PARTIAL
            ? `In Progress - ${filled_count} out of ${total_count} filled`
            : `Not Started - 0 out of ${total_count} filled`;
        
        return {
          status,
          tooltip,
          filledRequired: filled_count,
          totalRequired: total_count
        };
      }
    }

    // For other files or when Attributes API data not yet available, use cached data
    return this.fileStatusCache.get(fileId);
  }

  /**
   * Get header count display for the selected file
   * - Initially uses Files API data (from fileStatusCache)
   * - After modification/save, uses Attributes API data (from selectedFileAttributeSummary)
   */
  getHeaderCount(): { filled: number; total: number } {
    // If we have fresh Attributes API data for selected file (after first save), use it
    if (this.selectedFileAttributeSummary) {
      return {
        filled: this.selectedFileAttributeSummary.filled_count,
        total: this.selectedFileAttributeSummary.total_count
      };
    }

    // Otherwise, use Files API data from cache (initial load)
    if (this.selectedFile) {
      const cachedStatus = this.fileStatusCache.get(this.selectedFile.id);
      if (cachedStatus) {
        return {
          filled: cachedStatus.filledRequired,
          total: cachedStatus.totalRequired
        };
      }
    }

    // Fallback when no data available
    return { filled: 0, total: 0 };
  }

  /**
   * Get CSS class for file status dot based on backend attribute_summary
   * Preserves GroundTruthStatus enum logic (complete/partial/not-started)
   */
  getFileStatusClass(fileId: number | string): string {
    // For selected file, use Attributes API data if available
    if (this.selectedFile && String(fileId) === String(this.selectedFile.id)) {
      if (this.selectedFileAttributeSummary) {
        const { filled_count, total_count } = this.selectedFileAttributeSummary;
        let status: GroundTruthStatus;
        
        if (total_count === 0) {
          status = GroundTruthStatus.NOT_STARTED;
        } else if (filled_count >= total_count) {
          status = GroundTruthStatus.COMPLETE;
        } else if (filled_count > 0) {
          status = GroundTruthStatus.PARTIAL;
        } else {
          status = GroundTruthStatus.NOT_STARTED;
        }
        
        return getStatusClass(status);
      }
    }

    // For other files, use cached status from Files API
    const metadata = this.fileStatusCache.get(fileId);
    return metadata ? getStatusClass(metadata.status) : 'status-unknown';
  }

  ngAfterViewInit(): void {
    // Attach wheel zoom listener to PDF container
    this.setupPdfWheelZoom();
    
    // Setup ResizeObserver for reactive bounding box updates
    this.setupPdfContainerResizeObserver();
  }

  ngOnDestroy(): void {
    // Clean up viewer/form resize listeners in case the user navigates away mid-drag
    document.removeEventListener('mousemove', this.onSplitMouseMove);
    document.removeEventListener('mouseup', this.onSplitMouseUp);

    this.unmountSnipOverlay();

    // Clean up ResizeObserver
    this.pdfContainerResizeObserver?.disconnect();
    
    // Clear debounce timer
    if (this.reRenderDebounceTimer) {
      clearTimeout(this.reRenderDebounceTimer);
    }
    
    // Clean up wheel listener
    if (this.pdfContainer?.nativeElement) {
      this.pdfContainer.nativeElement.removeEventListener('wheel', this.boundWheelHandler);
    }
    
    // Clean up blob URLs to prevent memory leaks
    if (this.pdfSrc && this.pdfSrc.startsWith('blob:')) {
      URL.revokeObjectURL(this.pdfSrc);
    }

    // Stop any background warm-cache download in flight
    this.warmCacheAbortController?.abort();

    // Release the pdf.js document (worker-side resources)
    if (this.pdfDocument) {
      try { this.pdfDocument.destroy(); } catch { /* already destroyed */ }
      this.pdfDocument = null;
    }
    
    // Clear truth values cache on page destroy
    this.fileTruthValuesCache.clear();
    
    // Invalidate document-level caches when navigating away so the next visit
    // always fetches fresh schema / keys data from the backend.
    if (this.lastLoadedDocumentCode) {
      this.truthManagementService.invalidateDocumentModelCache(this.lastLoadedDocumentCode);
    }
    
    // Note: beforeunload is automatically cleaned up when component destroys
    // Angular's @HostListener handles cleanup automatically
  }

  /**
   * Setup ResizeObserver to monitor PDF container size changes
   * Automatically re-renders bounding boxes when layout changes (panel toggle, window resize, etc.)
   */
  private setupPdfContainerResizeObserver(): void {
    // Observe the outer .pdf-container (always present, unlike the lazy viewer
    // which only exists while a PDF is loaded) — its resizes imply the viewport's.
    setTimeout(() => {
      const container = this.pdfContainer?.nativeElement;
      if (!container) {
        return;
      }

      this.pdfContainerResizeObserver = new ResizeObserver(() => {
        // Refit pages to the new width, then re-render the active highlight.
        if (this.pdfDocument && this.totalPages > 0) {
          this.computePdfPageLayout();
          this.cdr.detectChanges();
        }
        // Debounce re-render to avoid excessive updates during smooth CSS transitions
        this.scheduleReRenderBoundingBoxes('resize');
      });

      this.pdfContainerResizeObserver.observe(container);
    }, 500); // Small delay to ensure DOM is ready
  }

  /**
   * Schedule a debounced re-render of bounding boxes
   * @param reason - Why the re-render was triggered (for logging/debugging)
   */
  private scheduleReRenderBoundingBoxes(reason: string = 'unknown'): void {
    // Clear any pending re-render
    if (this.reRenderDebounceTimer) {
      clearTimeout(this.reRenderDebounceTimer);
    }

    // Schedule new re-render after debounce period
    this.reRenderDebounceTimer = window.setTimeout(() => {
      // Only re-render if there's an active highlight
      if (this.highlightedAttributeId !== null) {
        const sourceData = this.fieldSourceDataMap.get(this.highlightedAttributeId);
        if (sourceData) {
          this.renderBoundingBoxOverlays(sourceData).catch(err => {
          });
        }
      }
    }, 300); // 300ms debounce - wait for panel expansion/collapse animations to complete
  }

  /**
   * PRODUCTION-GRADE: Handle browser refresh/close with unsaved changes
   * 
   * Shows native browser warning when user tries to:
   * - Refresh page (F5, Ctrl+R)
   * - Close tab/window
   * - Navigate to external URL
   * 
   * Note: Modern browsers only show generic message, not custom text
   * This is a security feature to prevent malicious sites from faking system dialogs
   */
  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    // Only show warning if there are unsaved changes
    if (this.hasUnsavedChanges()) {
      // Standard way to trigger browser's confirmation dialog
      event.preventDefault();
      
      // For legacy browsers (Chrome <119)
      event.returnValue = '';
      
      // Note: Custom message is ignored by modern browsers
      // Browser shows its own generic message like:
      // "Changes you made may not be saved. Leave site?"
      return;
    }
  }

  /**
   * PRODUCTION-GRADE: Angular Router CanDeactivate Guard Implementation
   * 
   * Handles ALL Angular navigation scenarios:
   * - routerLink clicks
   * - router.navigate() calls
   * - Browser back/forward buttons
   * - Sidebar/menu clicks
   * - Logo/home navigation
   * - Logout
   * 
   * Flow:
   * 1. User attempts navigation
   * 2. Guard calls this method
   * 3. If no changes → return true (allow navigation)
   * 4. If changes → show "Do you want to save changes?" modal
   * 5. User clicks Yes → save changes, wait for completion, allow navigation
   * 6. User clicks No → discard changes, allow navigation
   * 7. Navigation blocked until user responds
   * 
   * @returns Observable<boolean> - true = allow navigation, false = block navigation
   */
  canDeactivate(): Observable<boolean> {
    // CHANGED: Removed auto-fill blocking - user can now navigate freely during auto-fill
    // Notification will appear below header when auto-fill completes, even if user navigates away
    
    // No unsaved changes → allow navigation immediately
    if (!this.hasUnsavedChanges()) {
      return of(true);
    }
    // Check if there are auto-populated changes
    const hasAutoPopulatedChanges = Array.from(this.modifiedAttributeIds).some(
      id => this.autoPopulatedAttributeIds.has(id)
    );
    
    // Create Subject to communicate modal result back to router
    const navigationDecision$ = new Subject<boolean>();

    // Show save confirmation modal.
    if (hasAutoPopulatedChanges) {
      this.modalService.showSaveConfirmation(
        () => {
          this.executeSave(false)
            .then((saveSuccess) => {
              if (saveSuccess) {
                this.notificationService.showSaveSuccess(
                  this.selectedDocument?.documentNumber || '',
                  this.selectedFile?.fileName || ''
                );
                navigationDecision$.next(true);
                navigationDecision$.complete();
              } else {
                 this.showSaveFailedNavigationOptions(navigationDecision$);
              }
            })
            .catch((error) => {
              this.showSaveFailedNavigationOptions(navigationDecision$);
            });
        },
        () => {
          if (this.selectedFile) {
            this.clearPendingExtractionResults(this.selectedFile.id);
            this.clearAutoFillCompletedForFile(this.selectedFile.id);
          }
          this.notificationService.showSaveDiscarded(
            this.selectedDocument?.documentNumber || '',
            this.selectedFile?.fileName || ''
          );
          this.modifiedValues.clear();
          this.modifiedAttributeIds.clear();
          this.autoPopulatedAttributeIds.clear();
          this.autoPopulatedOriginalValues.clear();
          this.overriddenAutoPopulatedAttributeIds.clear();
          this.editingAttributeId = null;
          this.editingArrayIndex = null;
          this.editingTruthValueText = '';
          navigationDecision$.next(true);
          navigationDecision$.complete();
        }
      );
    } else {
      // Use standard save confirmation for manual changes
      this.modalService.showSaveConfirmation(
      // User clicked "Yes" - save changes then allow navigation
      () => {
       // Call refactored executeSave with showSuccessModal=false (no modal during navigation)
        this.executeSave(false)
          .then((saveSuccess) => {
            if (saveSuccess) {
              this.notificationService.showSaveSuccess(
                this.selectedDocument?.documentNumber || '',
                this.selectedFile?.fileName || ''
              );
              navigationDecision$.next(true); // Allow navigation
              navigationDecision$.complete();
            } else {
             // Save failed - give user option to force navigate or stay
              this.showSaveFailedNavigationOptions(navigationDecision$);
            }
          })
          .catch((error) => {
            // Error during save - give user option to force navigate or stay
            this.showSaveFailedNavigationOptions(navigationDecision$);
          });
      },
      // User clicked "No" - discard changes and allow navigation
      () => {
        // Clear pending auto-fill results if this was an auto-filled file
        if (this.selectedFile) {
          this.clearPendingExtractionResults(this.selectedFile.id);
          this.clearAutoFillCompletedForFile(this.selectedFile.id);
        }
        this.notificationService.showSaveDiscarded(
          this.selectedDocument?.documentNumber || '',
          this.selectedFile?.fileName || ''
        );
        // Clear unsaved changes state
        this.modifiedValues.clear();
        this.modifiedAttributeIds.clear();
        this.autoPopulatedAttributeIds.clear();
        this.autoPopulatedOriginalValues.clear();
        this.overriddenAutoPopulatedAttributeIds.clear();
        this.editingAttributeId = null;
        this.editingArrayIndex = null;
        this.editingTruthValueText = '';
        navigationDecision$.next(true); // Allow navigation
        navigationDecision$.complete();
      }
    );
    }

    // Return Observable that resolves when user makes a choice
    // Router will wait for this Observable to emit before proceeding
    return navigationDecision$.asObservable().pipe(take(1));
  }

  /**
   * Show options when save fails - prevent user from being trapped
   * Gives user choice to stay on page (try again) or force navigate (lose changes)
   */
  private showSaveFailedNavigationOptions(navigationDecision$: Subject<boolean>): void {
    // Show error modal with custom buttons using low-level show() API
    this.modalService.show({
      type: ModalType.CONFIRMATION,
      size: ModalSize.SMALL,
      title: 'Save Failed',
      message: 'Unable to save your changes. The server returned an error. Do you want to stay on this page to try again, or leave anyway and lose your changes?',
      confirmText: 'Leave Anyway',
      cancelText: 'Stay on Page',
      onConfirm: () => {
        // Clear unsaved changes to prevent infinite loop
        if (this.selectedFile) {
          this.clearAutoFillCompletedForFile(this.selectedFile.id);
        }
        this.modifiedValues.clear();
        this.modifiedAttributeIds.clear();
        this.autoPopulatedAttributeIds.clear();
        this.autoPopulatedOriginalValues.clear();
        this.editingAttributeId = null;
        this.editingArrayIndex = null;
        this.editingTruthValueText = '';
        navigationDecision$.next(true); // Allow navigation
        navigationDecision$.complete();
      },
      onCancel: () => {
        navigationDecision$.next(false); // Block navigation
        navigationDecision$.complete();
      }
    });
  }

  private setupPdfWheelZoom(): void {
    if (this.pdfContainer?.nativeElement) {
      this.pdfContainer.nativeElement.addEventListener('wheel', this.boundWheelHandler, { passive: false });
    }
  }

  private onPdfWheel(event: WheelEvent): void {
    // Only zoom when Ctrl key is held (standard zoom behavior)
    if (!event.ctrlKey) return;
    
    // Prevent browser zoom
    event.preventDefault();
    
    if (!this.pdfSrc) return;
    
    const viewer = this.getPdfViewerScrollContainer();
    if (!viewer) return;
    
    // Use viewer-relative cursor coordinates so zoom anchors to the visible PDF viewport.
    const viewerRect = viewer.getBoundingClientRect();
    const cursorX = Math.min(Math.max(event.clientX - viewerRect.left, 0), viewer.clientWidth);
    const cursorY = Math.min(Math.max(event.clientY - viewerRect.top, 0), viewer.clientHeight);
    
    // Store old zoom
    const oldZoom = this.zoom;
    
    // Calculate zoom delta - smooth increments (10% per scroll tick)
    const zoomDelta = event.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.min(3.0, Math.max(0.5, this.zoom + zoomDelta));
    
    if (newZoom === oldZoom) return;
    
    this.applyZoomWithAnchor(newZoom, cursorX, cursorY);
    
    // FIX: Re-render bounding boxes after wheel/trackpad zoom
    // Wait for scroll adjustment to complete before re-rendering
    setTimeout(() => {
      this.reRenderBoundingBoxesAfterZoom();
    }, 100);
  }

  // Tooltip methods
  showTooltip(event: MouseEvent, text: string): void {
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    
    this.tooltipText = text || 'No prompt available';
    // Position below and to the right of the icon
    this.tooltipTop = rect.bottom + 10;
    this.tooltipLeft = rect.right + 10;
    
    // Make sure tooltip doesn't go off-screen on the right
    const maxLeft = window.innerWidth - 380; // 350px max-width + 30px padding
    if (this.tooltipLeft > maxLeft) {
      this.tooltipLeft = maxLeft;
    }
    
    this.tooltipVisible = true;
  }

  hideTooltip(): void {
    this.tooltipVisible = false;
  }

  // ============================================
  // BOUNDING BOX HIGHLIGHTING METHODS (User Story 7126)
  // ============================================

  /**
   * Handle click on an attribute field row to navigate and highlight bounding box
   * Implements AC1-AC10 from User Story 7126
   * 
   * @param attributeId - The attribute ID that was clicked
   */
  onAttributeFieldClick(attributeId: number): void {
    const sourceData = this.fieldSourceDataMap.get(attributeId);
    // AC7: Re-click same field - re-focus on bounding box
    if (this.highlightedAttributeId === attributeId && sourceData?.boundingBoxes?.length) {
      this.scrollToBoundingBox();
      return;
    }
    
    // AC6: Clear previous highlight
    this.clearBoundingBoxHighlight();
    
    // AC10: No page number AND no bounding box - show first page (BAU)
    if (!sourceData?.pageNumber) {
      this.currentPage = 1;
      this.pageInput = 1;
      this.scrollToPage(1);
      this.cdr.detectChanges();
      return;
    }
    
    // AC1 & AC9: Navigate to the page (even if no bounding box)
    this.currentPage = sourceData.pageNumber;
    this.pageInput = sourceData.pageNumber;
    
    // AC9: If no bounding box data, just navigate to page (no highlight)
    if (!sourceData.boundingBoxes || sourceData.boundingBoxes.length === 0) {
      this.scrollToPage(sourceData.pageNumber);
      this.cdr.detectChanges();
      return;
    }
    
    // AC2-AC5: Has bounding box - set up highlight. Under virtual scroll a far
    // page has no DOM element yet, so scroll to it first; the retry loop then
    // finds the wrapper once the strategy stamps + the canvas paints it.
    this.highlightedAttributeId = attributeId;
    this.cdr.detectChanges();
    if (!this.getRenderedPageElement(sourceData.pageNumber)) {
      this.scrollToPage(sourceData.pageNumber);
    }
    this.renderBoundingBoxOverlaysWithRetry(sourceData, 0);
  }

  /**
   * Scroll to a specific page in the PDF viewer
   * Required because [show-all]="true" renders all pages in a scrollable container
   * and setting currentPage does NOT automatically scroll
   * 
   * @param pageNumber - 1-indexed page number to scroll to
   */
  private scrollToPage(pageNumber: number): void {
    // Index-based: the virtual-scroll strategy owns the offset table, so this
    // works even when the target page isn't stamped into the DOM yet.
    const viewport = this.lazyViewer?.viewport;
    if (!viewport || !this.totalPages) {
      return;
    }
    const index = Math.min(Math.max(0, pageNumber - 1), this.totalPages - 1);
    viewport.scrollToIndex(index, 'smooth');
  }

  /**
   * Render bounding box overlays with retry logic
   * Waits for the page element to be available before rendering
   * 
   * @param sourceData - Field source data containing page and bbox info
   * @param retryCount - Current retry attempt (max 10 retries with increasing delays)
   */
  private renderBoundingBoxOverlaysWithRetry(sourceData: FieldSourceData, retryCount: number, generation?: number): void {
    const gen = generation ?? this._bboxRenderGeneration;

    if (gen !== this._bboxRenderGeneration) {
      return;
    }

    const maxRetries = 20;
    const retryDelay = 150;

    const pageElement = this.getRenderedPageElement(sourceData.pageNumber);
    const isPageStable = pageElement && pageElement.offsetWidth > 0 && pageElement.offsetHeight > 0;

    if (!isPageStable) {
      if (retryCount < maxRetries) {
        setTimeout(() => {
          this.renderBoundingBoxOverlaysWithRetry(sourceData, retryCount + 1, gen);
        }, retryDelay);
        return;
      } else {
        return;
      }
    }

    if (gen !== this._bboxRenderGeneration) {
      return;
    }

    this.renderBoundingBoxOverlays(sourceData).catch(err => {
    });
  }

  /**
   * Render bounding box overlays on the PDF viewer
   * Transforms PDF coordinates to pixel coordinates and injects overlay elements into DOM
   * 
   * @param sourceData - Field source data containing page and bbox info
   */
  private async renderBoundingBoxOverlays(sourceData: FieldSourceData, recenter: boolean = true): Promise<void> {
    // Clear any existing overlays
    this.clearBoundingBoxOverlayElements();
    this.activeBoundingBoxes = [];
    
    if (!sourceData.boundingBoxes || sourceData.boundingBoxes.length === 0) {
      return;
    }
    
    // Get the rendered page element - we inject overlays INTO this element
    // so they automatically rotate with CSS transforms!
    const pageElement = this.getRenderedPageElement(sourceData.pageNumber);
    if (!pageElement) {
      return;
    }
    
    // Ensure page element can contain absolute-positioned children
    if (getComputedStyle(pageElement).position === 'static') {
      pageElement.style.position = 'relative';
    }
    
    // Get page width: prefer backend value (matches bbox coordinate space), then pdf.js cache, then fallback
    const cachedPdfWidth = this.pdfPageWidths.get(sourceData.pageNumber);
    const pdfPageWidth = (sourceData.pageWidth && sourceData.pageWidth > 0)
      ? sourceData.pageWidth
      : (cachedPdfWidth || 612);
    
    // Calculate scale factor (rendered width / original PDF width)
    const renderedWidth = pageElement.offsetWidth;
    const scale = renderedWidth / pdfPageWidth;
    
    // Get current rotation for logging
    const userRotation = this.pageRotations.get(sourceData.pageNumber) || 0;
    // AC3: Support multiple bounding boxes per field (numbered)
    // Use boundingBoxIndices if available (deduplicated), otherwise create sequential indices
    const indicesMap = sourceData.boundingBoxIndices || sourceData.boundingBoxes.map((_, i) => [i + 1]);
    
    // For array fields with new entries: only render bboxes for unchanged entries (index < originalArrayLength)
    let maxBboxIndex = sourceData.boundingBoxes.length;
    if (sourceData.originalArrayLength !== undefined && this.highlightedAttributeId !== null) {
      const highlightedAttr = this.attributes.find(a => a.id === this.highlightedAttributeId);
      if (highlightedAttr && highlightedAttr.is_array === true) {
        // Get current array length
        const currentArrayLength = this.getValueAsArray(this.highlightedAttributeId).length;
        
        // If user added new entries, only render bboxes for the original entries
        if (currentArrayLength > sourceData.originalArrayLength) {
          maxBboxIndex = sourceData.originalArrayLength;
        }
      }
    }
    
    // TABLE ATTRIBUTE MERGE: When the whole table attribute row is selected
    // (not a specific cell), merge all bboxes into one unified bounding box.
    if (!this.highlightedTableCell && sourceData.cellMetadata && sourceData.cellMetadata.length > 0) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (let index = 0; index < sourceData.boundingBoxes.length; index++) {
        if (index >= maxBboxIndex) continue;
        const bbox = sourceData.boundingBoxes[index];
        if (bbox.length < 4) continue;
        if (bbox[0] >= bbox[2] || bbox[1] >= bbox[3]) continue;
        if (bbox[0] < minX) minX = bbox[0];
        if (bbox[1] < minY) minY = bbox[1];
        if (bbox[2] > maxX) maxX = bbox[2];
        if (bbox[3] > maxY) maxY = bbox[3];
      }

      if (minX < Infinity) {
        const mergedOverlay = {
          left: minX * scale,
          top: minY * scale,
          width: (maxX - minX) * scale,
          height: (maxY - minY) * scale,
          index: 1
        };
        this.activeBoundingBoxes.push(mergedOverlay);
        this.createBoundingBoxOverlayElement(pageElement, mergedOverlay, []);
      }

      if (recenter) {
        requestAnimationFrame(() => { this.scrollToBoundingBox(); });
      }
      return;
    }

    // Process each bounding box - NO rotation transformation needed!
    // Overlays are injected INTO the page element, so they automatically
    // rotate with the CSS transform applied to the page.
    for (let index = 0; index < sourceData.boundingBoxes.length; index++) {
      // Skip bboxes for newly added entries in array fields
      if (index >= maxBboxIndex) {
        continue;
      }
      
      // TABLE CELL FILTERING: Skip bboxes that don't match the highlighted table cell
      if (this.highlightedTableCell && sourceData.cellMetadata && sourceData.cellMetadata[index]) {
        const cellMeta = sourceData.cellMetadata[index];
        const isMatchingCell = 
          cellMeta.rowIndex === this.highlightedTableCell.rowIndex &&
          cellMeta.columnName === this.highlightedTableCell.columnName;
        
        if (!isMatchingCell) {
          continue;
        } else {
        }
      }
      
      const bbox = sourceData.boundingBoxes[index];
      if (bbox.length < 4) continue;
      
      // bbox format: [x0, y0, x1, y1] = [TopLeftX, TopLeftY, BottomRightX, BottomRightY]
      // Coordinates are already in HTML space (top-left origin, Y increases downward)
      // NO transformation needed - CSS handles rotation automatically!
      const [x0, y0, x1, y1] = bbox;

      // Apply scale with 20% padding ratio (matching source page)
      const BOUNDING_BOX_PADDING_RATIO = 0.2;
      const rawWidth = (x1 - x0) * scale;
      const rawHeight = (y1 - y0) * scale;
      const padX = rawWidth * (BOUNDING_BOX_PADDING_RATIO / 2);
      const padY = rawHeight * (BOUNDING_BOX_PADDING_RATIO / 2);

      const overlay = {
        left: x0 * scale - padX,
        top: y0 * scale - padY,
        width: rawWidth + padX * 2,
        height: rawHeight + padY * 2,
        index: index + 1  // 1-based numbering for display
      };
      
      // Get the original entry indices for this unique bounding box
      const originalIndices = indicesMap[index] || [];
      this.activeBoundingBoxes.push(overlay);
      
      // Inject overlay INTO the page element (not scroll container)
      // This makes overlays automatically rotate with the CSS transform!
      this.createBoundingBoxOverlayElement(pageElement, overlay, originalIndices);
    }
    
    // AC5: Scroll to make highlighted content visible (skipped on passive
    // re-injection after CDK wrapper recreation, so it can't fight the user's scroll)
    if (recenter) {
      requestAnimationFrame(() => {
        this.scrollToBoundingBox();
      });
    }
  }

  /**
   * Create a bounding box overlay element and inject it into the page element
   * @param container - The page element to inject the overlay into (for automatic CSS rotation)
   * @param overlay - Position and size of the overlay
   * @param indices - Array of original entry indices for this bounding box (e.g., [1, 2, 3])
   */
  private createBoundingBoxOverlayElement(
    container: HTMLElement, 
    overlay: { left: number; top: number; width: number; height: number; index: number },
    indices: number[]
  ): void {
    const overlayEl = document.createElement('div');
    overlayEl.className = 'bounding-box-overlay injected-bbox';
    overlayEl.style.position = 'absolute';
    overlayEl.style.left = `${overlay.left}px`;
    overlayEl.style.top = `${overlay.top}px`;
    overlayEl.style.width = `${overlay.width}px`;
    overlayEl.style.height = `${overlay.height}px`;
    overlayEl.style.backgroundColor = 'rgba(37, 99, 235, 0.12)';
    overlayEl.style.border = '1px solid rgba(37, 99, 235, 0.9)';
    overlayEl.style.borderRadius = '2px';
    overlayEl.style.pointerEvents = 'none';
    overlayEl.style.zIndex = '100';
    overlayEl.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.15)';
    
    // Add number badge if we have indices to display (for array attributes with multiple entries)
    if (indices && indices.length > 0) {
      const badge = document.createElement('span');
      badge.className = 'bbox-number';
      badge.textContent = indices.join(', '); // Format: "1, 2, 3"
      badge.style.position = 'absolute';
      badge.style.top = '-16px';  // Closer to border
      badge.style.left = '0px';
      badge.style.height = '14px';
      badge.style.padding = '0 4px';  // Auto-expand width based on content
      badge.style.background = '#2563eb';
      badge.style.color = 'white';
      badge.style.borderRadius = '2px';  // Square with slight rounding
      badge.style.fontSize = '10px';
      badge.style.fontWeight = '600';
      badge.style.display = 'flex';
      badge.style.alignItems = 'center';
      badge.style.justifyContent = 'center';
      badge.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.2)';
      badge.style.whiteSpace = 'nowrap';  // Prevent wrapping of numbers
      overlayEl.appendChild(badge);
    }
    
    container.appendChild(overlayEl);
  }

  /**
   * Clear injected bounding box overlay elements from DOM
   */
  private clearBoundingBoxOverlayElements(): void {
    const existingOverlays = document.querySelectorAll('.injected-bbox');
    existingOverlays.forEach(el => el.remove());
  }

  /**
   * Get the rendered page element from the PDF viewer
   * 
   * @param pageNumber - 1-indexed page number
   * @returns HTMLElement or null
   */
  private getRenderedPageElement(pageNumber: number): HTMLElement | null {
    if (!this.pdfContainer?.nativeElement) {
      return null;
    }
    
    // The lazy viewer stamps pages as .pdf-page-wrapper[data-page="N"]; only
    // pages near the viewport exist in the DOM (CDK virtual scroll).
    const viewer = this.pdfContainer.nativeElement;
    const pageElement = viewer.querySelector(`[data-page="${pageNumber}"]`) as HTMLElement;

    return pageElement;
  }

  // NOTE: getPageHeight() and transformBboxForUserRotation() methods removed
  // These are no longer needed since overlays are now injected INTO the page element
  // and automatically rotate with CSS transforms. This simplifies the codebase
  // and eliminates 100+ lines of complex coordinate transformation logic.

  /**
   * Scroll the PDF viewer to bring the bounding box into view
   * Implements AC5: Auto-focus highlighted content
   * 
   * Now that overlays are injected INTO the page element (for automatic CSS rotation),
   * firstBox.top is relative to the page element, so we ADD pageElement.offsetTop.
   */
  private scrollToBoundingBox(): void {
    if (this.activeBoundingBoxes.length === 0) {
      return;
    }
    
    const firstBox = this.activeBoundingBoxes[0];
    const scrollContainer = this.getPdfViewerScrollContainer();
    
    if (!scrollContainer) {
      return;
    }
    
    // Get the highlighted attribute to find its page number
    const highlightedAttr = this.highlightedAttributeId !== null 
      ? this.fieldSourceDataMap.get(this.highlightedAttributeId) 
      : null;
    
    if (!highlightedAttr) {
      return;
    }
    
    // Get the page element to calculate absolute position
    const pageElement = this.getRenderedPageElement(highlightedAttr.pageNumber);
    if (!pageElement) {
      return;
    }
    
    const containerRect = scrollContainer.getBoundingClientRect();

    // firstBox.top is relative to the page element. Under CDK virtual scroll the
    // wrapper sits inside a translated content wrapper, so offsetTop is NOT the
    // scroll offset — derive it from live rects + current scrollTop instead.
    const pageRect = pageElement.getBoundingClientRect();
    const absoluteTop = (pageRect.top - containerRect.top) + scrollContainer.scrollTop + firstBox.top;

    // Calculate target: position bounding box in upper-third of viewport
    const targetScrollTop = absoluteTop - (containerRect.height / 3) + 50;
    
    scrollContainer.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: 'smooth'
    });
  }

  /**
   * Clear all bounding box highlights
   * Called on outside click, field deselection, or file change
   */
  clearBoundingBoxHighlight(): void {
    this._bboxRenderGeneration++;
    this.highlightedAttributeId = null;
    this.highlightedTableCell = null;  // Clear table cell highlighting
    this.activeBoundingBoxes = [];
    // Clear injected DOM elements
    this.clearBoundingBoxOverlayElements();
    this.cdr.detectChanges();
  }

  // ────────────────────────────────────────────────────────────────────────
  // Manual bounding-box snip tool (per attribute)
  // ────────────────────────────────────────────────────────────────────────

  canSnipForAttribute(_attributeId: number): boolean {
    return !!this.selectedFile && this.totalPages > 0 && !this.authService.isReadOnly;
  }

  isSnipActive(): boolean {
    return this.activeSnipAttributeId !== null;
  }

  getActiveSnipAttributeLabel(): string {
    if (this.activeSnipAttributeId === null) {
      return '';
    }
    const attribute = this.attributes.find(a => a.id === this.activeSnipAttributeId);
    return attribute?.attributeLabel ?? '';
  }

  startSnipForAttribute(attributeId: number): void {
    if (!this.canSnipForAttribute(attributeId)) {
      return;
    }

    // If a snip for a different attribute is already active, cancel it first.
    if (this.activeSnipAttributeId !== null && this.activeSnipAttributeId !== attributeId) {
      this.unmountSnipOverlay();
    }

    // Non-array header click → direct snip for index 0. Array per-value snips use
    // the inline icon inside each value-box via startSnipForValue().
    this.beginSnipForAttributeAndIndex(attributeId, 0);
  }

  startSnipForValue(attributeId: number, valueIndex: number, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.canSnipForAttribute(attributeId)) {
      return;
    }
    // Exit any in-progress edit on this attribute so the display value-box
    // (with the inline snip icon) resumes after the snip is cancelled or saved.
    if (this.editingAttributeId === attributeId) {
      this.editingAttributeId = null;
      this.editingArrayIndex = null;
      this.editingTruthValueText = '';
    }
    if (this.activeSnipAttributeId !== null && this.activeSnipAttributeId !== attributeId) {
      this.unmountSnipOverlay();
    }
    this.beginSnipForAttributeAndIndex(attributeId, valueIndex);
  }

  canUndoSnipForValue(attributeId: number, valueIndex: number): boolean {
    return !!this.pendingSnipBboxByAttributeId.get(attributeId)?.has(valueIndex);
  }

  isValueBoxEmpty(attributeId: number, valueIndex: number): boolean {
    if (this.isArrayValue(attributeId)) {
      const arr = this.getDisplayValueAsArray(attributeId);
      const v = arr?.[valueIndex];
      return !v || !String(v).trim();
    }
    const v = this.getDisplayValue(attributeId);
    return !v || !v.trim();
  }

  onSnipToggleClick(attributeId: number, valueIndex: number, event: MouseEvent): void {
    if (this.canUndoSnipForValue(attributeId, valueIndex)) {
      this.undoSnipForValue(attributeId, valueIndex, event);
    } else {
      this.startSnipForValue(attributeId, valueIndex, event);
    }
  }

  undoSnipForValue(attributeId: number, valueIndex: number, event?: MouseEvent): void {
    event?.stopPropagation();
    const inner = this.pendingSnipBboxByAttributeId.get(attributeId);
    if (!inner?.has(valueIndex)) {
      return;
    }
    inner.delete(valueIndex);

    if (inner.size === 0) {
      // All pending snips reverted — restore pre-snip source data.
      this.pendingSnipBboxByAttributeId.delete(attributeId);
      this.pendingSnipModifiedAttributeIds.delete(attributeId);
      const previous = this.pendingSnipPreviousSourceDataByAttributeId.get(attributeId) ?? null;
      this.pendingSnipPreviousSourceDataByAttributeId.delete(attributeId);
      if (previous) {
        const restored = this.cloneFieldSourceData(previous);
        this.fieldSourceDataMap.set(attributeId, restored);
        if (this.highlightedAttributeId === attributeId) {
          this.clearBoundingBoxHighlight();
          this.highlightedAttributeId = attributeId;
          this.renderBoundingBoxOverlaysWithRetry(restored, 0);
        }
      } else {
        this.fieldSourceDataMap.delete(attributeId);
        if (this.highlightedAttributeId === attributeId) {
          this.clearBoundingBoxHighlight();
        }
      }
      if (!this.originalFieldSourceDataMap.has(attributeId)) {
        this.manuallyOverriddenAttributeIds.delete(attributeId);
      }
    } else {
      // Other pending slots remain — rebuild fieldSourceDataMap from what's left.
      const sorted = Array.from(inner.entries()).sort(([a], [b]) => a - b);
      const updated: FieldSourceData = {
        pageNumber: sorted[0][1].pageNumber,
        pageWidth: sorted[0][1].pageWidth,
        boundingBoxes: sorted.map(([, p]) => [...p.bbox]),
        boundingBoxIndices: sorted.map(([i]) => [i + 1]),
      };
      this.fieldSourceDataMap.set(attributeId, updated);
      if (this.highlightedAttributeId === attributeId) {
        this.clearBoundingBoxHighlight();
        this.highlightedAttributeId = attributeId;
        this.renderBoundingBoxOverlaysWithRetry(updated, 0);
      }
    }
    this.cdr.markForCheck();
  }

  private beginSnipForAttributeAndIndex(attributeId: number, arrayIndex: number): void {
    const existing = this.fieldSourceDataMap.get(attributeId);
    const targetPage = (existing?.pageNumber && existing.pageNumber > 0)
      ? existing.pageNumber
      : (this.currentPage > 0 ? this.currentPage : 1);

    // Snapshot the auto-filled original the first time this attribute is overridden,
    // so undo can restore it later.
    if (existing && !this.originalFieldSourceDataMap.has(attributeId)) {
      this.originalFieldSourceDataMap.set(attributeId, this.cloneFieldSourceData(existing));
    }

    this.clearBoundingBoxHighlight();
    if (targetPage !== this.currentPage) {
      this.scrollToPage(targetPage);
    }
    this.activeSnipAttributeId = attributeId;
    this.activeSnipPage = targetPage;
    this.activeSnipArrayIndex = arrayIndex;
    this.cdr.markForCheck();

    // Defer mounting so the page is scrolled into view and the viewer has settled.
    setTimeout(() => this.mountSnipOverlay(targetPage), 50);
  }

  private mountSnipOverlay(pageNumber: number, retryCount: number = 0): void {
    if (!this.snipHost) {
      return;
    }
    const pageElement = this.getRenderedPageElement(pageNumber);
    if (!pageElement) {
      // Page not yet rendered (smooth-scroll + virtual-scroll lazy render).
      // Retry up to 20 times at 150ms spacing — 3s window for far pages.
      const maxRetries = 20;
      const retryDelay = 150;
      if (retryCount < maxRetries) {
        setTimeout(() => {
          // Snip may have been cancelled or redirected in the meantime; bail if so.
          if (this.activeSnipAttributeId !== null && this.activeSnipPage === pageNumber) {
            this.mountSnipOverlay(pageNumber, retryCount + 1);
          }
        }, retryDelay);
      }
      return;
    }

    this.unmountSnipOverlay();

    const ref = this.snipHost.createComponent(BboxSnipOverlayComponent);
    ref.setInput('attributeLabel', this.getActiveSnipAttributeLabel());
    ref.instance.boxDrawn.subscribe((rect: SnipRectPx) => this.onSnipBoxDrawn(rect));
    ref.instance.cancelled.subscribe(() => this.onSnipCancelled());

    // Ensure the page element is a positioned ancestor so inset:0 lays over it.
    const currentPosition = window.getComputedStyle(pageElement).position;
    if (currentPosition === 'static' || !currentPosition) {
      this.renderer.setStyle(pageElement, 'position', 'relative');
    }
    this.renderer.appendChild(pageElement, ref.location.nativeElement);

    this.snipOverlayRef = ref;

    // Reparent the instruction bar to .pdf-container so it stays pinned below
    // the Preview header while the user scrolls the PDF to pick a page spot.
    // The drag-capture overlay stays on the page element (coords stay correct).
    queueMicrotask(() => {
      const bar = (ref.location.nativeElement as HTMLElement)
        .querySelector<HTMLElement>('.snip-instruction-bar');
      const container = this.pdfContainer?.nativeElement;
      if (!bar || !container) {
        return;
      }
      this.renderer.appendChild(container, bar);
      this.renderer.setStyle(bar, 'position', 'absolute');
      this.renderer.setStyle(bar, 'top', '0.5rem');
      this.renderer.setStyle(bar, 'left', '50%');
      this.renderer.setStyle(bar, 'transform', 'translateX(-50%)');
      this.renderer.setStyle(bar, 'z-index', '600');
      this.snipInstructionBarEl = bar;
    });
  }

  private unmountSnipOverlay(): void {
    // Remove the reparented instruction bar BEFORE destroying the overlay
    // component so Angular doesn't try to clean up a detached DOM node.
    if (this.snipInstructionBarEl?.parentNode) {
      this.snipInstructionBarEl.parentNode.removeChild(this.snipInstructionBarEl);
    }
    this.snipInstructionBarEl = null;
    if (this.snipOverlayRef) {
      this.snipOverlayRef.destroy();
      this.snipOverlayRef = null;
    }
  }

  onSnipCancelled(): void {
    this.unmountSnipOverlay();
    this.activeSnipAttributeId = null;
    this.activeSnipPage = null;
    this.cdr.markForCheck();
  }

  onSnipBoxDrawn(rectPx: SnipRectPx): void {
    const attributeId = this.activeSnipAttributeId;
    const pageNumber = this.activeSnipPage;
    if (attributeId === null || pageNumber === null || !this.selectedFile) {
      this.onSnipCancelled();
      return;
    }

    const pageElement = this.getRenderedPageElement(pageNumber);
    if (!pageElement) {
      this.onSnipCancelled();
      return;
    }

    const renderedWidth = pageElement.getBoundingClientRect().width;
    const existing = this.fieldSourceDataMap.get(attributeId);
    const pageWidth = (existing?.pageWidth && existing.pageWidth > 0)
      ? existing.pageWidth
      : (this.pdfPageWidths.get(pageNumber) ?? 612);

    if (!renderedWidth || renderedWidth <= 0) {
      this.notificationService.showError('Unable to measure PDF page. Please try again.');
      return;
    }

    const scale = pageWidth / renderedWidth;
    // Round to integers to match the backend's integer-coordinate format for
    // auto-extracted bboxes (sub-pixel precision is irrelevant for annotation).
    const x0 = Math.round(rectPx.left * scale);
    const y0 = Math.round(rectPx.top * scale);
    const x1 = Math.round((rectPx.left + rectPx.width) * scale);
    const y1 = Math.round((rectPx.top + rectPx.height) * scale);
    const newBbox: [number, number, number, number] = [x0, y0, x1, y1];

    const arrayIndex = this.activeSnipArrayIndex ?? 0;

    // Snapshot the pre-snip state ONCE per attribute (first pending snip only),
    // so Undo can restore whatever bboxes were present (auto-filled or empty).
    if (!this.pendingSnipPreviousSourceDataByAttributeId.has(attributeId)) {
      this.pendingSnipPreviousSourceDataByAttributeId.set(
        attributeId,
        existing ? this.cloneFieldSourceData(existing) : null,
      );
    }

    // Add or replace the pending snip for this (attribute, arrayIndex) slot.
    const inner = this.pendingSnipBboxByAttributeId.get(attributeId) ?? new Map();
    inner.set(arrayIndex, { pageNumber, pageWidth, bbox: newBbox });
    this.pendingSnipBboxByAttributeId.set(attributeId, inner);

    const sorted = Array.from(inner.entries()).sort(([a], [b]) => a - b);
    const updated: FieldSourceData = {
      pageNumber: sorted[0][1].pageNumber,
      pageWidth: sorted[0][1].pageWidth,
      boundingBoxes: sorted.map(([, p]) => [...p.bbox]),
      boundingBoxIndices: sorted.map(([i]) => [i + 1]),
    };

    this.fieldSourceDataMap.set(attributeId, updated);
    this.manuallyOverriddenAttributeIds.add(attributeId);
    this.pendingSnipModifiedAttributeIds.add(attributeId);

    this.onSnipCancelled();
    this.highlightedAttributeId = attributeId;
    this.renderBoundingBoxOverlaysWithRetry(updated, 0);
    this.cdr.markForCheck();
  }

  canUndoSnipForAttribute(attributeId: number): boolean {
    // Undo is only offered while a snip is pending (not yet saved). After Save Changes,
    // the bbox is persisted; the user can redraw by clicking crop_free again.
    return this.pendingSnipBboxByAttributeId.has(attributeId);
  }

  undoSnipForAttribute(attributeId: number, event?: MouseEvent): void {
    event?.stopPropagation();
    if (!this.selectedFile) {
      return;
    }

    // Pending (unsaved) snip — purely local revert, no network call.
    if (this.pendingSnipBboxByAttributeId.has(attributeId)) {
      const previousSourceData = this.pendingSnipPreviousSourceDataByAttributeId.get(attributeId) ?? null;
      if (previousSourceData) {
        const restored = this.cloneFieldSourceData(previousSourceData);
        this.fieldSourceDataMap.set(attributeId, restored);
        if (this.highlightedAttributeId === attributeId) {
          this.clearBoundingBoxHighlight();
          this.highlightedAttributeId = attributeId;
          this.renderBoundingBoxOverlaysWithRetry(restored, 0);
        }
      } else {
        this.fieldSourceDataMap.delete(attributeId);
        if (this.highlightedAttributeId === attributeId) {
          this.clearBoundingBoxHighlight();
        }
      }
      this.pendingSnipBboxByAttributeId.delete(attributeId);
      this.pendingSnipPreviousSourceDataByAttributeId.delete(attributeId);
      this.pendingSnipModifiedAttributeIds.delete(attributeId);
      // Only drop the "manually overridden" flag if there is no previously-persisted snip
      // (i.e. no snapshot in originalFieldSourceDataMap).
      if (!this.originalFieldSourceDataMap.has(attributeId)) {
        this.manuallyOverriddenAttributeIds.delete(attributeId);
      }
      this.cdr.markForCheck();
      return;
    }

    // Saved snip — call the revert endpoint and restore the original on success.
    const fileId = String(this.selectedFile.id);
    const original = this.originalFieldSourceDataMap.get(attributeId);

    this.truthManagementService.revertSourceAttributeBoundingBox(fileId, {
      file_id: fileId,
      attribute_id: attributeId,
    }).subscribe({
      next: () => {
        if (original) {
          const restored = this.cloneFieldSourceData(original);
          this.fieldSourceDataMap.set(attributeId, restored);
          this.manuallyOverriddenAttributeIds.delete(attributeId);
          this.originalFieldSourceDataMap.delete(attributeId);
          if (this.highlightedAttributeId === attributeId) {
            this.clearBoundingBoxHighlight();
            this.highlightedAttributeId = attributeId;
            this.renderBoundingBoxOverlaysWithRetry(restored, 0);
          }
        } else {
          this.fieldSourceDataMap.delete(attributeId);
          this.manuallyOverriddenAttributeIds.delete(attributeId);
          if (this.highlightedAttributeId === attributeId) {
            this.clearBoundingBoxHighlight();
          }
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.notificationService.showError('Failed to undo bounding box. Please try again.');
      },
    });
  }

  private cloneFieldSourceData(src: FieldSourceData): FieldSourceData {
    return {
      pageNumber: src.pageNumber,
      pageWidth: src.pageWidth,
      boundingBoxes: src.boundingBoxes.map(b => [...b]),
      boundingBoxIndices: src.boundingBoxIndices?.map(r => [...r]),
      originalArrayLength: src.originalArrayLength,
      cellMetadata: src.cellMetadata?.map(cm => ({
        rowIndex: cm.rowIndex,
        columnName: cm.columnName,
        bbox: [...cm.bbox],
      })),
    };
  }

  private resetSnipStateForFileSwitch(): void {
    this.unmountSnipOverlay();
    this.activeSnipAttributeId = null;
    this.activeSnipPage = null;
    this.activeSnipArrayIndex = null;
    this.originalFieldSourceDataMap.clear();
    this.manuallyOverriddenAttributeIds.clear();
    this.pendingSnipBboxByAttributeId.clear();
    this.pendingSnipPreviousSourceDataByAttributeId.clear();
    this.pendingSnipModifiedAttributeIds.clear();
  }

  /**
   * Handle click on PDF container
   * PRODUCTION FIX: Bounding box should PERSIST until user clicks another field
   * Do NOT clear highlight on outside clicks - only clear when a new field is selected
   * The onAttributeFieldClick() method handles clearing previous highlight automatically
   * 
   * @param event - The click event (kept for template binding compatibility)
   */
  onPdfContainerClick(event: Event): void {
    // INTENTIONALLY EMPTY: Bounding box persists until user clicks another attribute field
    // This provides better UX - users can scroll, zoom, and interact with PDF
    // without losing the highlight context
  }

  /**
   * Handle resize divider mouse down event
   * Initiates dragging to resize the PDF viewer / truth values form split
   */
  startResize(event: MouseEvent): void {
    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartSplit = this.splitPosition;

    // Show col-resize cursor globally while dragging
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    document.addEventListener('mousemove', this.onSplitMouseMove);
    document.addEventListener('mouseup', this.onSplitMouseUp);

    // Prevent text selection during drag
    event.preventDefault();
  }

  /**
   * Handle mouse move event while resizing the viewer/form split
   */
  private onSplitMouseMove = (event: MouseEvent): void => {
    if (!this.isDragging) {
      return;
    }

    const wrapper = document.querySelector('.viewer-attributes-wrapper') as HTMLElement;
    if (!wrapper) {
      return;
    }

    const wrapperWidth = wrapper.offsetWidth;
    const deltaX = event.clientX - this.dragStartX;

    // Subtract the divider width so the two panels always sum to 100%
    const dividerWidth = 20;
    const wrapperWidthAdjusted = wrapperWidth - dividerWidth;
    const percentageChange = (deltaX / wrapperWidthAdjusted) * 100;

    let newSplitPosition = this.dragStartSplit + percentageChange;

    // Keep the form panel at its original minimum width (420px) and the
    // viewer panel wide enough that its header controls stay on one row
    const minAttributesWidthPercent = (420 / wrapperWidth) * 100;
    const minViewerWidthPercent = (400 / wrapperWidth) * 100;
    newSplitPosition = Math.max(
      minViewerWidthPercent,
      Math.min(100 - minAttributesWidthPercent, newSplitPosition)
    );

    this.splitPosition = newSplitPosition;
    this.cdr.markForCheck();
  };

  /**
   * Handle mouse up event to stop resizing the viewer/form split
   */
  private onSplitMouseUp = (): void => {
    this.isDragging = false;

    document.body.style.cursor = 'default';
    document.body.style.userSelect = 'auto';

    document.removeEventListener('mousemove', this.onSplitMouseMove);
    document.removeEventListener('mouseup', this.onSplitMouseUp);
  };

  /**
   * Check if an attribute has source data available (for visual indicator)
   * 
   * @param attributeId - Attribute ID to check
   * @returns true if source data exists
   */
  hasSourceData(attributeId: number): boolean {
    return this.fieldSourceDataMap.has(attributeId);
  }

  /**
   * Check if an attribute is currently highlighted
   * 
   * @param attributeId - Attribute ID to check
   * @returns true if this attribute is highlighted
   */
  isAttributeHighlighted(attributeId: number): boolean {
    return this.highlightedAttributeId === attributeId;
  }

  /**
   * Get page width from cached PDF data (User Story 7126)
   * Used when loading bounding box from DB (no page_width from API)
   * 
   * @param pageNumber - 1-indexed page number
   * @returns Page width in PDF points (default 612 for US Letter if not available)
   */
  getPageWidthFromPdf(pageNumber: number): number {
    // Try to get from cache first
    const cachedWidth = this.pdfPageWidths.get(pageNumber);
    if (cachedWidth) {
      return cachedWidth;
    }
    
    // Default to US Letter width if not available
    return 612;
  }

  /**
   * CRITICAL HELPER: Normalize extracted data for consistent bounding box storage
   * 
   * Handles all API response formats:
   * - Non-array fields: boundingBox as [x0, y0, x1, y1], pageNumber as number/string
   * - Array fields: boundingBox as [[x0, y0, x1, y1], ...], pageNumber as number/string/list
   * 
   * Returns consistent format: { pageNumber, boundingBoxes, pageWidth }
   * Ensures page numbers are converted from strings if needed.
   * 
   * @param attrData - Raw API response data (object or array)
   * @param isArrayAttr - Whether this is an array-type field
   * @returns Normalized data object or null if invalid
   */
  private normalizeBoundingBoxData(
    attrData: any,
    isArrayAttr: boolean
  ): { pageNumber: number; boundingBoxes: number[][]; pageWidth: number } | null {
    let pageNumber: number | undefined;
    let boundingBoxes: number[][] = [];
    let pageWidth: number | undefined;

    if (Array.isArray(attrData)) {
      // Array format: iterate over items to collect all bboxes
      attrData.forEach((item, index) => {
        if (!item) return;

        // Extract page number from first valid item (use for navigation)
        if (index === 0) {
          const rawPageNum = item['Page Number'];
          // Handle page number as string or number
          pageNumber = typeof rawPageNum === 'string' ? parseInt(rawPageNum, 10) : rawPageNum;
          pageWidth = item['Page Width'];
        }

        // Extract bounding box - handle both single and array formats
        const bbox = item.BoundingBox || item.bounding_box;
        if (!bbox) return;

        if (Array.isArray(bbox)) {
          if (bbox.length === 0) return;
          
          // Check if first element is array (list of bboxes) or number (single bbox)
          if (Array.isArray(bbox[0])) {
            // Array of arrays: [[x0,y0,x1,y1], ...]
            bbox.forEach(singleBox => {
              if (Array.isArray(singleBox) && singleBox.length >= 4) {
                boundingBoxes.push([singleBox[0], singleBox[1], singleBox[2], singleBox[3]]);
              }
            });
          } else if (typeof bbox[0] === 'number' && bbox.length >= 4) {
            // Single bbox [x0, y0, x1, y1] - wrap in array
            boundingBoxes.push([bbox[0], bbox[1], bbox[2], bbox[3]]);
          }
        }
      });
    } else if (attrData && typeof attrData === 'object') {
      const pageNumberData = attrData['Page Number'];
      const boundingBoxData = attrData.BoundingBox || attrData.bounding_box;
      pageWidth = attrData['Page Width'];

      // ✅ ALIGNED ARRAY STRUCTURE: Handle case where pageNumber and boundingBox are both arrays
      if (Array.isArray(pageNumberData) && Array.isArray(boundingBoxData)) {
        // Both are arrays - iterate through aligned entries (handle non-aligned by using min length)
        pageNumber = undefined;
        const alignedLength = Math.min(boundingBoxData.length, pageNumberData.length);

        for (let i = 0; i < alignedLength; i++) {
          const bbox = boundingBoxData[i];
          const pageNum_raw = pageNumberData[i];

          // Skip if bbox is null or pageNumber is invalid
          if (!bbox || !Array.isArray(bbox) || bbox.length < 4) {
            continue;
          }
          if (pageNum_raw == null || pageNum_raw === 0 || pageNum_raw === '0') {
            continue;
          }

          // Parse page number
          const pageNum = typeof pageNum_raw === 'string' ? parseInt(pageNum_raw, 10) : pageNum_raw;
          if (!pageNum || pageNum <= 0) {
            continue;
          }

          // Valid entry - collect bbox and use first valid page for navigation
          if (!pageNumber) pageNumber = pageNum;
          boundingBoxes.push([bbox[0], bbox[1], bbox[2], bbox[3]]);
        }

        // Log non-aligned warning if arrays have different lengths
        if (boundingBoxData.length !== pageNumberData.length) {
        }
      } else {
        // Non-aligned structure - fallback to original logic for single value structure
        const rawPageNum = attrData['Page Number'];
        pageNumber = typeof rawPageNum === 'string' ? parseInt(rawPageNum, 10) : rawPageNum;

        const bbox = attrData.BoundingBox || attrData.bounding_box;
        if (bbox && Array.isArray(bbox)) {
          if (bbox.length === 0) {
            // Empty bbox
          } else if (Array.isArray(bbox[0])) {
            // Array of arrays: [[x0,y0,x1,y1], ...]
            bbox.forEach(singleBox => {
              if (Array.isArray(singleBox) && singleBox.length >= 4) {
                boundingBoxes.push([singleBox[0], singleBox[1], singleBox[2], singleBox[3]]);
              }
            });
          } else if (typeof bbox[0] === 'number' && bbox.length >= 4) {
            // Single bbox [x0, y0, x1, y1] - wrap in array
            boundingBoxes.push([bbox[0], bbox[1], bbox[2], bbox[3]]);
          }
        }
      }
    }

    // Return null if no valid page number or bounding boxes
    if (!pageNumber || pageNumber <= 0 || boundingBoxes.length === 0) {
      return null;
    }

    return {
      pageNumber,
      boundingBoxes,
      pageWidth: pageWidth || 612  // Default to US Letter width
    };
  }

  // ============================================
  // DATA EXTRACTION HELPER METHODS
  // ============================================

  /**
   * Check if auto-population should be triggered for a file
   * Threshold logic:
   * - If ≥70% of fields are filled -> return false (too much manual work, don't overwrite)
   * - If <10% of fields modified since last extraction -> return true (safe to refresh)
   * - If no prior extraction -> return true (first time)
   */
  private shouldAutoPopulate(fileId: number | string): boolean {
    const totalFields = this.attributes.length;
    if (totalFields === 0) return false;

    // Count filled fields (non-null, non-empty)
    let filledFields = 0;
    for (const attr of this.attributes) {
      if (!attr.id) continue;
      const modifiedVal = this.modifiedValues.get(attr.id);
      const truthVal = this.currentFileTruthValues.get(attr.id)?.value;
      const hasModified = this.modifiedValues.has(attr.id) && toStringValue(modifiedVal) !== '';
      const hasTruthValue = this.currentFileTruthValues.has(attr.id) && toStringValue(truthVal) !== '';
      if (hasModified || hasTruthValue) {
        filledFields++;
      }
    }

    // Calculate fill percentage
    const fillPercentage = (filledFields / totalFields) * 100;

    // Single threshold check: if fill percentage > 10%, skip auto-population
    if (fillPercentage > (this.AUTO_POPULATE_THRESHOLD * 100)) {
      this.modalService.show({
        type: ModalType.INFO,
        size: ModalSize.SMALL,
        title: 'Auto-Population Skipped',
        message: `${Math.round(fillPercentage)}% of fields are already filled. Auto-population only works when ≤10% of fields are filled to preserve your work.`,
        confirmText: 'OK'
      });
      return false;
    }
    // Fill percentage ≤10% -> allow auto-population
    return true;
  }

  /**
   * Poll extraction status until COMPLETED or timeout
   * Uses recursive setTimeout with 2-second intervals
   * @param requestId - The extraction request ID to poll
   * @param fileId - The file ID being extracted (so we can clean up state when done)
   * @param startTime - When polling started (for timeout check)
   */
  private pollExtractionComplete(requestId: string, fileId: number | string, startTime: number = Date.now()): void {
    const elapsed = Date.now() - startTime;
    
    // Timeout check (30 minutes)
    if (elapsed > this.EXTRACTION_TIMEOUT) {
      this.clearExtractionState(fileId);
      this.isOverrideMode = false;
      localStorage.removeItem('truthManagement_extraction'); // Clear saved state
      this.notificationService.showAutoFillError(
        this.selectedDocument?.documentName || '',
        this.selectedFile?.fileName || '',
        'Autofill Failed.',
        'Reason',
        'Technical error'
      );
      this.cdr.detectChanges();
      return;
    }

    // Poll the status
    this.truthManagementService.pollExtractionStatus(requestId).subscribe({
      next: (response) => {
        if (response.status === 'COMPLETED') {
          this.clearExtractionState(fileId);
          localStorage.removeItem('truthManagement_extraction'); // Clear saved state
          
          // Map extracted attributes to truth values (use fileId, not selectedFile)
          if (response.attributes) {
            this.mapExtractedToTruthValues(response.attributes, fileId);
          } else {
            this.notificationService.showError('No data extracted from document');
          }
          
          // Schedule change detection to avoid ExpressionChangedAfterItHasBeenCheckedError
          setTimeout(() => this.cdr.detectChanges(), 0);
        } else if (response.status === 'FAILED') {
          this.clearExtractionState(fileId);
          this.isOverrideMode = false;
          localStorage.removeItem('truthManagement_extraction'); // Clear saved state
          this.notificationService.showAutoFillError(
            this.selectedDocument?.documentName || '',
            this.selectedFile?.fileName || '',
            'Autofill Failed.',
            'Reason',
            'Technical error'
          );
          // Schedule change detection to avoid ExpressionChangedAfterItHasBeenCheckedError
          setTimeout(() => this.cdr.detectChanges(), 0);
        } else {
          // Still PENDING or RUNNING - poll again after 2 seconds
          setTimeout(() => {
            this.pollExtractionComplete(requestId, fileId, startTime);
          }, this.EXTRACTION_POLL_INTERVAL);
        }
      },
      error: (error) => {
        this.clearExtractionState(fileId);
        this.isOverrideMode = false;
        localStorage.removeItem('truthManagement_extraction'); // Clear saved state
        
        // Show error notification
        const fileName = this.selectedFile?.fileName || 'File';
        const documentName = this.selectedDocument?.documentName || this.selectedFile?.fileName || 'Document';
        this.notificationService.showAutoFillError(
          documentName,
          fileName,
          'Autofill Failed.',
          'Reason',
          'Technical error'
        );
        
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Clear extraction state for a specific file
   * Removes from extracting set and cleans up request ID
   */
  private clearExtractionState(fileId: number | string): void {
    this.extractingFiles.delete(fileId);
    this.extractionRequestIds.delete(fileId);
    
    // Update legacy flags
    if (this.extractingFileId === fileId) {
      this.extractingFileId = null;
    }
    
    // If no more files are extracting, clear global flag
    if (this.extractingFiles.size === 0) {
      this.isExtracting = false;
    }
  }

  /**
   * Get file info (name, document name) for a given file ID
   * Looks up from files array to avoid relying on currently selected file
   */
  private getFileInfoForId(fileId: number | string): { fileName: string, documentName: string } {
    // Find the file in the files array
    const file = this.files.find(f => f.id === fileId);
    const fileName = file?.fileName || 'Unknown File';
    const documentName = this.selectedDocument?.documentName || fileName;
    
    return { fileName, documentName };
  }

  /**
   * Store pending extraction results in sessionStorage
   * Used when user switches files during extraction
   */
  private storePendingExtractionResults(fileId: number | string, extractedAttrs: Record<string, any>): void {
    const pendingKey = `truthManagement_pendingResults_${fileId}`;
    const pendingData = {
      fileId,
      extractedAttrs,
      timestamp: Date.now(),
      isOverrideMode: this.isOverrideMode
    };
    
    sessionStorage.setItem(pendingKey, JSON.stringify(pendingData));
  }

  /**
   * Deduplicate bounding boxes and track their original indices
   * @param bboxes Array of bounding boxes [x0, y0, x1, y1]
   * @returns Object with unique bboxes and mapping of original indices to each unique bbox
   */
  private deduplicateBoundingBoxes(bboxes: number[][]): { uniqueBboxes: number[][], indicesMap: number[][] } {
    if (!bboxes || bboxes.length === 0) {
      return { uniqueBboxes: [], indicesMap: [] };
    }

    const uniqueBboxes: number[][] = [];
    const indicesMap: number[][] = [];
    const bboxMap = new Map<string, number>(); // Key: bbox coordinates as string, Value: index in uniqueBboxes

    bboxes.forEach((bbox, originalIndex) => {
      if (!bbox || bbox.length < 4) {
        return; // Skip invalid bboxes
      }

      // Create a unique key for this bounding box (exact coordinate match)
      const bboxKey = `${bbox[0]},${bbox[1]},${bbox[2]},${bbox[3]}`;

      if (bboxMap.has(bboxKey)) {
        // Duplicate found - add original index to existing mapping
        const uniqueIndex = bboxMap.get(bboxKey)!;
        indicesMap[uniqueIndex].push(originalIndex + 1); // 1-based index
      } else {
        // New unique bbox - add to arrays
        const uniqueIndex = uniqueBboxes.length;
        uniqueBboxes.push(bbox);
        indicesMap.push([originalIndex + 1]); // 1-based index
        bboxMap.set(bboxKey, uniqueIndex);
      }
    });
    return { uniqueBboxes, indicesMap };
  }

  /**
   * Check and apply pending extraction results for current file
   * Called when switching to a file
   */
  private checkAndApplyPendingResults(fileId: number | string): void {
    const pendingKey = `truthManagement_pendingResults_${fileId}`;
    const pendingDataStr = sessionStorage.getItem(pendingKey);
    if (!pendingDataStr) {
      return; // No pending results
    }
    
    try {
      const pendingData = JSON.parse(pendingDataStr);
      const age = Date.now() - pendingData.timestamp;
      
      // Only apply if less than 30 minutes old
      if (age > 30 * 60 * 1000) {
        sessionStorage.removeItem(pendingKey);
        return;
      }
      // Apply the results now that we're on the correct file
      this.isOverrideMode = pendingData.isOverrideMode;
      this.applyStoredExtractionResults(pendingData.extractedAttrs, fileId);
      
      // DON'T clean up sessionStorage yet - keep it so user can navigate back and forth
      // Pending results will be cleared only after successful save or when they expire
    } catch (error) {
      // Still keep in sessionStorage - user can try again or fix the issue
    }
  }

  /**
   * Clear pending extraction results from sessionStorage
   * Called when user discards changes (clicks "No" on save dialog)
   */
  private clearPendingExtractionResults(fileId: number | string): void {
    const pendingKey = `truthManagement_pendingResults_${fileId}`;
    sessionStorage.removeItem(pendingKey);
  }

  /**
   * Apply stored extraction results (used for pending results)
   */
  private applyStoredExtractionResults(extractedAttrs: Record<string, any>, fileId: number | string): void {
    let autoFilledCount = 0;
    let overriddenCount = 0;
    if (this.attributes.length > 0) {
    }
    
    for (const [attrKey, attrData] of Object.entries(extractedAttrs)) {
      const attribute = this.findMatchingAttribute(attrKey);
      
      if (!attribute || !attribute.id) {
        continue;
      }
      
      let extractedValue: string | string[] | TableData = '';
      let isTableExtraction = false;
      
      // ============================================
      // TABLE FIELD HANDLING (consistent with mapExtractedToTruthValues)
      // ============================================
      
      // Check if this is table-formatted data AND the attribute is a table field
      if (Array.isArray(attrData) && this.isTableFormatData(attrData) && attribute.isTableField) {
        // Extract table data from API response format to TableData dictionary format
        const { tableData, columns } = this.extractTableDataFromApi(attrData);
        
        if (Object.keys(tableData).length > 0 && columns.length > 0) {
          extractedValue = tableData;
          isTableExtraction = true;
        }
        // else: leave extractedValue as '' so it flows through the empty-value handling below
      } else if (Array.isArray(attrData)) {
        // Check if this attribute is marked as array
        const isArrayAttr = attribute.is_array === true;

        if (isArrayAttr) {
          // Extract ALL items as native array (v2 API format)
          extractedValue = attrData
            .map(item => item?.Value || item?.value || '')
            .filter(v => v.trim());
        } else {
          // Single value attribute - take first only
          extractedValue = attrData[0]?.Value || attrData[0]?.value || '';
        }
      } else if (attrData && typeof attrData === 'object') {
        extractedValue = attrData.Value || attrData.value || '';
      }

      // Check if overriding existing value (for override count)
      const existingModifiedValue = this.modifiedValues.get(attribute.id);
      const existingTruthValue = this.currentFileTruthValues.get(attribute.id)?.value;
      const hasExistingValue = (existingModifiedValue && toStringValue(existingModifiedValue) !== '') ||
                               (existingTruthValue && toStringValue(existingTruthValue) !== '');

      // Classify the extraction: empty/invalid (including an explicit "N/A"/"NA") results are
      // ignored outright - not applied, not counted, not shown, and never auto-toggled to N/A.
      // The N/A state stays a manual, user-initiated action.
      const classification = this.classifyAutofillValue(attribute, extractedValue);

      if (classification === 'empty' || classification === 'invalid') {
        continue;
      }

      if (this.isOverrideMode && hasExistingValue) {
        overriddenCount++;
      }

      // Apply the value
      this.modifiedValues.set(attribute.id, extractedValue);
      this.modifiedAttributeIds.add(attribute.id);
      this.autoPopulatedAttributeIds.add(attribute.id);
      this.autoPopulatedOriginalValues.set(attribute.id, extractedValue);

      if (this.notApplicableAttributes.has(attribute.id)) {
        this.notApplicableAttributes.delete(attribute.id);
      }

      // Store bounding box source data for highlighting (User Story 7126)
      // Handle both table and non-table fields
      if (isTableExtraction && Array.isArray(attrData)) {
        // Table field: extract per-cell bounding boxes
        const { tableData: _ } = this.extractTableDataFromApi(attrData);
        const tableColumns = Object.keys(_);

        if (tableColumns.length > 0) {
          const { boundingBoxes, pageNumber, pageWidth, cellMetadata } = this.extractTableBoundingBoxes(attrData, tableColumns);

          if (boundingBoxes.length > 0) {
            // For table fields, store all bboxes without deduplication (each cell is unique)
            this.fieldSourceDataMap.set(attribute.id, {
              pageNumber,
              boundingBoxes: boundingBoxes,
              boundingBoxIndices: boundingBoxes.map((_, i) => [i + 1]), // 1-based indices for each bbox
              pageWidth,
              originalArrayLength: attrData.length, // Number of rows in table
              cellMetadata: cellMetadata  // Store per-cell mapping for highlighting specific cells
            });
          }
        }
      } else {
        // Non-table field: use existing bbox normalization
        const normalizedBboxData = this.normalizeBoundingBoxData(attrData, attribute.is_array === true);

        if (normalizedBboxData) {
          // Deduplicate bounding boxes and track original indices for combined badge display
          const { uniqueBboxes, indicesMap } = this.deduplicateBoundingBoxes(normalizedBboxData.boundingBoxes);

          // Calculate original array length (for array fields, before deduplication)
          let originalArrayLength = 1;
          if (attribute.is_array === true) {
            originalArrayLength = Array.isArray(extractedValue) ? extractedValue.length : normalizedBboxData.boundingBoxes.length;
          }

          this.fieldSourceDataMap.set(attribute.id, {
            pageNumber: normalizedBboxData.pageNumber,
            boundingBoxes: uniqueBboxes,
            boundingBoxIndices: indicesMap,
            pageWidth: normalizedBboxData.pageWidth,
            originalArrayLength: originalArrayLength
          });
        }
      }

      autoFilledCount++;
    }

    this.autoFilledFiles.add(fileId);

    this.extractionHistory.set(fileId, {
      timestamp: Date.now(),
      fieldCount: autoFilledCount,
      modifiedSinceExtraction: 0
    });
    
    this.isOverrideMode = false;
    
    // Store override count in sessionStorage for service to show notification
    const pendingKey = `truthManagement_pendingResults_${fileId}`;
    try {
      const pendingDataStr = sessionStorage.getItem(pendingKey);
      if (pendingDataStr) {
        const pendingData = JSON.parse(pendingDataStr);
        const age = Date.now() - pendingData.timestamp;
        
        // Store accurate override count calculated by component
        pendingData.overriddenCount = overriddenCount;
        pendingData.autoFilledCount = autoFilledCount;
        
        // If extraction just completed (< 10 seconds old), trigger notification from service
        // only once per pending payload.
        if (age < 10000 && !pendingData.notificationShown) {
          // Mark notification as shown (prevent service fallback)
          pendingData.notificationShown = true;
          sessionStorage.setItem(pendingKey, JSON.stringify(pendingData));
          
          // Get file info for notification
          const extractionFileInfo = this.getFileInfoForId(fileId);
          
          // Trigger service to show notification (works on ANY page)
          setTimeout(() => {
            this.truthManagementService.showExtractionNotification(
              fileId,
              extractionFileInfo.documentName,
              extractionFileInfo.fileName,
              autoFilledCount,
              overriddenCount
            );
          }, 100); // Small delay to ensure sessionStorage is updated
        } else {
          // Page refresh or old data - apply silently, don't set notificationShown flag
          sessionStorage.setItem(pendingKey, JSON.stringify(pendingData));
        }
      }
    } catch (e) {
    }
    
    this.cdr.detectChanges();
  }

  /**
   * Map extracted attributes from API to component's truth values
   * Populates modifiedValues Map and marks as modified to enable Save button
   * UPDATED: Stores results in sessionStorage if file changed, shows notification regardless
   */
  private mapExtractedToTruthValues(extractedAttrs: Record<string, any>, fileId: number | string): void {
    // Store file name/document name for notification (before checking if file changed)
    const extractionFileInfo = this.getFileInfoForId(fileId);
    const notificationFileName = extractionFileInfo.fileName;
    const notificationDocName = extractionFileInfo.documentName;
    
    // Check if we're still on the correct file
    const isStillOnSameFile = this.selectedFile && this.selectedFile.id === fileId;
    
    if (!isStillOnSameFile) {
      // Count how many fields would be filled (for notification)
      let pendingFieldCount = 0;
      for (const [attrKey, attrData] of Object.entries(extractedAttrs)) {
        let extractedValue = '';
        if (Array.isArray(attrData)) {
          extractedValue = attrData[0]?.Value || '';
        } else if (attrData && typeof attrData === 'object') {
          extractedValue = attrData.Value || '';
        }
        if (extractedValue && extractedValue.trim()) {
          pendingFieldCount++;
        }
      }
      
      // Store extraction results in sessionStorage so they can be applied when user switches back
      this.storePendingExtractionResults(fileId, extractedAttrs);
      this.autoFilledFiles.add(fileId);
      
      // NOTE: Notification will be shown when user switches back to the file and values are actually applied
      // IMPORTANT: Return early - don't try to apply to current (wrong) file
      return;
    }
    
    let autoFilledCount = 0;
    let overriddenCount = 0;
    // Iterate through extracted attributes
    for (const [attrKey, attrData] of Object.entries(extractedAttrs)) {
      // Find matching attribute using sophisticated matching (case-insensitive, normalized)
      const attribute = this.findMatchingAttribute(attrKey);
      
      if (!attribute || !attribute.id) {
        continue;
      }
      // Extract value from the attribute data object
      // Support: single object {Value, Page Number, BoundingBox}, array of objects, or table format
      let extractedValue: string | string[] | TableData = '';
      let isTableExtraction = false;
      
      // ============================================
      // PHASE 2: TABLE FIELD HANDLING
      // ============================================
      
      // Check if this is table-formatted data AND the attribute is a table field
      if (Array.isArray(attrData) && this.isTableFormatData(attrData) && attribute.isTableField) {
        // Extract table data from API response format to TableData dictionary format
        const { tableData, columns } = this.extractTableDataFromApi(attrData);
        
        if (Object.keys(tableData).length > 0 && columns.length > 0) {
          extractedValue = tableData;
          isTableExtraction = true;
        }
        // else: leave extractedValue as '' so it flows through the empty-value handling below
      } else if (Array.isArray(attrData)) {
        // Handle array format (for non-table fields or when isTableField is not set)
        const isArrayAttr = attribute.is_array === true;

        if (isArrayAttr) {
          // Extract ALL items as native array (v2 API format)
          extractedValue = attrData
            .map(item => item?.Value || '')
            .filter(v => v.trim());
        } else {
          // Single value attribute - take first only
          extractedValue = attrData[0]?.Value || '';
        }
      } else if (attrData && typeof attrData === 'object') {
        // Object format: directly access Value property
        extractedValue = attrData.Value || '';
      }

      // Check current field state
      const existingModifiedValue = this.modifiedValues.get(attribute.id);
      const existingTruthValue = this.currentFileTruthValues.get(attribute.id)?.value;
      const hasExistingValue = (existingModifiedValue && toStringValue(existingModifiedValue) !== '') ||
                               (existingTruthValue && toStringValue(existingTruthValue) !== '');
      const wasAutoPopulated = this.autoPopulatedAttributeIds.has(attribute.id);

      // Classify the extraction: empty/invalid (including an explicit "N/A"/"NA") results are
      // ignored outright - not applied, not counted, not shown, and never auto-toggled to N/A.
      // The N/A state stays a manual, user-initiated action.
      const classification = this.classifyAutofillValue(attribute, extractedValue);

      if (classification === 'empty' || classification === 'invalid') {
        continue;
      }

      // Use component-level override flag (set when user confirms override modal)
      if (this.isOverrideMode) {
        // Override mode: Override ALL fields (user confirmed)
        if (hasExistingValue) {
          if (wasAutoPopulated) {
            this.overriddenAutoPopulatedAttributeIds.add(attribute.id);
          }
          overriddenCount++;
        }
      } else {
        // Normal mode: Skip fields that already have values
        if (hasExistingValue) {
          continue;
        }
      }

      // Field is empty or can be overridden - populate it
      this.modifiedValues.set(attribute.id, extractedValue);
      this.modifiedAttributeIds.add(attribute.id);
      this.autoPopulatedAttributeIds.add(attribute.id); // Track as auto-populated (blue dot)
      this.autoPopulatedOriginalValues.set(attribute.id, extractedValue); // Store for undo
      
      // Store bounding box source data for highlighting (User Story 7126)
      // Handle both table and non-table fields
      if (isTableExtraction && Array.isArray(attrData)) {
        // Table field: extract per-cell bounding boxes
        const { tableData: _ } = this.extractTableDataFromApi(attrData);
        const tableColumns = Object.keys(_);
        
        if (tableColumns.length > 0) {
          const { boundingBoxes, pageNumber, pageWidth, cellMetadata } = this.extractTableBoundingBoxes(attrData, tableColumns);
          
          if (boundingBoxes.length > 0) {
            // For table fields, store all bboxes without deduplication (each cell is unique)
            this.fieldSourceDataMap.set(attribute.id, {
              pageNumber,
              boundingBoxes: boundingBoxes,
              boundingBoxIndices: boundingBoxes.map((_, i) => [i + 1]), // 1-based indices for each bbox
              pageWidth,
              originalArrayLength: attrData.length, // Number of rows in table
              cellMetadata: cellMetadata  // Store per-cell mapping for highlighting specific cells
            });
          }
        }
      } else {
        // Non-table field: use existing bbox normalization
        const normalizedBboxData = this.normalizeBoundingBoxData(attrData, attribute.is_array === true);
        
        if (normalizedBboxData) {
          // Keep deduped bbox/index mapping consistent with other extraction paths.
          const { uniqueBboxes, indicesMap } = this.deduplicateBoundingBoxes(normalizedBboxData.boundingBoxes);

          // Calculate original array length (for array fields)
          let originalArrayLength = 1;
          if (attribute.is_array === true) {
            originalArrayLength = Array.isArray(extractedValue) ? extractedValue.length : normalizedBboxData.boundingBoxes.length;
          }
          
          this.fieldSourceDataMap.set(attribute.id, {
            pageNumber: normalizedBboxData.pageNumber,
            boundingBoxes: uniqueBboxes,
            boundingBoxIndices: indicesMap,
            pageWidth: normalizedBboxData.pageWidth,
            originalArrayLength: originalArrayLength
          });
        }
      }
      
      // Remove from N/A set if it was previously marked as Not Applicable
      if (this.notApplicableAttributes.has(attribute.id)) {
        this.notApplicableAttributes.delete(attribute.id);
      }
      
      autoFilledCount++;
      }
    // Mark this file as auto-filled
    this.autoFilledFiles.add(fileId);
    
    // Update extraction history
    this.extractionHistory.set(fileId, {
      timestamp: Date.now(),
      fieldCount: autoFilledCount,
      modifiedSinceExtraction: 0  // Reset modification counter
    });
    
    // Reset override mode flag
    this.isOverrideMode = false;
    
    // Show success notification (always show, even if user switched files)
    if (autoFilledCount > 0) {
      // Use stored file info from beginning of method, not current file
      this.notificationService.showAutoFillSuccess(
        notificationDocName,
        notificationFileName,
        autoFilledCount,
        overriddenCount > 0 ? overriddenCount : undefined
      );
      // Force change detection only if still on same file (scheduled to avoid ExpressionChangedAfterItHasBeenCheckedError)
      if (isStillOnSameFile) {
        setTimeout(() => this.cdr.detectChanges(), 0);
      }
    } else {
      // Show error notification when no fields were extracted
      this.notificationService.showError('No matching fields extracted from document.');
    }
  }

  /**
   * Trigger auto-extraction for a file
   * Checks threshold and submits extraction request if allowed
   */
  /**
   * Manually trigger auto-fill when user clicks the Auto Fill button
   * This method is called from the template
   * 
   * Scenarios:
   * 1. Recent run exists (<5 hours) → Use dashboard API (fast)
   * 2. New document / Empty fields / Old run (>5 hours) → Use extract + poll APIs
   */
  triggerManualAutoFill(): void {
    if (!this.selectedFile) {
      this.notificationService.showError('No File Selected. Please select a file first.');
      return;
    }

    if (!this.selectedDocument) {
      this.notificationService.showError('No Document Selected. Please select a document first.');
      return;
    }
    // Check if file has ANY filled fields (manual or auto-filled)
    const totalFields = this.attributes.length;
    let filledFieldsCount = 0;
    for (const attr of this.attributes) {
      if (!attr.id) continue;
      const modifiedVal = this.modifiedValues.get(attr.id);
      const truthVal = this.currentFileTruthValues.get(attr.id)?.value;
      const hasModified = this.modifiedValues.has(attr.id) && toStringValue(modifiedVal) !== '';
      const hasTruthValue = this.currentFileTruthValues.has(attr.id) && toStringValue(truthVal) !== '';
      if (hasModified || hasTruthValue) {
        filledFieldsCount++;
      }
    }
    
    const hasFilledFields = filledFieldsCount > 0;
    const wasAutoFilled = this.autoFilledFiles.has(this.selectedFile.id);
    const selectedFileId = this.selectedFile.id;
    const isNewlyUploadedFile = this.newlyUploadedFiles.has(selectedFileId)
      || this.newlyUploadedFiles.has(selectedFileId.toString())
      || this.newlyUploadedFiles.has(Number(selectedFileId));
    const skipDashboardForAutofill = isNewlyUploadedFile || filledFieldsCount === 0;
     
    // If file has ANY filled fields (manual OR auto-filled), show confirmation
    if (hasFilledFields) {
      this.modalService.show({
        type: ModalType.CONFIRMATION,
        size: ModalSize.SMALL,
        title: 'Re-trigger Auto-Fill?',
        message: 'This file has already been auto filled. Re-triggering will override ALL the fields.',
        confirmText: 'Yes',
        cancelText: 'No',
        allowOutsideClick: false,
        allowEscapeKey: false,
        onConfirm: () => {
         this.isOverrideMode = true; // SET OVERRIDE FLAG
          this.proceedWithAutoFill(true, skipDashboardForAutofill); // Pass isReTrigger = true to force override
        },
        onCancel: () => {
         this.isOverrideMode = false; // Ensure flag is reset
        }
      });
      return;
    }
    
    // File is empty - show confirmation for first-time auto-fill
    this.modalService.show({
      type: ModalType.CONFIRMATION,
      size: ModalSize.SMALL,
      title: 'Start Auto-Population?',
      message: 'Do you want to start auto-population for this file?',
      confirmText: 'Yes',
      cancelText: 'No',
      allowOutsideClick: false,
      allowEscapeKey: false,
      onConfirm: () => {
       this.isOverrideMode = false; // Not overriding (empty file)
        this.proceedWithAutoFill(false, skipDashboardForAutofill); // First time on empty file
      },
      onCancel: () => {
      }
    });
  }
  
  /**
   * Proceed with auto-fill after confirmation (if needed)
   * @param isReTrigger - If true, force fresh extraction (bypass dashboard cache)
   */
  private proceedWithAutoFill(isReTrigger: boolean = false, skipDashboardForAutofill: boolean = false): void {
    if (!this.selectedFile || !this.selectedDocument) {
      return;
    }
    
    // GUARD: Prevent duplicate extraction for the SAME file
    if (this.extractingFiles.has(this.selectedFile.id)) {
      alert(`Auto-fill is already running for "${this.selectedFile.fileName}". Please wait for it to complete.`);
      return;
    }
    
    // NOTE: Don't add to extractingFiles here - it will be added in triggerAutoExtraction
    // This allows re-triggering after override modal confirmation
    // Extraction state persistence is now handled by the service in startExtractionInBackground()
    // No need to set localStorage here

    // If re-triggering, FORCE fresh extraction (bypass dashboard cache)
    if (isReTrigger) {
      this.performDataExtraction();
      return;
    }

    // For newly uploaded files or 0-filled files, dashboard data is not useful; go directly to extraction.
    if (skipDashboardForAutofill) {
      this.performDataExtraction();
      return;
    }

    // First-time auto-fill: Check dashboard for recent runs (fast path)
    this.truthManagementService.getDashboard().subscribe({
      next: (dashboard) => { 
        // Find the document model in dashboard
        const docModel = dashboard.document_models?.find(
          dm => dm.document_code.toLowerCase() === this.selectedDocument!.documentNumber.toLowerCase()
        );

        if (!docModel) {
          this.performDataExtraction();
          return;
        }

        const latestRun = docModel.latest_run;
        
        // Check if there's a recent run (within 5 hours)
        if (latestRun && latestRun.started_at) {
          const runTime = new Date(latestRun.started_at).getTime();
          const currentTime = Date.now();
          const hoursSinceRun = (currentTime - runTime) / (1000 * 60 * 60);
          if (hoursSinceRun < 5) {
            this.populateFromDashboard(latestRun, this.selectedFile!.fileName);
            return;
          } else {
          }
        } else {
        }

        // No recent run, use extraction APIs
        this.performDataExtraction();
      },
      error: (error) => {
        // Fallback to extraction if dashboard fails
        this.performDataExtraction();
      }
    });
  }

  /**
   * Populate fields from dashboard run results (fast path)
   */
  private populateFromDashboard(latestRun: any, fileName: string): void {
    const fileId = this.selectedFile?.id;
    
    // CRITICAL: Verify we have a valid file
    if (!this.selectedFile || !fileId) {
      if (fileId) {
        this.clearExtractionState(fileId);
      }
      this.isOverrideMode = false;
      localStorage.removeItem('truthManagement_extraction');
      return;
    }
    
    // Verify we're still on the correct file
    if (this.selectedFile.fileName !== fileName) {
      this.clearExtractionState(fileId);
      this.isOverrideMode = false;
      localStorage.removeItem('truthManagement_extraction');
      return;
    }
    // Find results for this specific file
    const fileResult = latestRun.results?.find(
      (r: any) => r.file_name === fileName
    );

    if (!fileResult || !fileResult.field_results) {
      this.performDataExtraction();
      return;
    }
    let populatedCount = 0;
    let overriddenCount = 0;

    // Map field results to attributes
    for (const [fieldName, fieldResult] of Object.entries(fileResult.field_results)) {
      const fieldData = fieldResult as any;
      // Find corresponding attribute
      const attribute = this.attributes.find(
        attr => attr.attributeKey === fieldName || attr.attributeLabel === fieldName
      );

      if (!attribute || !attribute.id) {
       continue;
      }
      // Check current field state
      const existingValue = this.currentFileTruthValues.get(attribute.id);
      const hasExistingValue = existingValue && existingValue.value && toStringValue(existingValue.value) !== '';
      const wasAutoPopulated = this.autoPopulatedAttributeIds.has(attribute.id);

      // Use component-level override flag (set when user confirms override modal)
      if (this.isOverrideMode) {
        // Override mode: Override ALL fields (user confirmed)
        if (hasExistingValue) {
          if (wasAutoPopulated) {
            this.overriddenAutoPopulatedAttributeIds.add(attribute.id);
          }
          overriddenCount++;
        }
      } else {
        // Normal mode: Skip fields that already have values
        if (hasExistingValue) {
           continue;
        }
      }

      // Classify the extraction: empty/invalid (including an explicit "N/A"/"NA") results are
      // ignored outright - not applied, not counted, not shown, and never auto-toggled to N/A.
      // The N/A state stays a manual, user-initiated action.
      if (fieldData.value && !(Array.isArray(fieldData.value) && this.isEmptyValue(fieldData.value))) {
        const normalizedValue = this.normalizeDateToMMDDYYYY(fieldData.value);
        const classification = this.classifyAutofillValue(attribute, normalizedValue);

        if (classification === 'invalid') {
          continue;
        }

        this.modifiedValues.set(attribute.id, normalizedValue);
        this.modifiedAttributeIds.add(attribute.id);
        this.autoPopulatedAttributeIds.add(attribute.id);
        this.autoPopulatedOriginalValues.set(attribute.id, normalizedValue);

        // Store page number for navigation (User Story 7126)
        // NOTE: Dashboard API doesn't return bounding box data, only extraction API does
        // PRODUCTION FIX: Only store if page number > 0 (0 means "not found")
        const pageNum = fieldData.page_number || fieldData.pageNumber || fieldData['Page Number'];
        if (pageNum && pageNum > 0) {
          // Calculate original array length (for array fields)
          let originalArrayLength = 1;
          if (attribute.is_array === true && fieldData.value) {
            originalArrayLength = Array.isArray(fieldData.value) ? fieldData.value.length : 1;
          }

          this.fieldSourceDataMap.set(attribute.id, {
            pageNumber: pageNum,
            boundingBoxes: [],  // Dashboard API doesn't have bbox data
            pageWidth: 612,  // Default width
            originalArrayLength: originalArrayLength
          });
        }

        populatedCount++;
      }
    }

    // Mark this file as auto-filled
    this.autoFilledFiles.add(fileId);

    // Clear extraction state for this file
    this.clearExtractionState(fileId);
    this.isOverrideMode = false; // Reset override flag
    localStorage.removeItem('truthManagement_extraction'); // Clear saved state on completion
    this.cdr.detectChanges();
    
    // FINAL VERIFICATION: Ensure file hasn't changed before showing results
    if (!this.selectedFile || this.selectedFile.fileName !== fileName) {
      return;
    }
    
   
    if (populatedCount > 0) {
      let message = `Auto-filled ${populatedCount} field${populatedCount > 1 ? 's' : ''} successfully.`;
      if (overriddenCount > 0) {
        message += ` ${overriddenCount} overridden.`;
      }
      this.notificationService.showSuccess(message, [
        { label: 'Document model', value: this.selectedDocument?.documentName || this.selectedDocument?.documentNumber || '' },
        { label: 'File', value: this.selectedFile?.fileName || '' }
      ]);
    } else {
     this.notificationService.showError('No fields to populate — all values already exist.');
    }
  }

  /**
   * Perform fresh data extraction using extract + poll APIs (slow path)
   */
  private performDataExtraction(): void {
    this.triggerAutoExtraction(this.selectedFile!, true);
  }

  private triggerAutoExtraction(file: File, bypassThreshold: boolean = false): void {
    // Guard: Check if we have the necessary data
    if (!file || !file.id) {
     return;
    }

    if (!this.selectedDocument) {
      return;
    }
    
    // GUARD: Prevent duplicate extraction for the SAME file (not all files)
    if (this.extractingFiles.has(file.id)) {
      return;
    }

    // Check if this is a newly uploaded file (bypass threshold for first-time extraction)
    const isNewlyUploaded = this.newlyUploadedFiles.has(file.id);
    if (isNewlyUploaded) {
      bypassThreshold = true;
      // Remove from newly uploaded set after checking
      this.newlyUploadedFiles.delete(file.id);
    }

    // Check threshold (unless bypassed)
    if (!bypassThreshold && !this.shouldAutoPopulate(file.id)) {
      return;
    }
    
    // Add this file to the extracting set
    this.extractingFiles.add(file.id);
    
    // Set extraction state and capture start time (legacy flags)
    this.isExtracting = true;
    this.extractingFileId = file.id;
    this.lastExtractionTimestamp = Date.now();
    // Show autofill initiated toaster
    this.notificationService.showExtractionInitiated(this.selectedDocument?.documentName || '', file.fileName || '');
    
    this.cdr.detectChanges();

    // Get document model/code
    const documentModel = this.selectedDocument.documentNumber;

    // Look up SAS URL from already-loaded evaluation files (avoids extra API call)
    const evalFile = this.evaluationFiles.find((ef: any) =>
      (ef.uuid || ef.id?.toString()) === file.id.toString()
    );
    const cachedSasUrl = evalFile?.blob_sas_url;

    if (cachedSasUrl) {
      fetch(cachedSasUrl)
        .then(response => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.blob();
        })
        .then(blob => {
          this.truthManagementService.startExtractionInBackground(documentModel, file.id, blob, this.selectedDocument?.documentName, file.fileName, this.isOverrideMode);
        })
        .catch(error => {
          this.refreshSasAndExtract(file, documentModel, 0);
        });
    } else {
      // SAS URL not cached — fetch fresh from files API
      this.refreshSasAndExtract(file, documentModel, 0);
    }
  }

  private refreshSasAndExtract(file: File, documentModel: string, retryCount: number): void {
    this.truthManagementService.getEvaluationFiles(this.selectedDocument?.documentNumber).subscribe({
      next: (files) => {
        const evalFile = files.find((f: any) =>
          (f.uuid || f.id?.toString()) === file.id.toString()
        );
        const fileUrl = evalFile?.blob_sas_url;
        if (!fileUrl) {
          this.clearExtractionState(file.id);
          this.isOverrideMode = false;
          this.notificationService.showError('File Access Error: No URL available.');
          this.cdr.detectChanges();
          return;
        }
        fetch(fileUrl)
          .then(r => {
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            return r.blob();
          })
          .then(blob => {
            this.truthManagementService.startExtractionInBackground(documentModel, file.id, blob, this.selectedDocument?.documentName, file.fileName, this.isOverrideMode);
          })
          .catch(err => {
            if (retryCount < this.PDF_MAX_RETRIES) {
              this.refreshSasAndExtract(file, documentModel, retryCount + 1);
            } else {
              this.clearExtractionState(file.id);
              this.isOverrideMode = false;
              this.notificationService.showError('File Access Error: Failed to access the file for extraction.');
              this.cdr.detectChanges();
            }
          });
      },
      error: () => {
        this.clearExtractionState(file.id);
        this.isOverrideMode = false;
        this.notificationService.showError('File Access Error: Failed to retrieve file information.');
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Handle table data changes from table-row-input component
   * When user edits a table cell, this receives the entire updated dictionary
   * Format: {"ColumnName": "val1,val2,val3", "Col2": "a,b,c"}
   */
  onTableDataChanged(attributeId: number, tableData: TableData): void {
    // Update modified values with the dictionary format
    this.modifiedValues.set(attributeId, tableData);
    this.modifiedAttributeIds.add(attributeId);
    
    // Clear auto-fill state if this was previously auto-populated
    this.autoPopulatedAttributeIds.delete(attributeId);
    
    // Clear N/A if table now has data
    if (Object.keys(tableData).length > 0 && Object.values(tableData).some(v => Array.isArray(v) && v.length > 0)) {
      this.notApplicableAttributes.delete(attributeId);
    }
    
    // Mark file as modified
    if (this.selectedFile?.id !== undefined && this.selectedFile?.id !== null) {
      this.clearAutoFillCompletedForFile(this.selectedFile.id);
    }
    this.cdr.detectChanges();
  }

  onTableReverted(attributeId: number): void {
    this.modifiedValues.delete(attributeId);
    this.modifiedAttributeIds.delete(attributeId);
    this.cdr.detectChanges();
  }

  /**
   * Handle new row added to table
   * Just marks the attribute as modified; data will be sent via onTableDataChanged
   */
  onTableRowAdded(attributeId: number): void {
    // Mark as modified - the actual data will be handled by onTableDataChanged
    this.modifiedAttributeIds.add(attributeId);
    
    // Clear auto-fill state
    this.autoPopulatedAttributeIds.delete(attributeId);
    
    // Mark file as modified
    if (this.selectedFile?.id !== undefined && this.selectedFile?.id !== null) {
      this.clearAutoFillCompletedForFile(this.selectedFile.id);
    }
  }

  /**
   * Handle table row removed event
   * Just marks the attribute as modified; data will be sent via onTableDataChanged
   */
  onTableRowRemoved(attributeId: number): void {
    // Mark as modified - the actual data will be handled by onTableDataChanged
    this.modifiedAttributeIds.add(attributeId);

    // Clear auto-fill state
    this.autoPopulatedAttributeIds.delete(attributeId);

    // Mark file as modified
    if (this.selectedFile?.id !== undefined && this.selectedFile?.id !== null) {
      this.clearAutoFillCompletedForFile(this.selectedFile.id);
    }
  }

  /**
   * Handle table cell click event
   * Fired by table-row-input when a cell is clicked, before it enters edit mode.
   * Restricts bounding box highlighting to only the clicked cell.
   */
  onTableCellClicked(attributeId: number, cell: { rowIndex: number; columnName: string }): void {
    this.highlightTableCell(attributeId, cell.rowIndex, cell.columnName);
  }

  /**
   * Handle table row edit event
   * Fired by table-row-input when a cell is edited
   */
  onTableRowEdited(attributeId: number, edit: { rowIndex: number; columnName: string; value: string }): void {
    // Mark as modified - the actual data will be handled by onTableDataChanged
    this.modifiedAttributeIds.add(attributeId);
    
    // Clear auto-fill state
    this.autoPopulatedAttributeIds.delete(attributeId);
    
    // Highlight only this specific table cell's bounding box
    this.highlightTableCell(attributeId, edit.rowIndex, edit.columnName);
  }

  /**
   * Highlight a specific table cell's bounding box
   * Restricts highlighting to only the clicked/edited cell instead of all cells in the table
   * 
   * @param attributeId - The table field attribute ID
   * @param rowIndex - The row index of the cell (0-based)
   * @param columnName - The column name of the cell
   */
  private highlightTableCell(attributeId: number, rowIndex: number, columnName: string): void {
    const sourceData = this.fieldSourceDataMap.get(attributeId);
    
    // AC7: Re-click same cell - re-focus on its bounding box
    if (
      this.highlightedTableCell?.attributeId === attributeId &&
      this.highlightedTableCell?.rowIndex === rowIndex &&
      this.highlightedTableCell?.columnName === columnName &&
      sourceData?.boundingBoxes?.length
    ) {
      this.scrollToBoundingBox();
      return;
    }
    
    // Clear previous highlight
    this.clearBoundingBoxHighlight();
    
    // AC10: No page number AND no bounding box - show first page (BAU)
    if (!sourceData?.pageNumber) {
      this.currentPage = 1;
      this.pageInput = 1;
      this.scrollToPage(1);
      this.cdr.detectChanges();
      return;
    }
    
    // AC1 & AC9: Navigate to the page
    this.currentPage = sourceData.pageNumber;
    this.pageInput = sourceData.pageNumber;
    
    // AC9: If no bounding box data, just navigate to page (no highlight)
    if (!sourceData.boundingBoxes || sourceData.boundingBoxes.length === 0) {
      this.scrollToPage(sourceData.pageNumber);
      this.cdr.detectChanges();
      return;
    }
    
    // AC2-AC5: Has bounding box - set up highlight for this specific cell.
    // Under virtual scroll a far page has no DOM element yet — scroll first.
    this.highlightedAttributeId = attributeId;
    this.highlightedTableCell = { attributeId, rowIndex, columnName };
    this.cdr.detectChanges();
    if (!this.getRenderedPageElement(sourceData.pageNumber)) {
      this.scrollToPage(sourceData.pageNumber);
    }
    this.renderBoundingBoxOverlaysWithRetry(sourceData, 0);
  }

  /**
   * Handle a table cell entering/exiting edit mode
   * Shows the Save Changes button immediately, before the edit is committed on blur
   */
  onTableCellEditingChanged(isEditing: boolean): void {
    this.isEditingTableCell = isEditing;
    this.cdr.detectChanges();
  }
}
